/**
 * GuidedAnalyzeView — Guided Flow Analyze Workspace
 *
 * Three tabs:
 *   Dashboard   — real summary signals (source composition + code analysis snapshot)
 *   Inventory   — file-level inventory table (existing, unchanged)
 *   Technical   — deeper code signals (placeholder)
 *
 * Data sources (real, no mock data):
 *
 *   INGEST (always available after upload):
 *     GET /ingest/results/{ingest_job_id}
 *     → classified_catalog.json  (file paths by type, counts by type)
 *
 *   CODE ANALYSIS (available if a previous run exists for this project):
 *     GET /workflows/runs?account_id=X&app_name=Y&limit=10
 *     → find most recent run with pipeline-code-analysis job
 *     GET /codeanalysis/{ca_job_id}/results/json/comprehensive_parse_results.json
 *     → summary.source_lines, paragraphs, data_items, files_analyzed
 *     → cross_reference.copybook_usage, call_graph
 *
 * BULK MODE (2026-03-30): toggle, checkboxes, banner, selection summary panel.
 */

import { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import {
  ArrowLeft, Upload, FileCode, Sparkles, CheckCircle2,
  ChevronRight, ChevronDown, Search, Filter, List,
  FolderOpen, AlertTriangle, AlertCircle, Loader2,
  MoreHorizontal, Check, X, BarChart2, BookOpen, Code2,
  Network, GitBranch, Database, HardDrive, Download, Layers, FileArchive,
  Share2,
  Home, Workflow, Plug, FileBarChart,
  Settings, HelpCircle, User,
  LayoutGrid,
  PanelRightClose, PanelRightOpen,
} from 'lucide-react'
import { ReactFlowProvider } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { getAPIBaseUrl, isQA } from '../../environment'
import AIResponseModal from '../editor/AIResponseModal'
import AIAssistant from '../designer/AIAssistant'
import { DependencyGraph } from '../graph'
import {
  explainCobolCode,
  generateJavaFromCobol,
  customPrompt as customPromptAI,
} from '../../services/aiService'
import DataView from '../views/DataView'
import './GuidedAnalyzeView.css'
import '../runs/RunsView.css'

// ── Real-data helpers (ingest) ────────────────────────────────────────────────

function extractFileName(filePath) {
  if (!filePath) return ''
  return filePath.split('/').pop() || filePath
}

const VSAM_EXTENSIONS = new Set(['.dat', '.vsam', '.ksds', '.esds', '.rrds'])


function isVSAMCandidate(filename) {
  const dot = filename.lastIndexOf('.')
  if (dot < 0) return false
  return VSAM_EXTENSIONS.has(filename.slice(dot).toLowerCase())
}

const TYPE_TO_GROUP = {
  cobol:         'COBOL',
  copybook:      'COPYBOOK',
  jcl:           'JCL',
  bms:           'BMS',
  sql:           'SQL',
  data:          'DATA',
  config:        'CONFIG',
  documentation: 'DOCS',
  unknown:       'UNKNOWN',
}

function mapCatalogToFiles(classified_catalog) {
  const classifications = classified_catalog?.classifications || {}
  const files = []
  let id = 1
  for (const [type, entries] of Object.entries(classifications)) {
    const baseGroup = TYPE_TO_GROUP[type] || type.toUpperCase()
    for (const entry of (entries || [])) {
      // Handle both string paths and object entries {path, size, ...}
      const filePath = typeof entry === 'string' ? entry : entry?.path || ''
      const name   = extractFileName(filePath)
      const isVSAM = (type === 'unknown' || type === 'data') && isVSAMCandidate(name)
      files.push({
        id:         id++,
        group:      isVSAM ? 'VSAM' : baseGroup,
        name,
        path:       filePath,
        loc:        0,
        complexity: 'N/A',
        status:     'classified',
      })
    }
  }
  return files
}

function buildMetrics(files) {
  const total      = files.length
  const classified = files.filter(f => f.status === 'classified').length
  const review     = files.filter(f => f.status === 'needs-review').length
  const missing    = files.filter(f => f.status === 'missing-refs').length
  const dataAssets = files.filter(f => f.group === 'VSAM').length
  return [
    { label: 'Total Assets', value: total,      accent: false   },
    { label: 'Classified',   value: classified,  accent: 'green' },
    { label: 'Needs Review', value: review,      accent: 'amber' },
    { label: 'Missing Refs', value: missing,     accent: 'red'   },
    { label: 'Data Assets',  value: dataAssets,  accent: false   },
  ]
}

// ── Dashboard helpers ────────────────────────────────────────────────────────

/** SOURCE: classified_catalog — always available after ingest */
const COMP_TYPES = [
  { key: 'cobol',    label: 'COBOL',     color: '#388bfd' },
  { key: 'copybook', label: 'Copybooks', color: '#a78bfa' },
  { key: 'jcl',      label: 'JCL',       color: '#f59e0b' },
  { key: 'bms',      label: 'BMS',       color: '#22c55e' },
  { key: 'sql',      label: 'SQL',       color: '#f97316' },
  { key: 'unknown',  label: 'Other',     color: '#6b7280' },
]

function buildSourceComposition(catalogData) {
  const summary = catalogData?.summary || {}
  const total   = Math.max(Object.values(summary).reduce((a, b) => a + (b || 0), 0), 1)
  return COMP_TYPES
    .map(t => ({ ...t, count: summary[t.key] || 0, pct: Math.round((summary[t.key] || 0) / total * 100) }))
    .filter(t => t.count > 0)
}

/** SOURCE: comprehensive_parse_results.summary + cross_reference */
function buildCodeSummary(parseData) {
  if (!parseData) return null
  const sum       = parseData.summary || {}
  const programs  = parseData.programs || {}
  const xref      = parseData.cross_reference || {}
  const callGraph = xref.call_graph || {}
  const cbUsage   = xref.copybook_usage || {}

  // Total external calls = sum of all called-program arrays
  const totalCalls = Object.values(callGraph).reduce((n, arr) => n + (arr?.length || 0), 0)

  // Derive values from program data when summary uses different field names.
  // Engine's comprehensive_parse_results.summary uses: program_count, total_data_items, total_files
  // Job output_data.summary uses: source_lines, data_items, paragraphs, files_analyzed
  const sourceLines = sum.source_lines
    || Object.values(programs).reduce((n, p) => n + (p.line_count || 0), 0)
  const dataItems = sum.data_items || sum.total_data_items || 0
  const paragraphs = sum.paragraphs
    || Object.values(programs).reduce((n, p) => n + (p.paragraphs?.length || 0), 0)

  return {
    sourceLines,
    dataItems,
    paragraphs,
    filesAnalyzed:   sum.files_analyzed || sum.total_files || Object.keys(programs).length,
    javaLines:       sum.java_lines       || 0,
    totalCalls,
    uniqueCopybooks: Object.keys(cbUsage).length,
  }
}

/** SOURCE: comprehensive_parse_results.cross_reference.copybook_usage */
function buildTopCopybooks(parseData, limit = 6) {
  if (!parseData) return null
  const cbUsage = parseData.cross_reference?.copybook_usage || {}
  return Object.entries(cbUsage)
    .map(([name, programs]) => ({ name, usedBy: programs?.length || 0 }))
    .sort((a, b) => b.usedBy - a.usedBy)
    .slice(0, limit)
}

/** SOURCE: asset graph — extract JCL execution entry points + their targets */
function buildExecutionSummary(assetGraph) {
  if (!assetGraph) return null
  const assets  = assetGraph.assets        || []
  const rels    = assetGraph.relationships || []
  const jclJobs = assets.filter(a => a.type === 'JCL_JOB')
  if (jclJobs.length === 0) return null

  return jclJobs.map(job => {
    const execRels = rels.filter(r => r.from === job.id && r.type === 'EXECUTES')
    const ddRels   = rels.filter(r => r.from === job.id && r.type === 'DD_REFERENCES')
    const programs = execRels.map(r => r.metadata?.program || r.to.split(':').pop())
    const steps    = job.metadata?.steps || []
    return {
      name:      job.name,
      file:      job.file,
      programs,
      stepCount: steps.length || execRels.length,
      ddCount:   ddRels.length,
    }
  })
}

/** SOURCE: asset graph — extract VSAM + DB2 data assets */
function buildDataLayer(assetGraph) {
  if (!assetGraph) return null
  const assets = assetGraph.assets || []
  const vsam   = assets.filter(a => a.type === 'VSAM_DATASET' || a.type === 'DATA_FILE')
  const db2    = assets.filter(a => a.type === 'DB2_TABLE')
  const dclgen = assets.filter(a => a.type === 'DB2_DCLGEN')
  if (vsam.length === 0 && db2.length === 0 && dclgen.length === 0) return null
  return { vsam, db2, dclgen }
}

/** Build deterministic plain-English narrative from real analysis data */
function buildNarrative(assetGraph, parseData, catalogData, topCB) {
  const sentences = []
  const agSum  = assetGraph?.summary || {}
  const catSum = catalogData?.summary || {}

  // Sentence 1: Composition
  const programs  = agSum.asset_counts?.COBOL_PROGRAM || catSum.cobol    || 0
  const cbs       = agSum.asset_counts?.COPYBOOK       || catSum.copybook || 0
  const jclJobs   = agSum.jcl_job_count || catSum.jcl || 0
  const dataFiles = (agSum.vsam_dataset_count || 0) + (agSum.data_file_count || 0)
  const db2Tables = agSum.db2_table_count || 0

  if (programs > 0 || cbs > 0) {
    const parts = []
    if (programs > 0)   parts.push(`${programs} COBOL program${programs !== 1 ? 's' : ''}`)
    if (cbs > 0)        parts.push(`${cbs} shared copybook${cbs !== 1 ? 's' : ''}`)
    if (jclJobs > 0)    parts.push(`${jclJobs} JCL job${jclJobs !== 1 ? 's' : ''}`)
    if (dataFiles > 0)  parts.push(`${dataFiles} data file${dataFiles !== 1 ? 's' : ''}`)
    if (db2Tables > 0)  parts.push(`${db2Tables} DB2 table${db2Tables !== 1 ? 's' : ''}`)
    sentences.push(`This application contains ${parts.join(', ')}.`)
  }

  // Sentence 2: Primary execution path from JCL
  if (assetGraph) {
    const jclAssets = (assetGraph.assets || []).filter(a => a.type === 'JCL_JOB')
    const rels      = assetGraph.relationships || []
    if (jclAssets.length > 0) {
      const job      = jclAssets[0]
      const execRels = rels.filter(r => r.from === job.id && r.type === 'EXECUTES')
      const progs    = execRels.map(r => r.metadata?.program || r.to.split(':').pop())
      const nSteps   = job.metadata?.steps?.length || execRels.length
      if (progs.length > 0)
        sentences.push(`The primary execution path is ${job.name} → ${progs.join(', ')} via ${nSteps} step${nSteps !== 1 ? 's' : ''}.`)
    } else if (parseData) {
      const cg = parseData.cross_reference?.call_graph || {}
      const top = Object.entries(cg)
        .map(([prog, calls]) => ({ prog, count: calls?.length || 0 }))
        .sort((a, b) => b.count - a.count)[0]
      if (top && top.count > 0)
        sentences.push(`${top.prog} is the most active program, making ${top.count} external call${top.count !== 1 ? 's' : ''}.`)
    }
  }

  // Sentence 3: Most reused copybook
  if (topCB && topCB.length > 0 && topCB[0].usedBy > 1) {
    const top = topCB[0]
    sentences.push(`'${top.name}' is the most reused shared structure, referenced by ${top.usedBy} program${top.usedBy !== 1 ? 's' : ''}.`)
  }

  // Sentence 3b: Code Analysis depth — only when parseData is available
  if (parseData) {
    const psum      = parseData.summary || {}
    const srcLines  = psum.source_lines || 0
    const dataItems = psum.data_items   || 0
    const derivedParas = Object.values(parseData.programs || {})
      .reduce((n, p) => n + ((p.paragraphs || []).length), 0)
    const parts = []
    if (srcLines    > 0) parts.push(`${srcLines.toLocaleString()} source lines`)
    if (derivedParas > 0) parts.push(`${derivedParas} paragraphs`)
    if (dataItems   > 0) parts.push(`${dataItems.toLocaleString()} data items`)
    if (parts.length > 0)
      sentences.push(`Code Analysis found ${parts.join(', ')} mapped to Java types.`)
  }

  // Sentence 4: Issue summary
  const issueCount   = agSum.total_issues   || 0
  const issueCounts  = agSum.issue_counts   || {}
  if (issueCount > 0) {
    const extDs    = issueCounts.JCL_EXTERNAL_DATASET    || 0
    const unrefDb2 = issueCounts.DB2_TABLE_UNREFERENCED  || 0
    const parts    = []
    if (extDs > 0)    parts.push(`${extDs} external dataset${extDs !== 1 ? 's' : ''} requiring migration decisions`)
    if (unrefDb2 > 0) parts.push(`${unrefDb2} DB2 table${unrefDb2 !== 1 ? 's' : ''} with no detected SQL references`)
    const other = issueCount - extDs - unrefDb2
    if (other > 0)    parts.push(`${other} other item${other !== 1 ? 's' : ''} for review`)
    if (parts.length > 0)
      sentences.push(`Review required: ${parts.join('; ')}.`)
  } else if (assetGraph && issueCount === 0) {
    sentences.push('No system issues detected — all assets and references resolve within the application boundary.')
  }

  return sentences
}

// ── Static config ────────────────────────────────────────────────────────────

const GROUP_ORDER  = ['COBOL', 'COPYBOOK', 'JCL', 'BMS', 'VSAM', 'DATA', 'SQL', 'CONFIG', 'DOCS', 'UNKNOWN']

const GROUP_COLORS = {
  COBOL:    '#388bfd',
  COPYBOOK: '#a78bfa',
  JCL:      '#f59e0b',
  BMS:      '#22c55e',
  VSAM:     '#06b6d4',
  DATA:     '#14b8a6',
  SQL:      '#f97316',
  CONFIG:   '#6b7280',
  DOCS:     '#6b7280',
  UNKNOWN:  '#6b7280',
}

const STATUS_META = {
  'classified':   { label: 'Classified',   css: 'ga-status-classified'   },
  'needs-review': { label: 'Needs Review', css: 'ga-status-needs-review' },
  'missing-refs': { label: 'Missing Refs', css: 'ga-status-missing'      },
}

const SCOPE_CHIPS = [
  { id: 'all',          label: 'All'          },
  { id: 'needs-review', label: 'Needs Review' },
  { id: 'missing-refs', label: 'Missing'      },
  { id: 'vsam',         label: 'Data Assets'  },
]

const VIEW_OPTIONS = ['List', 'By Type', 'By Folder', 'By Risk']
const NAV_TABS     = ['Dashboard', 'Inventory', 'Technical View', 'Data View']
const TV_TABS      = ['Dependencies', 'Copybooks', 'Data Assets', 'Field X-Ref', 'JCL / Execution', 'Issues', 'Statistics', 'Compiler']

const DETAIL_FOR = {
  default: {
    type: 'COBOL Program',
    recommendations: [
      'Extract shared data items into separate Java model classes.',
      'Review PERFORM loops for conversion to Java iterators.',
      'Validate COMP-3 packed-decimal fields against Java BigDecimal.',
    ],
  },
  COBOL: {
    type: 'COBOL Program',
    recommendations: [
      'Extract shared data items into separate Java model classes.',
      'Review PERFORM loops for conversion to Java iterators.',
      'Validate COMP-3 packed-decimal fields against Java BigDecimal.',
    ],
  },
  COPYBOOK: {
    type: 'COBOL Copybook',
    recommendations: [
      'Map to a shared Java DTO/model class.',
      'Check for redundant field definitions across copybooks.',
    ],
  },
  JCL: {
    type: 'JCL Job',
    recommendations: [
      'Map JCL steps to Spring Batch job steps.',
      'Review DD statements for file dependency resolution.',
    ],
  },
  BMS: {
    type: 'BMS Map',
    recommendations: [
      'Convert BMS screens to REST endpoints or UI components.',
      'Map SEND MAP / RECEIVE MAP patterns to controller methods.',
    ],
  },
  VSAM: {
    type: 'VSAM Data File',
    recommendations: [
      'Define a corresponding JPA entity and repository.',
      'Review VSAM KSDS key structure for primary key mapping.',
    ],
  },
  SQL: {
    type: 'SQL Script',
    recommendations: [
      'Map native SQL queries to JPA repository methods.',
      'Review embedded SQL for COBOL host variable bindings.',
    ],
  },
  CONFIG: {
    type: 'Configuration File',
    recommendations: [
      'Map configuration values to Spring application properties.',
      'Review for environment-specific settings that need externalization.',
    ],
  },
  DOCS: {
    type: 'Documentation',
    recommendations: [
      'Review documentation for embedded business rule references.',
    ],
  },
}

const JOURNEY_STEPS = [
  { id: 'create',  label: 'Setup',         icon: Sparkles },
  { id: 'upload',  label: 'Upload Source', icon: Upload   },
  { id: 'analyze', label: 'Analyze',       icon: FileCode },
]

// ── Dashboard sub-components ─────────────────────────────────────────────────

/** Compact readiness signal — derived entirely from real data, no scoring invented */
function ReadinessChip({ files, issueCount, parseData, caLoading }) {
  if (caLoading) return null

  const missingCount = files.filter(f => f.status === 'missing-refs').length

  let state, label, detail
  if (missingCount > 0) {
    state = 'warn'; label = 'Review Required'
    detail = `${missingCount} missing reference${missingCount !== 1 ? 's' : ''}`
  } else if (issueCount > 0) {
    state = 'note'; label = 'Ready with Notes'
    detail = `${issueCount} item${issueCount !== 1 ? 's' : ''} to review`
  } else if (parseData) {
    state = 'ready'; label = 'Ready'
    detail = 'All assets resolved'
  } else {
    state = 'note'; label = 'Partially Ready'
    detail = 'Code Analysis not yet run'
  }

  return (
    <div className={`ga-readiness-chip ga-readiness-${state}`}>
      {state === 'ready'
        ? <CheckCircle2 size={12} />
        : <AlertTriangle size={12} />
      }
      <span className="ga-readiness-label">{label}</span>
      <span className="ga-readiness-sep">·</span>
      <span className="ga-readiness-detail">{detail}</span>
    </div>
  )
}

/** Single overview metric card */
function DashCard({ label, value, pending = false }) {
  return (
    <div className="ga-dash-card">
      <span className="ga-dash-card-value">
        {pending
          ? <Loader2 size={14} className="ga-load-spinner" />
          : (value !== null && value !== undefined)
            ? (typeof value === 'number' ? value.toLocaleString() : value)
            : '—'
        }
      </span>
      <span className="ga-dash-card-label">{label}</span>
    </div>
  )
}

/** Stat cell inside the Code Analysis Snapshot grid */
function DashStat({ label, value }) {
  return (
    <div className="ga-dash-stat">
      <span className="ga-dash-stat-value">{value}</span>
      <span className="ga-dash-stat-label">{label}</span>
    </div>
  )
}

// Inline issue label map used by DashboardContent (mirrors ISSUE_TYPE_META below)
const DASH_ISSUE_META = {
  JCL_EXTERNAL_DATASET:    { label: 'External Dataset',        action: 'Decide whether to migrate or replace each dataset.' },
  DB2_TABLE_UNREFERENCED:  { label: 'Unreferenced DB2 Table',  action: 'Verify table is accessed from another source or exclude from scope.' },
  UNRESOLVED_LOGICAL_FILE: { label: 'Unresolved Logical File', action: 'Check FILE-CONTROL SELECT/ASSIGN values and JCL DD entries.' },
  UNRESOLVED_COPYBOOK:     { label: 'Unresolved Copybook',     action: 'Ensure the copybook is included in the uploaded source.' },
  JCL_UNRESOLVED_PROGRAM:  { label: 'Unresolved JCL Program',  action: 'Include the program in the upload or flag as an external dependency.' },
}

/**
 * Dashboard tab content — real-data summary layer.
 *
 * Sections (all driven by real artifacts, no mock data):
 *   1. KPI Strip       — asset graph + catalog + parse data
 *   2. Composition     — source breakdown (catalog) + execution entry points (asset graph)
 *   3. Shared Structures + Data Layer  — parse cross_reference + asset graph
 *   4. Review Items    — asset graph issues with readable labels and actions
 *   5. Application Narrative — deterministic plain-English from real counts
 *
 * Every section degrades gracefully when its data source is unavailable.
 */
function DashboardContent({ catalogData, files, parseData, assetGraph, caLoading, caJobId, projectName, intelligenceMetrics, deadCode }) {
  // ── Extract data sources ──────────────────────────────────────────────────
  const agSum    = assetGraph?.summary       || {}
  const agAssets = assetGraph?.assets        || []
  const agRels   = assetGraph?.relationships || []
  const agIssues = assetGraph?.issues        || []
  const hasGraph = !!assetGraph

  // ── KPI values — asset graph primary, catalog secondary, parseData tertiary
  const programCount  = Object.keys(parseData?.programs   || {}).length
  const copybookCount = Object.keys(parseData?.copybooks  || {}).length
  const kpiTotalAssets = hasGraph
    ? (agSum.total_assets || 0)
    : files.length || (programCount + copybookCount)
  const kpiPrograms    = hasGraph
    ? (agSum.asset_counts?.COBOL_PROGRAM || 0)
    : (catalogData?.summary?.cobol || programCount || 0)
  const kpiCopybooks   = hasGraph
    ? (agSum.asset_counts?.COPYBOOK || 0)
    : (catalogData?.summary?.copybook || copybookCount || 0)
  const kpiJclJobs     = agSum.jcl_job_count   || 0
  const kpiDataFiles   = (agSum.vsam_dataset_count || 0) + (agSum.data_file_count || 0)
  const kpiDb2Tables   = agSum.db2_table_count  || 0
  const kpiRelships    = agSum.total_relationships || 0
  const kpiIssues      = agSum.total_issues     || 0

  // ── Supporting panel data ─────────────────────────────────────────────────
  const sourceComp  = buildSourceComposition(catalogData)
  const codeSum     = buildCodeSummary(parseData)
  const topCB       = buildTopCopybooks(parseData)
  const execSummary = buildExecutionSummary(assetGraph)
  const dataLayer   = buildDataLayer(assetGraph)
  const narrative   = buildNarrative(assetGraph, parseData, catalogData, topCB)

  // Issue groups — {issueType: [issue, ...]}
  // Note: asset graph uses 'type' (not 'issue_type') for the issue classification key
  const issueGroups = {}
  agIssues.forEach(issue => {
    const t = issue.type || issue.issue_type || 'UNKNOWN'
    if (!issueGroups[t]) issueGroups[t] = []
    issueGroups[t].push(issue)
  })

  // Log which sections are populated and from which source
  console.log('[Dashboard] Sections populated:', {
    kpiSource:      hasGraph ? 'asset_graph' : 'catalog',
    sourceComp:     sourceComp.length > 0,
    execSummary:    !!execSummary,
    codeDepth:      !!codeSum,
    topCopybooks:   topCB ? topCB.length : 0,
    dataLayer:      !!dataLayer,
    issueGroups:    Object.keys(issueGroups).length,
    narrative:      narrative.length,
    caLoading,
  })

  // ── Risk Intelligence help window (local to Dashboard) ──────────────────
  const [intelHelpOpen, setIntelHelpOpen] = useState(false)
  const [intelHelpPos,  setIntelHelpPos]  = useState({ top: 110, left: 60 })
  const intelHelpBtnRef = useRef(null)

  useEffect(() => {
    if (!intelHelpOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setIntelHelpOpen(false)
        intelHelpBtnRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [intelHelpOpen])

  return (
    <div className="ga-dashboard">

      {/* ── Section 1: KPI Strip ─────────────────────────────────────────── */}
      {/* SOURCE: asset graph (primary) + catalog + parse data (fallback)     */}
      <div className="ga-dash-cards">
        <DashCard label="Total Assets"   value={kpiTotalAssets} />
        <DashCard label="COBOL Programs" value={kpiPrograms} />
        <DashCard label="Copybooks"      value={kpiCopybooks} />
        {hasGraph && <DashCard label="JCL Jobs"      value={kpiJclJobs} />}
        {hasGraph && kpiDataFiles > 0 && <DashCard label="Data Files"    value={kpiDataFiles} />}
        {hasGraph && <DashCard label="DB2 Tables"    value={kpiDb2Tables} />}
        {hasGraph && <DashCard label="Relationships" value={kpiRelships} />}
        <DashCard
          label="Issues"
          value={caLoading ? null : (hasGraph ? kpiIssues : null)}
          pending={caLoading}
        />
        <DashCard label="Source Lines" value={codeSum?.sourceLines ?? null} pending={caLoading && !codeSum} />
        <DashCard label="Data Items"   value={codeSum?.dataItems   ?? null} pending={caLoading && !codeSum} />
      </div>

      {/* ── Readiness signal ─────────────────────────────────────────────── */}
      <ReadinessChip
        files={files}
        issueCount={kpiIssues}
        parseData={parseData}
        caLoading={caLoading}
      />

      {/* ── Section 2: Source Composition + Execution Entry Points ──────── */}
      <div className="ga-dash-row">

        {/* SOURCE: classified_catalog — always available after ingest */}
        <div className="ga-dash-panel">
          <div className="ga-dash-panel-header">
            <BarChart2 size={14} className="ga-dash-panel-icon" />
            <span className="ga-dash-panel-title">Source Composition</span>
            <span className="ga-dash-source-tag">ingest</span>
          </div>
          <div className="ga-comp-list">
            {sourceComp.length > 0
              ? sourceComp.map(item => (
                  <div key={item.key} className="ga-comp-row">
                    <span className="ga-comp-label" style={{ color: item.color }}>{item.label}</span>
                    <div className="ga-comp-bar-track">
                      <div className="ga-comp-bar-fill"
                        style={{ width: `${Math.max(item.pct, 2)}%`, backgroundColor: item.color }} />
                    </div>
                    <span className="ga-comp-pct">{item.pct}%</span>
                    <span className="ga-comp-count">{item.count}</span>
                  </div>
                ))
              : <p className="ga-dash-empty">No file classification data available.</p>
            }
          </div>
        </div>

        {/* SOURCE: asset graph JCL_JOB + EXECUTES rels (with code depth fallback) */}
        <div className="ga-dash-panel">
          <div className="ga-dash-panel-header">
            <GitBranch size={14} className="ga-dash-panel-icon" />
            <span className="ga-dash-panel-title">
              {execSummary ? 'Execution Entry Points' : 'Code Depth'}
            </span>
            <span className={`ga-dash-source-tag${!hasGraph && !codeSum ? ' ga-dash-source-tag--pending' : ''}`}>
              {execSummary
                ? 'asset graph · JCL'
                : hasGraph
                  ? 'asset graph'
                  : (codeSum ? 'code analysis' : 'pending')}
            </span>
          </div>

          {caLoading ? (
            <div className="ga-dash-pending">
              <Loader2 size={15} className="ga-load-spinner" />
              <span>Looking for code analysis…</span>
            </div>
          ) : execSummary ? (
            // Full JCL execution map
            <div className="ga-dash-exec-list">
              {execSummary.map(entry => (
                <div key={entry.name} className="ga-dash-exec-entry">
                  <div className="ga-dash-exec-header">
                    <span className="ga-dash-exec-job">{entry.name}</span>
                    <span className="ga-dash-exec-meta">
                      {entry.stepCount} step{entry.stepCount !== 1 ? 's' : ''} · {entry.ddCount} DD ref{entry.ddCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {entry.programs.length > 0 && (
                    <div className="ga-dash-exec-targets">
                      <span className="ga-dash-exec-arrow">executes</span>
                      <span className="ga-dash-exec-progs">{entry.programs.join(', ')}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : codeSum ? (
            // Fallback: code depth stats when no JCL
            <div className="ga-dash-stat-grid">
              <DashStat label="Source Lines"    value={codeSum.sourceLines.toLocaleString()} />
              <DashStat label="Paragraphs"      value={codeSum.paragraphs.toLocaleString()} />
              <DashStat label="Data Items"      value={codeSum.dataItems.toLocaleString()} />
              <DashStat label="External Calls"  value={codeSum.totalCalls.toLocaleString()} />
              {codeSum.uniqueCopybooks > 0 && (
                <DashStat label="Unique Copybooks" value={codeSum.uniqueCopybooks.toString()} />
              )}
            </div>
          ) : (
            <div className="ga-dash-unavail">
              <p className="ga-dash-unavail-title">No execution data available</p>
              <p className="ga-dash-unavail-hint">
                Run the full pipeline to see JCL entry points and program call graphs.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Section 3: Top Shared Structures + Data Layer ───────────────── */}
      <div className="ga-dash-row">

        {/* SOURCE: comprehensive_parse_results.cross_reference.copybook_usage */}
        <div className="ga-dash-panel">
          <div className="ga-dash-panel-header">
            <BookOpen size={14} className="ga-dash-panel-icon" />
            <span className="ga-dash-panel-title">Top Shared Structures</span>
            <span className={`ga-dash-source-tag${!codeSum ? ' ga-dash-source-tag--pending' : ''}`}>
              {codeSum ? 'code analysis' : 'pending'}
            </span>
          </div>
          {caLoading ? (
            <div className="ga-dash-pending">
              <Loader2 size={15} className="ga-load-spinner" /><span>Loading…</span>
            </div>
          ) : topCB && topCB.length > 0 ? (
            <div className="ga-dash-cb-list">
              {topCB.map(cb => (
                <div key={cb.name} className="ga-dash-cb-row">
                  <span className="ga-dash-cb-name">{cb.name}</span>
                  <span className="ga-dash-cb-bar-wrap">
                    <span className="ga-dash-cb-bar" style={{ width: `${Math.min(cb.usedBy * 20, 100)}%` }} />
                  </span>
                  <span className="ga-dash-cb-count">{cb.usedBy} program{cb.usedBy !== 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="ga-dash-unavail">
              <p className="ga-dash-unavail-title">
                {codeSum ? 'No copybook usage data found' : 'Code Analysis not yet run'}
              </p>
              <p className="ga-dash-unavail-hint">
                {codeSum
                  ? 'No COPY statements detected in the parsed programs.'
                  : 'Run the full pipeline to see copybook usage analysis.'}
              </p>
            </div>
          )}
        </div>

        {/* SOURCE: asset graph VSAM_DATASET, DATA_FILE, DB2_TABLE, DB2_DCLGEN */}
        <div className="ga-dash-panel">
          <div className="ga-dash-panel-header">
            <Database size={14} className="ga-dash-panel-icon" />
            <span className="ga-dash-panel-title">Data Layer</span>
            <span className={`ga-dash-source-tag${!hasGraph ? ' ga-dash-source-tag--pending' : ''}`}>
              {hasGraph ? 'asset graph' : 'pending'}
            </span>
          </div>
          {caLoading ? (
            <div className="ga-dash-pending">
              <Loader2 size={15} className="ga-load-spinner" /><span>Loading…</span>
            </div>
          ) : dataLayer ? (
            <div className="ga-dash-data-groups">
              {dataLayer.vsam.length > 0 && (
                <div className="ga-dash-data-group">
                  <span className="ga-dash-data-group-label">VSAM / Data Files</span>
                  <div className="ga-dash-data-chips">
                    {dataLayer.vsam.map(a => (
                      <span key={a.id} className="ga-dash-data-chip ga-dash-data-chip--vsam">{a.name}</span>
                    ))}
                  </div>
                </div>
              )}
              {dataLayer.db2.length > 0 && (
                <div className="ga-dash-data-group">
                  <span className="ga-dash-data-group-label">DB2 Tables</span>
                  <div className="ga-dash-data-chips">
                    {dataLayer.db2.map(a => (
                      <span key={a.id} className="ga-dash-data-chip ga-dash-data-chip--db2">{a.name}</span>
                    ))}
                  </div>
                </div>
              )}
              {dataLayer.dclgen.length > 0 && (
                <div className="ga-dash-data-group">
                  <span className="ga-dash-data-group-label">DB2 DCLGENs</span>
                  <div className="ga-dash-data-chips">
                    {dataLayer.dclgen.map(a => (
                      <span key={a.id} className="ga-dash-data-chip ga-dash-data-chip--dclgen">{a.name}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="ga-dash-unavail">
              <p className="ga-dash-unavail-title">
                {hasGraph ? 'No data assets detected' : 'Asset graph not available'}
              </p>
              <p className="ga-dash-unavail-hint">
                {hasGraph
                  ? 'No VSAM datasets or DB2 tables were resolved in the asset graph.'
                  : 'Run the full pipeline to discover VSAM datasets and DB2 tables.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Section 4: Review Items (Issues) — full-width ────────────────── */}
      {/* SOURCE: asset graph issues array — only rendered when graph is loaded */}
      {(hasGraph || caLoading) && (
        <div className="ga-dash-panel">
          <div className="ga-dash-panel-header">
            <AlertTriangle size={14} className="ga-dash-panel-icon" />
            <span className="ga-dash-panel-title">Review Items</span>
            <span className={`ga-dash-source-tag${kpiIssues > 0 ? ' ga-dash-source-tag--warn' : ''}`}>
              {caLoading
                ? 'loading'
                : kpiIssues > 0
                  ? `${kpiIssues} item${kpiIssues !== 1 ? 's' : ''} · asset graph`
                  : 'none found'}
            </span>
          </div>
          {caLoading ? (
            <div className="ga-dash-pending">
              <Loader2 size={15} className="ga-load-spinner" /><span>Loading…</span>
            </div>
          ) : kpiIssues === 0 ? (
            <div className="ga-dash-ok">
              <CheckCircle2 size={15} className="ga-dash-ok-icon" />
              <span>No system issues detected — all assets and references resolved within the application boundary.</span>
            </div>
          ) : (
            <div className="ga-dash-review-groups">
              {Object.entries(issueGroups).map(([issueType, issues]) => {
                const meta = DASH_ISSUE_META[issueType]
                return (
                  <div key={issueType} className="ga-dash-review-group">
                    <div className="ga-dash-review-group-hdr">
                      <AlertTriangle size={12} className="ga-dash-issue-icon" />
                      <span className="ga-dash-review-group-label">
                        {meta?.label || issueType.replace(/_/g, ' ')}
                      </span>
                      <span className="ga-dash-issue-count">{issues.length}</span>
                    </div>
                    <div className="ga-dash-review-items">
                      {issues.slice(0, 5).map((issue, i) => (
                        <div key={i} className="ga-dash-review-item">
                          <span className="ga-dash-review-asset">
                            {issue.asset_id?.split(':').pop() || issue.asset_id}
                          </span>
                          <span className="ga-dash-review-action">
                            {issue.detail || (meta && meta.action) || ''}
                          </span>
                        </div>
                      ))}
                      {issues.length > 5 && (
                        <div className="ga-dash-review-more">
                          +{issues.length - 5} more — see Technical View → Issues
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Section 5: Application Narrative ─────────────────────────────── */}
      {/* SOURCE: deterministic from real counts — no LLM, no fake data       */}
      {narrative.length > 0 && (
        <div className="ga-dash-panel">
          <div className="ga-dash-panel-header">
            <Sparkles size={14} className="ga-dash-panel-icon" />
            <span className="ga-dash-panel-title">Application Summary</span>
            <span className="ga-dash-source-tag">derived · real data only</span>
          </div>
          <div className="ga-dash-narrative">
            {narrative.map((sentence, i) => (
              <p key={i} className="ga-dash-narrative-item">{sentence}</p>
            ))}
          </div>
        </div>
      )}

      {/* ── Section 6: Risk Intelligence (Phase 1) ───────────────────────── */}
      {/* SOURCE: intelligence_metrics.json — computed by executor Step 7.6   */}
      {(intelligenceMetrics || caLoading) && (
        <div className="ga-dash-panel">
          <div className="ga-dash-panel-header">
            <AlertTriangle size={14} className="ga-dash-panel-icon" />
            <span className="ga-dash-panel-title">Risk Intelligence</span>
            <span className="ga-dash-source-tag">intelligence · Phase 1</span>
            {/* Help affordance — same floating-window pattern as Risk Analysis */}
            <button
              ref={intelHelpBtnRef}
              className="ga-risk-help-btn ga-intel-help-btn"
              onClick={() => {
                if (!intelHelpOpen) {
                  setIntelHelpPos({
                    top:  110,
                    left: Math.max(60, Math.round(window.innerWidth * 0.30)),
                  })
                }
                setIntelHelpOpen(o => !o)
              }}
              title="What is this?"
              aria-expanded={intelHelpOpen}
            >
              <HelpCircle size={12} />
              <span className="ga-risk-help-label">What is this?</span>
            </button>
          </div>

          {/* ── Draggable floating help window for Risk Intelligence ── */}
          {intelHelpOpen && (
            <div
              className="ga-risk-help-popover"
              style={{ top: intelHelpPos.top, left: intelHelpPos.left }}
            >
              <div
                className="ga-risk-help-popover-header"
                onMouseDown={(e) => {
                  if (e.button !== 0) return
                  e.preventDefault()
                  const startX    = e.clientX
                  const startY    = e.clientY
                  const startLeft = intelHelpPos.left
                  const startTop  = intelHelpPos.top
                  const onMove = (ev) => setIntelHelpPos({
                    left: startLeft + (ev.clientX - startX),
                    top:  startTop  + (ev.clientY - startY),
                  })
                  const onUp = () => {
                    document.removeEventListener('mousemove', onMove)
                    document.removeEventListener('mouseup',   onUp)
                  }
                  document.addEventListener('mousemove', onMove)
                  document.addEventListener('mouseup',   onUp)
                }}
              >
                <span className="ga-risk-help-popover-title">About Risk Intelligence</span>
                <button
                  className="ga-risk-help-close"
                  onClick={() => setIntelHelpOpen(false)}
                  title="Close"
                >
                  <X size={12} />
                </button>
              </div>

              <div className="ga-risk-help-terms">
                <div className="ga-risk-help-term">
                  <span className="ga-risk-help-term-name" style={{ color: '#818cf8' }}>What this panel shows</span>
                  <span className="ga-risk-help-term-def">
                    A project-level summary of modernization risk across all analyzed COBOL programs.
                    It uses Phase 1 intelligence metrics — Fan-Out and Instability — to score each program
                    and group them into tiers.
                  </span>
                </div>
                <div className="ga-risk-help-term">
                  <span className="ga-risk-help-term-name" style={{ color: '#ef4444' }}>High risk</span>
                  <span className="ga-risk-help-term-def">
                    Programs with many outbound dependencies and high instability. These are the most
                    complex to modernize and most likely to cause downstream issues if transformed
                    incompletely.
                  </span>
                </div>
                <div className="ga-risk-help-term">
                  <span className="ga-risk-help-term-name" style={{ color: '#f59e0b' }}>Medium risk</span>
                  <span className="ga-risk-help-term-def">
                    Programs with moderate coupling or instability. Likely straightforward to modernize
                    but worth reviewing dependencies before starting.
                  </span>
                </div>
                <div className="ga-risk-help-term">
                  <span className="ga-risk-help-term-name" style={{ color: '#22c55e' }}>Low risk</span>
                  <span className="ga-risk-help-term-def">
                    Programs with few dependencies and stable coupling. Good candidates to modernize
                    first or in parallel without high coordination overhead.
                  </span>
                </div>
                <div className="ga-risk-help-term">
                  <span className="ga-risk-help-term-name">Scores are relative</span>
                  <span className="ga-risk-help-term-def">
                    All scores are normalized within this project. The program with the highest
                    Fan-Out gets the highest score — so "100" means most complex in this project,
                    not universally complex.
                  </span>
                </div>
              </div>

              <div className="ga-risk-help-formula" style={{ cursor: 'default' }}>
                <p className="ga-risk-help-formula-text">
                  Go to <strong style={{ color: 'var(--text-secondary, #8b92a5)' }}>Inventory → By Risk</strong> to
                  see every program ranked by score with full Fan-Out, Fan-In, and Instability breakdowns.
                </p>
              </div>
            </div>
          )}

          {caLoading && !intelligenceMetrics ? (
            <div className="ga-dash-pending">
              <Loader2 size={15} className="ga-load-spinner" />
              <span>Computing intelligence metrics…</span>
            </div>
          ) : intelligenceMetrics ? (
            <div className="ga-dash-risk-intel">
              {(() => {
                const ps     = intelligenceMetrics.project_summary || {}
                const total  = ps.total_programs || 1
                const hi     = ps.high_risk_count   || 0
                const med    = ps.medium_risk_count  || 0
                const lo     = ps.low_risk_count     || 0
                const hiPct  = Math.round(hi  / total * 100)
                const medPct = Math.round(med / total * 100)
                const loPct  = 100 - hiPct - medPct
                const progs  = intelligenceMetrics.programs || {}
                const top3   = Object.entries(progs)
                  .sort((a, b) => b[1].risk_score - a[1].risk_score)
                  .slice(0, 3)
                return (
                  <>
                    {/* Segmented tier distribution bar */}
                    <div className="ga-dash-risk-dist">
                      <div className="ga-dash-risk-dist-bar">
                        {hiPct  > 0 && <div className="ga-dash-risk-dist-seg ga-dash-risk-dist-seg--high"   style={{ width: `${hiPct}%`  }} />}
                        {medPct > 0 && <div className="ga-dash-risk-dist-seg ga-dash-risk-dist-seg--medium" style={{ width: `${medPct}%` }} />}
                        {loPct  > 0 && <div className="ga-dash-risk-dist-seg ga-dash-risk-dist-seg--low"    style={{ width: `${loPct}%`  }} />}
                      </div>
                      <div className="ga-dash-risk-dist-labels">
                        <span className="ga-dash-risk-dist-lbl ga-dash-risk-dist-lbl--high">{hi} High</span>
                        <span className="ga-dash-risk-dist-lbl ga-dash-risk-dist-lbl--medium">{med} Medium</span>
                        <span className="ga-dash-risk-dist-lbl ga-dash-risk-dist-lbl--low">{lo} Low</span>
                        <span className="ga-dash-risk-dist-total">{total} program{total !== 1 ? 's' : ''} scored</span>
                      </div>
                    </div>

                    {/* Top-3 highest-risk programs */}
                    {top3.length > 0 && (
                      <div className="ga-dash-risk-top">
                        <span className="ga-dash-risk-top-label">Highest Risk</span>
                        {top3.map(([stem, m], idx) => (
                          <div key={stem} className="ga-dash-risk-top-row">
                            <span className="ga-dash-risk-top-rank">{idx + 1}</span>
                            <span className="ga-dash-risk-top-name">{stem}</span>
                            <div className="ga-dash-risk-top-bar-wrap">
                              <div
                                className={`ga-dash-risk-top-bar ga-dash-risk-top-bar--${m.risk_tier}`}
                                style={{ width: `${m.risk_score}%` }}
                              />
                            </div>
                            <span className={`ga-risk-badge ga-risk-badge--${m.risk_tier}`}>{m.risk_score}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Modernization note */}
                    <p className="ga-dash-risk-intel-note">
                      Score = Fan-Out (60%) + Instability (40%) — relative to this project.
                      Switch to <strong>Inventory → By Risk</strong> to see all programs ranked.
                    </p>
                  </>
                )
              })()}
            </div>
          ) : null}
        </div>
      )}

      {/* ── Section 7: Dead Code Detection (Tier 1) ────────────────────────── */}
      {deadCode?.records?.length > 0 && (() => {
        const deadParas = deadCode.records.filter(r => !r.is_entry_point)
        const entryParas = deadCode.records.filter(r => r.is_entry_point)
        return (
          <div className="ga-dash-panel">
            <div className="ga-dash-panel-header">
              <AlertCircle size={14} className="ga-dash-panel-icon" />
              <span className="ga-dash-panel-title">Dead Code Detection</span>
              <span className="ga-dash-source-tag">graph · Tier 1</span>
            </div>
            {deadParas.length > 0 ? (
              <>
                <p style={{ color: 'var(--text-secondary, #8b92a5)', fontSize: '12px', margin: '0 0 8px' }}>
                  {deadParas.length} paragraph{deadParas.length !== 1 ? 's' : ''} never PERFORMed — unreachable code candidates
                </p>
                <table className="ga-tv-table">
                  <thead>
                    <tr>
                      <th>Paragraph</th>
                      <th>Program</th>
                      <th className="ga-tv-th-num">Stmts</th>
                      <th className="ga-tv-th-num">Lines</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deadParas.slice(0, 15).map((r, i) => (
                      <tr key={i}>
                        <td className="ga-tv-td-name">{r.paragraph}</td>
                        <td>{r.program}</td>
                        <td className="ga-tv-td-num">{r.statement_count ?? '—'}</td>
                        <td className="ga-tv-td-num">
                          {r.start_line && r.end_line ? `${r.start_line}–${r.end_line}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {deadParas.length > 15 && (
                  <p style={{ color: 'var(--text-tertiary, #666)', fontSize: '11px', margin: '6px 0 0' }}>
                    +{deadParas.length - 15} more unreachable paragraphs
                  </p>
                )}
              </>
            ) : (
              <p style={{ color: '#22c55e', fontSize: '12px' }}>
                No unreachable code found — all paragraphs are reachable via PERFORM chains.
              </p>
            )}
            {entryParas.length > 0 && (
              <p style={{ color: 'var(--text-tertiary, #666)', fontSize: '11px', margin: '6px 0 0' }}>
                {entryParas.length} entry-point paragraph{entryParas.length !== 1 ? 's' : ''} (no incoming PERFORMs but contain outgoing calls — expected)
              </p>
            )}
          </div>
        )
      })()}

    </div>
  )
}

// ── Technical View: issue type descriptions ──────────────────────────────────

const ISSUE_TYPE_META = {
  JCL_EXTERNAL_DATASET: {
    label:   'External Dataset',
    explain: (assetId) => `"${assetId}" is referenced in JCL but not found in the application source — it likely belongs to a shared library or production catalog.`,
    action:  'Decide whether to migrate this dataset or replace it with a new data store.',
  },
  DB2_TABLE_UNREFERENCED: {
    label:   'Unreferenced DB2 Table',
    explain: (assetId) => `"${assetId}" was found in DDL/DCLGEN but no program references it via SQL. It may be unused or the SQL was not detected.`,
    action:  'Verify whether this table is accessed from another source, or exclude it from the migration scope.',
  },
  UNRESOLVED_LOGICAL_FILE: {
    label:   'Unresolved Logical File',
    explain: (assetId) => `"${assetId}" is a SELECT-ASSIGN logical file name that could not be matched to a physical dataset in the catalog or JCL DD statements.`,
    action:  'Check FILE-CONTROL SELECT/ASSIGN values and JCL DD/DSN entries for a matching dataset.',
  },
  UNRESOLVED_COPYBOOK: {
    label:   'Unresolved Copybook',
    explain: (assetId) => `"${assetId}" is referenced in a COPY statement but was not found in the analyzed source.`,
    action:  'Ensure all required copybooks are included in the upload.',
  },
  MISSING_FD_FOR_SELECT: {
    label:   'Missing File Description',
    explain: (assetId) => `A SELECT statement exists for "${assetId}" but no matching FD (File Description) was found in the DATA DIVISION FILE SECTION.`,
    action:  'Verify the FD entry is present in the COBOL source.',
  },
  JCL_UNRESOLVED_PROGRAM: {
    label:   'Unresolved JCL Program',
    explain: (assetId) => `"${assetId}" is referenced in a JCL EXEC PGM= statement but was not found in the application source.`,
    action:  'Include the program in the upload or note it as an external dependency.',
  },
  DB2_TABLE_MISSING_DDL: {
    label:   'Missing DB2 DDL',
    explain: (assetId) => `"${assetId}" is accessed via SQL in a COBOL program but no CREATE TABLE DDL or DCLGEN source was found.`,
    action:  'Include the DDL or DCLGEN file for this table in the upload.',
  },
}

// ── Technical View sub-components ───────────────────────────────────────────

/** "Not available" state for a Technical View section */
function TechPending({ title, hint }) {
  return (
    <div className="ga-tv-pending">
      <p className="ga-tv-pending-title">{title}</p>
      <p className="ga-tv-pending-hint">{hint}</p>
    </div>
  )
}

/**
 * Technical View tab content.
 *
 * Six inner sub-tabs powered by comprehensive_parse_results.json:
 *
 *   Dependencies  — cross_reference.call_graph
 *   Program Flow  — programs[].paragraphs / calls / line_count
 *   Copybooks     — cross_reference.copybook_usage, summary.copy_statements_*
 *   Data Model    — unified_data_model.items + stats
 *   File I/O      — cross_reference.file_usage
 *   Statistics    — summary.*
 *
 * All sections show an honest "unavailable" state if the data field is empty
 * or code analysis has not yet run. No fake values.
 */

// ── Asset graph → React Flow nodes/edges transform ────────────────────────────
const AG_TYPE_MAP = {
  COBOL_PROGRAM: { nodeType: 'program',  fillColor: '#4a90d9' },
  COPYBOOK:      { nodeType: 'copybook', fillColor: '#6b8e23' },
  DB2_TABLE:     { nodeType: 'file',     fillColor: '#d4a574' },
  DB2_DCLGEN:    { nodeType: 'file',     fillColor: '#d4a574' },
  VSAM_DATASET:  { nodeType: 'file',     fillColor: '#d4a574' },
  DATA_FILE:     { nodeType: 'file',     fillColor: '#d4a574' },
  DATASET:       { nodeType: 'file',     fillColor: '#d4a574' },
  JCL_JOB:       { nodeType: 'program',  fillColor: '#9b59b6' },
}
const AG_EDGE_COLOR = {
  CALLS: '#4a90d9', COPIES: '#6b8e23', READS: '#d4a574',
  WRITES: '#e74c3c', FILE_IO: '#d4a574', EXECUTES: '#9b59b6',
}
function buildAssetGraphData(ag) {
  const safeId = id => (id || '').replace(/[^a-zA-Z0-9_-]/g, '_')
  const nodes = (ag?.assets || []).map(a => {
    const cfg = AG_TYPE_MAP[a.type] || { nodeType: 'program', fillColor: '#8b949e' }
    return {
      id: safeId(a.id),
      type: 'dependency',
      position: { x: 0, y: 0 },
      data: { label: a.name || safeId(a.id), nodeType: cfg.nodeType, fillColor: cfg.fillColor },
    }
  })
  const edges = (ag?.relationships || []).map((r, i) => ({
    id: `ag-edge-${i}`,
    source: safeId(r.from),
    target: safeId(r.to),
    type: 'smoothstep',
    label: r.type,
    style: { stroke: AG_EDGE_COLOR[r.type] || '#666' },
    labelStyle: { fill: '#ccc', fontSize: 10 },
  }))
  return { nodes, edges }
}

function TechnicalViewContent({ parseData, assetGraph, caLoading, catalogData, intelligenceMetrics, copybookImpact, fieldXref, complexityHeatmap, gnucobolEnrichment, performChain }) {
  const [tvTab, setTvTab] = useState('Dependencies')
  const [compilerSubTab, setCompilerSubTab] = useState('Compile Status')
  const [xrefFilter, setXrefFilter] = useState('')
  // graphModal: null | { type: 'call' | 'asset', title: string }
  const [graphModal, setGraphModal] = useState(null)
  const openGraphModal = (type, title) => {
    console.log(`[GraphModal] Opening ${title} — type=${type}`)
    setGraphModal({ type, title })
  }
  const closeGraphModal = () => {
    console.log('[GraphModal] Closed')
    setGraphModal(null)
  }

  // ── Loading / no-data guards ─────────────────────────────────────────────
  if (caLoading) {
    return (
      <div className="ga-tv-loading">
        <Loader2 size={22} className="ga-load-spinner" />
        <span>Looking for code analysis data…</span>
      </div>
    )
  }

  if (!parseData && !assetGraph) {
    return (
      <div className="ga-tab-placeholder">
        <Code2 size={36} className="ga-tab-placeholder-icon" />
        <p className="ga-tab-placeholder-title">Code Analysis Not Yet Run</p>
        <p className="ga-tab-placeholder-hint">
          Technical View is powered by <strong>comprehensive_parse_results.json</strong> and{' '}
          <strong>guided_analyze_asset_graph.json</strong>.
          Run the full pipeline for this project to see dependency graphs, copybook analysis,
          data assets, JCL execution, issues, and statistics.
        </p>
      </div>
    )
  }

  // ── Extract real data from parseData (optional chaining — parseData may be null) ──
  const callGraph   = parseData?.cross_reference?.call_graph    || {}
  const cbUsage     = parseData?.cross_reference?.copybook_usage || {}
  const fileUsage   = parseData?.cross_reference?.file_usage    || {}
  const programs    = parseData?.programs  || {}
  const udm         = parseData?.unified_data_model || {}
  const sum         = parseData?.summary   || {}

  // Call-graph in-degree (how many programs call a given program)
  const inDegree = {}
  for (const callees of Object.values(callGraph)) {
    for (const callee of (callees || [])) {
      const key = String(callee).split('/').pop().replace(/\.[^.]+$/, '').toLowerCase()
      inDegree[key] = (inDegree[key] || 0) + 1
    }
  }

  // Dependencies rows: sorted by total connectivity
  const depsRows = Object.entries(callGraph)
    .map(([prog, callees]) => {
      const baseName = prog.split('/').pop().replace(/\.[^.]+$/, '').toLowerCase()
      return {
        name:     prog.split('/').pop(),
        callsOut: (callees || []).length,
        calledBy: inDegree[baseName] || 0,
        callees:  callees || [],
      }
    })
    .sort((a, b) => (b.callsOut + b.calledBy) - (a.callsOut + a.calledBy))

  // Program Flow rows: sorted by line count descending
  const flowRows = Object.entries(programs)
    .map(([name, p]) => ({
      name:           name.split('/').pop(),
      lineCount:      p.line_count          || 0,
      paragraphs:     (p.paragraphs         || []).length,
      calls:          (p.calls              || []).length,
      copyStatements: (p.copy_statements    || []).length,
    }))
    .sort((a, b) => b.lineCount - a.lineCount)

  // Derived paragraph count — comprehensive_parse summary does NOT include source_lines
  // or paragraphs, but they ARE in programs[].paragraphs[]. Compute them here so
  // Statistics can show real values instead of misleading zeros.
  const derivedParagraphs = Object.values(programs)
    .reduce((n, p) => n + ((p.paragraphs || []).length), 0)

  // Copybook rows: sorted by usage count
  const cbRows = Object.entries(cbUsage)
    .map(([name, users]) => ({
      name:     name.split('/').pop(),
      usedBy:   (users || []).length,
      programs: users || [],
    }))
    .sort((a, b) => b.usedBy - a.usedBy)

  const totalCopy     = sum.copy_statements_total    || 0
  const resolvedCopy  = sum.copy_statements_resolved || 0
  const unresolvedCopy = Math.max(totalCopy - resolvedCopy, 0)

  // Data model
  const udmStats      = udm.stats || {}
  const udmItems      = udm.items || []
  const topLevelItems = udmItems.filter(i => String(i.level) === '01' || i.level === 1)

  // File I/O rows
  const fileRows = Object.entries(fileUsage)
    .map(([name, progs]) => ({
      name,
      usedBy:   (progs || []).length,
      programs: progs || [],
    }))
    .sort((a, b) => b.usedBy - a.usedBy)

  console.log('[TechnicalView] Data loaded:', {
    tvTab,
    callGraphPrograms: Object.keys(callGraph).length,
    cbUsageEntries:    Object.keys(cbUsage).length,
    fileUsageEntries:  Object.keys(fileUsage).length,
    programCount:      Object.keys(programs).length,
    udmItemCount:      udmItems.length,
    topLevel01Count:   topLevelItems.length,
    totalCopyStmts:    totalCopy,
    resolvedCopyStmts: resolvedCopy,
    assetGraphAssets:  assetGraph?.assets?.length || 0,
    assetGraphIssues:  assetGraph?.issues?.length || 0,
  })

  // ── Asset graph extractions ───────────────────────────────────────────────
  const agAssets        = assetGraph?.assets        || []
  const agIssues        = assetGraph?.issues         || []
  const agRelationships = assetGraph?.relationships  || []
  const agDb2Assets     = agAssets.filter(a => a.type === 'DB2_TABLE' || a.type === 'DB2_DCLGEN')
  const agVsamAssets    = agAssets.filter(a => a.type === 'VSAM_DATASET' || a.type === 'DATA_FILE')
  const agJclAssets     = agAssets.filter(a => a.type === 'JCL_JOB')
  const agDatasetAssets = agAssets.filter(a => a.type === 'DATASET')
  const agPrograms      = agAssets.filter(a => a.type === 'COBOL_PROGRAM')

  // Access map: asset id → { reads: [progName], writes: [progName] }
  const dataAccessMap = {}
  for (const rel of agRelationships) {
    if (rel.type === 'READS' || rel.type === 'WRITES' || rel.type === 'FILE_IO') {
      if (!dataAccessMap[rel.to]) dataAccessMap[rel.to] = { reads: [], writes: [] }
      const fromName = rel.from.split(':').pop()
      if (rel.type === 'READS')       dataAccessMap[rel.to].reads.push(fromName)
      else if (rel.type === 'WRITES') dataAccessMap[rel.to].writes.push(fromName)
      else {
        dataAccessMap[rel.to].reads.push(fromName)
        dataAccessMap[rel.to].writes.push(fromName)
      }
    }
  }

  // Copybook usage from asset graph COPIES relationships
  const agCbUsageMap = {}
  for (const rel of agRelationships) {
    if (rel.type === 'COPIES') {
      if (!agCbUsageMap[rel.to]) agCbUsageMap[rel.to] = []
      agCbUsageMap[rel.to].push(rel.from.split(':').pop())
    }
  }

  // Issues grouped by type
  const agIssuesByType = {}
  for (const issue of agIssues) {
    const t = issue.issue_type || issue.type || 'UNKNOWN'
    if (!agIssuesByType[t]) agIssuesByType[t] = []
    agIssuesByType[t].push(issue)
  }

  return (
    <div className="ga-tv">

      {/* ── Inner sub-tab bar ─────────────────────────────────────────────── */}
      <div className="ga-tv-tabs">
        {TV_TABS.map(t => (
          <button
            key={t}
            className={`ga-tv-tab${tvTab === t ? ' active' : ''}`}
            onClick={() => {
              setTvTab(t)
              console.log('[TechnicalView] Tab switched to:', t)
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ── Section body ──────────────────────────────────────────────────── */}
      <div className="ga-tv-body">

        {/* DEPENDENCIES — SOURCE: cross_reference.call_graph + asset graph programs */}
        {tvTab === 'Dependencies' && (
          <div className="ga-tv-section">

            {/* ── Program overview (from asset graph) ── */}
            {agPrograms.length > 0 && (
              <>
                <div className="ga-tv-section-hdr">
                  <Network size={14} className="ga-tv-section-icon" />
                  <span className="ga-tv-section-title">Program Inventory</span>
                  <button
                    className="ga-graph-btn"
                    onClick={() => {
                      console.log('[GraphModal] Asset Graph launched from Program Inventory — assets:', (assetGraph?.assets || []).length, 'relationships:', (assetGraph?.relationships || []).length)
                      openGraphModal('asset', 'Asset Graph')
                    }}
                    title="View asset graph — programs, copybooks, files and their relationships"
                  >
                    <Share2 size={13} />
                    Asset Graph
                  </button>
                </div>
                <table className="ga-tv-table">
                  <thead>
                    <tr>
                      <th>Program</th>
                      <th className="ga-tv-th-num">Lines</th>
                      <th className="ga-tv-th-num">Paragraphs</th>
                      <th className="ga-tv-th-num">COPY</th>
                      <th className="ga-tv-th-num">CALLs</th>
                      <th>Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agPrograms
                      .slice()
                      .sort((a, b) => (b.metadata?.line_count || 0) - (a.metadata?.line_count || 0))
                      .map(a => {
                        const m = a.metadata || {}
                        const flags = []
                        if (m.has_sql)  flags.push('SQL')
                        if (m.has_cics) flags.push('CICS')
                        if (m.has_ims)  flags.push('IMS')
                        if (m.has_mq)   flags.push('MQ')
                        return (
                          <tr key={a.id}>
                            <td className="ga-tv-td-name">{a.name}</td>
                            <td className="ga-tv-td-num">{m.line_count ? m.line_count.toLocaleString() : '—'}</td>
                            <td className="ga-tv-td-num">{m.paragraph_count ?? '—'}</td>
                            <td className="ga-tv-td-num">{m.copy_statement_count ?? '—'}</td>
                            <td className="ga-tv-td-num">{m.call_count ?? '—'}</td>
                            <td className="ga-tv-td-list">
                              {flags.length > 0
                                ? flags.map(f => (
                                    <span key={f} className="ga-tv-flag-tag">{f}</span>
                                  ))
                                : <span className="ga-tv-muted">—</span>
                              }
                            </td>
                          </tr>
                        )
                      })
                    }
                  </tbody>
                </table>
              </>
            )}

            {/* ── Program-to-program calls (from call graph) ── */}
            <div className="ga-tv-section-hdr" style={{ marginTop: agPrograms.length > 0 ? '20px' : 0 }}>
              <Network size={14} className="ga-tv-section-icon" />
              <span className="ga-tv-section-title">Program-to-Program Dependencies</span>
              <button
                className="ga-graph-btn"
                onClick={() => {
                  console.log('[GraphModal] Call Graph launched — programs in callGraph:', Object.keys(callGraph).length)
                  openGraphModal('call', 'Call Graph')
                }}
                title="View call graph — program-to-program CALL relationships"
              >
                <Share2 size={13} />
                Call Graph
              </button>
            </div>
            {depsRows.length > 0 ? (
              <table className="ga-tv-table">
                <thead>
                  <tr>
                    <th>Program</th>
                    <th className="ga-tv-th-num">Calls Out</th>
                    <th className="ga-tv-th-num">Called By</th>
                    <th>Calls To</th>
                  </tr>
                </thead>
                <tbody>
                  {depsRows.map(row => (
                    <tr key={row.name}>
                      <td className="ga-tv-td-name">{row.name}</td>
                      <td className="ga-tv-td-num">{row.callsOut}</td>
                      <td className="ga-tv-td-num">{row.calledBy}</td>
                      <td className="ga-tv-td-list">
                        {row.callees.length > 0
                          ? row.callees.slice(0, 5).join(', ') +
                            (row.callees.length > 5 ? ` +${row.callees.length - 5} more` : '')
                          : <span className="ga-tv-muted">—</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : agPrograms.length === 0 ? (
              <TechPending
                title="No dependency data found"
                hint="Run Code Analysis to populate program dependency and call graph data."
              />
            ) : (
              <div className="ga-tv-pending">
                <p className="ga-tv-pending-hint">No external CALL statements detected — programs appear to be self-contained.</p>
              </div>
            )}

            {/* ── File / dataset access (from asset graph READS/WRITES) ── */}
            {(() => {
              const fileAccessRels = agRelationships.filter(r =>
                r.type === 'READS' || r.type === 'WRITES' || r.type === 'FILE_IO'
              )
              if (fileAccessRels.length === 0) return null
              // Group by program
              const byProg = {}
              for (const rel of fileAccessRels) {
                const prog = rel.from.split(':').pop()
                if (!byProg[prog]) byProg[prog] = { reads: [], writes: [] }
                const dsn = rel.to.split(':').pop()
                if (rel.type === 'READS')       byProg[prog].reads.push(dsn)
                else if (rel.type === 'WRITES') byProg[prog].writes.push(dsn)
                else {
                  byProg[prog].reads.push(dsn)
                  byProg[prog].writes.push(dsn)
                }
              }
              const progRows = Object.entries(byProg).sort((a, b) =>
                (b[1].reads.length + b[1].writes.length) - (a[1].reads.length + a[1].writes.length)
              )
              return (
                <>
                  <div className="ga-tv-section-subhdr" style={{ marginTop: '20px' }}>
                    File &amp; Dataset Access — {fileAccessRels.length} relationships
                  </div>
                  <table className="ga-tv-table">
                    <thead>
                      <tr>
                        <th>Program</th>
                        <th>Reads From</th>
                        <th>Writes To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {progRows.map(([prog, acc]) => (
                        <tr key={prog}>
                          <td className="ga-tv-td-name">{prog}</td>
                          <td className="ga-tv-td-list">
                            {acc.reads.length > 0
                              ? acc.reads.slice(0, 3).join(', ') + (acc.reads.length > 3 ? ` +${acc.reads.length - 3}` : '')
                              : <span className="ga-tv-muted">—</span>
                            }
                          </td>
                          <td className="ga-tv-td-list">
                            {acc.writes.length > 0
                              ? acc.writes.slice(0, 3).join(', ') + (acc.writes.length > 3 ? ` +${acc.writes.length - 3}` : '')
                              : <span className="ga-tv-muted">—</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )
            })()}

            {/* ── Graph Modal (Call Graph / Asset Graph) ────────────────── */}
            {/* ── PERFORM chain (paragraph-level internal calls) ── */}
            {performChain && performChain.records?.length > 0 && (() => {
              // Build tree structure from flat caller→callee records
              const records = performChain.records
              const programs = [...new Set(records.map(r => r.caller_program))]
              // For each program, build adjacency: caller → [callees]
              const byProgram = {}
              for (const r of records) {
                const prog = r.caller_program
                if (!byProgram[prog]) byProgram[prog] = {}
                if (!byProgram[prog][r.caller]) byProgram[prog][r.caller] = []
                byProgram[prog][r.caller].push(r.callee)
              }
              // Find root paragraphs (callers that are never callees, or have most outgoing)
              const findRoots = (adj) => {
                const allCallees = new Set()
                for (const targets of Object.values(adj)) {
                  for (const t of targets) allCallees.add(t)
                }
                const roots = Object.keys(adj).filter(k => !allCallees.has(k))
                return roots.length > 0 ? roots : [Object.keys(adj)[0]]
              }

              // Render tree recursively (max 4 levels to avoid explosion)
              const renderTree = (adj, node, depth, visited) => {
                if (depth > 4 || visited.has(node)) return null
                visited.add(node)
                const children = adj[node] || []
                const isLeaf = children.length === 0 || depth === 4
                return (
                  <div key={node} style={{ marginLeft: depth * 20, borderLeft: depth > 0 ? '1px solid var(--border-primary, #333)' : 'none', paddingLeft: depth > 0 ? 10 : 0, marginBottom: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
                      <span style={{ fontSize: '10px', color: 'var(--text-tertiary, #666)', width: 14, textAlign: 'center' }}>
                        {isLeaf ? '·' : '▾'}
                      </span>
                      <span style={{ fontSize: '12px', fontFamily: 'var(--font-mono, monospace)', color: children.length > 0 ? 'var(--text-primary, #e0e0e0)' : 'var(--text-secondary, #8b92a5)' }}>
                        {node}
                      </span>
                      {children.length > 0 && (
                        <span style={{ fontSize: '9px', color: 'var(--text-tertiary, #666)', padding: '1px 4px', background: 'var(--bg-tertiary, #2a2a3a)', borderRadius: 3 }}>
                          {children.length} call{children.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      {visited.has(node) && depth > 0 && (
                        <span style={{ fontSize: '9px', color: '#facc15', padding: '1px 4px', background: 'rgba(250,204,21,0.1)', borderRadius: 3 }}>recursive</span>
                      )}
                    </div>
                    {children.map(c => renderTree(adj, c, depth + 1, new Set(visited)))}
                  </div>
                )
              }

              return (
                <div style={{ marginTop: '20px' }}>
                  <div className="ga-tv-section-hdr">
                    <GitBranch size={14} className="ga-tv-section-icon" />
                    <span className="ga-tv-section-title">PERFORM Chain — {records.length} paragraph calls</span>
                    <span className="ga-dash-source-tag">{programs.length} program{programs.length !== 1 ? 's' : ''}</span>
                  </div>

                  {programs.map(prog => {
                    const adj = byProgram[prog]
                    const roots = findRoots(adj)
                    const totalParas = new Set([...Object.keys(adj), ...Object.values(adj).flat()])
                    return (
                      <div key={prog} style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary, #8b92a5)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                          {prog}
                          <span style={{ fontWeight: 400, fontSize: '10px', color: 'var(--text-tertiary, #666)' }}>
                            {totalParas.size} paragraphs · {Object.values(adj).flat().length} PERFORM calls
                          </span>
                        </div>
                        <div style={{ background: 'var(--bg-secondary, #1a1a2e)', borderRadius: 6, padding: '10px 14px', border: '1px solid var(--border-primary, #333)', maxHeight: 400, overflowY: 'auto' }}>
                          {roots.map(r => renderTree(adj, r, 0, new Set()))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })()}

            {graphModal && (() => {
              const isCall  = graphModal.type === 'call'
              const isEmpty = isCall
                ? Object.keys(callGraph).length === 0
                : (assetGraph?.assets || []).length === 0
              const badge = isCall
                ? `${Object.keys(callGraph).length} programs`
                : `${(assetGraph?.assets || []).length} nodes · ${(assetGraph?.relationships || []).length} edges`
              const graphData = isCall
                ? parseData?.cross_reference
                : buildAssetGraphData(assetGraph)
              const mainProg = isCall
                ? (Object.keys(callGraph)[0]?.split('/').pop()?.replace(/\.[^.]+$/, '') || 'MAIN')
                : undefined
              const emptyHint = isCall
                ? 'Run Code Analysis to generate the call graph.'
                : 'Run Code Analysis to populate the asset graph.'

              return (
                <div
                  className="ga-graph-modal-overlay"
                  onClick={closeGraphModal}
                >
                  <div className="ga-graph-modal" onClick={e => e.stopPropagation()}>
                    <div className="ga-graph-modal-header">
                      <div className="ga-graph-modal-title">
                        <Share2 size={16} />
                        <span>{graphModal.title}</span>
                        {!isEmpty && (
                          <span className="ga-graph-modal-badge">{badge}</span>
                        )}
                      </div>
                      <button
                        className="ga-graph-modal-close"
                        onClick={closeGraphModal}
                        title="Close graph"
                      >
                        <X size={18} />
                      </button>
                    </div>

                    <div className="ga-graph-modal-body">
                      {isEmpty ? (
                        <div className="ga-graph-empty-state">
                          <Network size={40} />
                          <p className="ga-graph-empty-title">No data available</p>
                          <p className="ga-graph-empty-hint">{emptyHint}</p>
                        </div>
                      ) : (
                        <ReactFlowProvider>
                          <DependencyGraph
                            graphData={graphData}
                            mainProgram={mainProg}
                          />
                        </ReactFlowProvider>
                      )}
                    </div>
                  </div>
                </div>
              )
            })()}

          </div>
        )}

        {/* ISSUES — SOURCE: guided_analyze_asset_graph.issues[] */}
        {tvTab === 'Issues' && (
          <div className="ga-tv-section">
            <div className="ga-tv-section-hdr">
              <AlertTriangle size={14} className="ga-tv-section-icon" />
              <span className="ga-tv-section-title">System Issues</span>
              <span className="ga-dash-source-tag">asset graph</span>
            </div>
            {agIssues.length > 0 ? (
              <>
                {/* Summary counts by type */}
                <div className="ga-tv-issue-summary">
                  {Object.entries(agIssuesByType).map(([type, issues]) => {
                    const meta = ISSUE_TYPE_META[type]
                    return (
                      <div key={type} className="ga-tv-issue-summary-chip">
                        <span className="ga-tv-issue-summary-count">{issues.length}</span>
                        <span className="ga-tv-issue-summary-label">{meta?.label || type.replace(/_/g, ' ')}</span>
                      </div>
                    )
                  })}
                </div>

                {/* Issues grouped by type */}
                {Object.entries(agIssuesByType).map(([type, issues]) => {
                  const meta     = ISSUE_TYPE_META[type]
                  const severity = (issues[0]?.severity || 'info').toLowerCase()
                  return (
                    <div key={type} className="ga-tv-issue-group">
                      <div className="ga-tv-issue-group-hdr">
                        <span className={`ga-tv-severity-badge ga-tv-severity--${severity}`}>{severity}</span>
                        <span className="ga-tv-issue-group-title">{meta?.label || type.replace(/_/g, ' ')}</span>
                        <span className="ga-tv-issue-group-count">{issues.length}</span>
                      </div>
                      {meta?.action && (
                        <div className="ga-tv-issue-action">
                          <strong>Action:</strong> {meta.action}
                        </div>
                      )}
                      <table className="ga-tv-table">
                        <thead>
                          <tr>
                            <th>Asset</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {issues.map((issue, i) => {
                            const assetId  = issue.asset_id?.split(':').pop() || issue.asset_id || '—'
                            const describe = meta?.explain ? meta.explain(assetId) : (issue.message || '—')
                            return (
                              <tr key={i} className={`ga-tv-issue-row ga-tv-issue--${severity}`}>
                                <td className="ga-tv-td-name">{assetId}</td>
                                <td className="ga-tv-td-list">{describe}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )
                })}
              </>
            ) : assetGraph ? (
              <div className="ga-dash-ok">
                <CheckCircle2 size={15} className="ga-dash-ok-icon" />
                <span>No system issues detected in the asset graph.</span>
              </div>
            ) : (
              <TechPending
                title="Asset graph not available"
                hint="Issue detection is powered by guided_analyze_asset_graph.json. Run Code Analysis to generate this artifact."
              />
            )}
          </div>
        )}

        {/* COPYBOOKS — SOURCE: cross_reference.copybook_usage + asset graph COPIES */}
        {tvTab === 'Copybooks' && (
          <div className="ga-tv-section">
            <div className="ga-tv-section-hdr">
              <BookOpen size={14} className="ga-tv-section-icon" />
              <span className="ga-tv-section-title">Copybook Usage</span>
              <span className="ga-dash-source-tag">cross_reference.copybook_usage</span>
            </div>

            {/* COPY resolution summary bar */}
            {totalCopy > 0 && (
              <div className="ga-tv-resolve-bar">
                <span className="ga-tv-resolve-label">
                  {resolvedCopy} of {totalCopy} COPY statements resolved
                  {unresolvedCopy > 0 && (
                    <span className="ga-tv-unresolved"> — {unresolvedCopy} unresolved</span>
                  )}
                </span>
                <div className="ga-comp-bar-track ga-tv-resolve-track">
                  <div
                    className="ga-comp-bar-fill"
                    style={{
                      width: `${Math.round(resolvedCopy / totalCopy * 100)}%`,
                      backgroundColor: unresolvedCopy > 0 ? '#f59e0b' : '#22c55e',
                    }}
                  />
                </div>
                <span className="ga-tv-resolve-pct">
                  {Math.round(resolvedCopy / totalCopy * 100)}%
                </span>
              </div>
            )}

            {/* Unresolved copybook issues callout */}
            {(agIssuesByType['UNRESOLVED_COPYBOOK'] || []).length > 0 && (
              <div className="ga-tv-issue-callout ga-tv-issue-callout--warn">
                <AlertTriangle size={13} />
                <span>
                  {agIssuesByType['UNRESOLVED_COPYBOOK'].length} unresolved copybook
                  {agIssuesByType['UNRESOLVED_COPYBOOK'].length > 1 ? 's' : ''}:{' '}
                  {agIssuesByType['UNRESOLVED_COPYBOOK'].map(i => i.asset_id?.split(':').pop() || i.asset_id).join(', ')}
                </span>
              </div>
            )}

            {cbRows.length > 0 ? (() => {
              const maxUse = Math.max(...cbRows.map(r => r.usedBy), 1)
              return (
                <table className="ga-tv-table">
                  <thead>
                    <tr>
                      <th>Copybook</th>
                      <th className="ga-tv-th-num">Programs</th>
                      <th style={{ width: '140px' }}>Reuse</th>
                      <th>Used By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cbRows.map(row => (
                      <tr key={row.name}>
                        <td className="ga-tv-td-name">
                          {row.name}
                          {row.usedBy >= 4 && (
                            <span className="ga-tv-hub-badge" title="High-reuse hub copybook">hub</span>
                          )}
                        </td>
                        <td className="ga-tv-td-num">{row.usedBy}</td>
                        <td>
                          <div className="ga-tv-usage-bar-track">
                            <div
                              className="ga-tv-usage-bar-fill"
                              style={{ width: `${Math.round(row.usedBy / maxUse * 100)}%` }}
                            />
                          </div>
                        </td>
                        <td className="ga-tv-td-list">
                          {row.programs.length > 0
                            ? row.programs.slice(0, 4).join(', ') +
                              (row.programs.length > 4 ? ` +${row.programs.length - 4} more` : '')
                            : <span className="ga-tv-muted">—</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            })() : (
              <TechPending
                title="No copybook usage data found"
                hint="cross_reference.copybook_usage is empty. No COPY statements may have been resolved, or the copybook resolution step did not complete for this run."
              />
            )}

            {/* ── Copybook Change Impact Analysis (Tier 1) ── */}
            {copybookImpact?.records?.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <div className="ga-tv-section-subhdr">
                  Change Impact Analysis — {copybookImpact.record_count} copybooks
                </div>
                <table className="ga-tv-table">
                  <thead>
                    <tr>
                      <th>Copybook</th>
                      <th className="ga-tv-th-num">Programs</th>
                      <th className="ga-tv-th-num">Fields</th>
                      <th className="ga-tv-th-num">Paragraphs</th>
                      <th className="ga-tv-th-num">Impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {copybookImpact.records.slice(0, 20).map((r, i) => {
                      const score = r.impact_score || 0
                      const color = score > 10 ? '#ef4444' : score > 5 ? '#f59e0b' : '#22c55e'
                      return (
                        <tr key={i}>
                          <td className="ga-tv-td-name">{r.copybook}</td>
                          <td className="ga-tv-td-num">{r.program_count}</td>
                          <td className="ga-tv-td-num">{r.field_count}</td>
                          <td className="ga-tv-td-num">{r.paragraph_count}</td>
                          <td className="ga-tv-td-num">
                            <span style={{ color, fontWeight: 600 }}>{score}</span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                {copybookImpact.records.length > 20 && (
                  <p style={{ color: 'var(--text-tertiary, #666)', fontSize: '11px', margin: '6px 0 0' }}>
                    +{copybookImpact.records.length - 20} more copybooks
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* DATA ASSETS — SOURCE: asset graph DB2/VSAM + unified_data_model */}
        {tvTab === 'Data Assets' && (
          <div className="ga-tv-section">
            <div className="ga-tv-section-hdr">
              <Database size={14} className="ga-tv-section-icon" />
              <span className="ga-tv-section-title">Data Assets</span>
              <span className="ga-dash-source-tag">asset graph + unified_data_model</span>
            </div>

            {/* DB2 Tables from asset graph */}
            {agDb2Assets.length > 0 && (
              <>
                <div className="ga-tv-section-subhdr">DB2 Tables — {agDb2Assets.length}</div>
                <table className="ga-tv-table">
                  <thead>
                    <tr>
                      <th>Table</th>
                      <th className="ga-tv-th-num">Columns</th>
                      <th>Accessed By</th>
                      <th>Source File</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agDb2Assets.map(a => {
                      const access = dataAccessMap[a.id] || {}
                      const readers = access.reads  || []
                      const writers = access.writes || []
                      const allProgs = [...new Set([...readers, ...writers])]
                      return (
                        <tr key={a.id}>
                          <td className="ga-tv-td-name">
                            {a.name}
                            <span className={`ga-tv-type-tag ga-tv-type-tag--${(a.type || '').toLowerCase().replace(/_/g, '-')} ga-tv-type-tag--inline`}>
                              {a.type}
                            </span>
                          </td>
                          <td className="ga-tv-td-num">{a.metadata?.column_count ?? '—'}</td>
                          <td className="ga-tv-td-list">
                            {allProgs.length > 0
                              ? allProgs.slice(0, 3).join(', ') + (allProgs.length > 3 ? ` +${allProgs.length - 3}` : '')
                              : <span className="ga-tv-muted">—</span>
                            }
                          </td>
                          <td className="ga-tv-td-path">{a.file || '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </>
            )}

            {/* VSAM + data files from asset graph */}
            {agVsamAssets.length > 0 && (
              <>
                <div className="ga-tv-section-subhdr">VSAM / Data Files — {agVsamAssets.length}</div>
                <table className="ga-tv-table">
                  <thead>
                    <tr>
                      <th>Dataset</th>
                      <th>Type</th>
                      <th>Reads</th>
                      <th>Writes</th>
                      <th>Source File</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agVsamAssets.map(a => {
                      const access = dataAccessMap[a.id] || {}
                      const readers = access.reads  || []
                      const writers = access.writes || []
                      return (
                        <tr key={a.id}>
                          <td className="ga-tv-td-name">{a.name}</td>
                          <td className="ga-tv-td-tag">
                            <span className={`ga-tv-type-tag ga-tv-type-tag--${(a.type || 'unknown').toLowerCase().replace(/_/g, '-')}`}>
                              {a.type}
                            </span>
                          </td>
                          <td className="ga-tv-td-list">
                            {readers.length > 0
                              ? readers.slice(0, 3).join(', ') + (readers.length > 3 ? ` +${readers.length - 3}` : '')
                              : <span className="ga-tv-muted">—</span>
                            }
                          </td>
                          <td className="ga-tv-td-list">
                            {writers.length > 0
                              ? writers.slice(0, 3).join(', ') + (writers.length > 3 ? ` +${writers.length - 3}` : '')
                              : <span className="ga-tv-muted">—</span>
                            }
                          </td>
                          <td className="ga-tv-td-path">{a.file || '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </>
            )}

            {/* COBOL Unified Data Model from parseData */}
            {udmItems.length > 0 && (
              <>
                <div className="ga-tv-section-subhdr">
                  COBOL Unified Data Model — {topLevelItems.length} top-level records (Level 01)
                </div>
                <div className="ga-tv-stat-row">
                  {[
                    { label: 'Total Items',    value: (udmStats.total_items    || 0).toLocaleString() },
                    { label: 'Groups',         value: (udmStats.groups         || 0).toLocaleString() },
                    { label: 'OCCURS',         value: (udmStats.with_occurs    || 0).toLocaleString() },
                    { label: 'REDEFINES',      value: (udmStats.with_redefines || 0).toLocaleString() },
                    { label: 'From Programs',  value: (udmStats.from_programs  || 0).toLocaleString() },
                    { label: 'From Copybooks', value: (udmStats.from_copybooks || 0).toLocaleString() },
                  ].map(s => (
                    <div key={s.label} className="ga-dash-stat">
                      <span className="ga-dash-stat-value">{s.value}</span>
                      <span className="ga-dash-stat-label">{s.label}</span>
                    </div>
                  ))}
                </div>
                <table className="ga-tv-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Source File</th>
                      <th>Type</th>
                      <th className="ga-tv-th-num">Group</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topLevelItems.slice(0, 60).map((item, i) => (
                      <tr key={`${item.name}-${i}`}>
                        <td className="ga-tv-td-name">{item.name || '—'}</td>
                        <td className="ga-tv-td-path">
                          {(item.source_file || '').split('/').pop() || '—'}
                        </td>
                        <td className="ga-tv-td-tag">
                          <span className={`ga-tv-type-tag ga-tv-type-tag--${(item.source_type || 'unknown').toLowerCase()}`}>
                            {item.source_type || '—'}
                          </span>
                        </td>
                        <td className="ga-tv-td-bool">
                          {item.is_group ? '✓' : <span className="ga-tv-muted">—</span>}
                        </td>
                      </tr>
                    ))}
                    {topLevelItems.length > 60 && (
                      <tr>
                        <td colSpan={4} className="ga-tv-more-row">
                          +{topLevelItems.length - 60} more level-01 records
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </>
            )}

            {agDb2Assets.length === 0 && agVsamAssets.length === 0 && udmItems.length === 0 && (
              <TechPending
                title="No data assets found"
                hint="Run Code Analysis to populate DB2 tables, VSAM datasets, and the COBOL unified data model."
              />
            )}
          </div>
        )}

        {/* FIELD CROSS-REFERENCE — SOURCE: graph query (Tier 1) */}
        {tvTab === 'Field X-Ref' && (
          <div className="ga-tv-section">
            <div className="ga-tv-section-hdr">
              <Search size={14} className="ga-tv-section-icon" />
              <span className="ga-tv-section-title">Field Cross-Reference</span>
              <span className="ga-dash-source-tag">graph · Tier 1</span>
            </div>

            {fieldXref?.records?.length > 0 ? (() => {
              const filtered = xrefFilter
                ? fieldXref.records.filter(r =>
                    (r.field || '').toLowerCase().includes(xrefFilter.toLowerCase()) ||
                    (r.copybook || '').toLowerCase().includes(xrefFilter.toLowerCase()) ||
                    (r.pic || '').toLowerCase().includes(xrefFilter.toLowerCase()) ||
                    (r.java_type || '').toLowerCase().includes(xrefFilter.toLowerCase()))
                : fieldXref.records
              return (
                <>
                  <p style={{ color: 'var(--text-secondary, #8b92a5)', fontSize: '12px', margin: '0 0 8px' }}>
                    {fieldXref.record_count} data fields across all copybooks — search by field name, copybook, PIC type, or Java type.
                  </p>
                  <div style={{ margin: '0 0 10px' }}>
                    <input
                      type="text"
                      placeholder="Filter by field, copybook, PIC, or Java type…"
                      value={xrefFilter}
                      onChange={e => setXrefFilter(e.target.value)}
                      style={{
                        width: '340px', padding: '6px 10px', fontSize: '12px',
                        background: 'var(--bg-secondary, #1e1e2e)', border: '1px solid var(--border-color, #333)',
                        borderRadius: '4px', color: 'var(--text-primary, #e0e0e0)',
                      }}
                    />
                    {xrefFilter && (
                      <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--text-tertiary, #666)' }}>
                        {filtered.length} of {fieldXref.records.length} fields
                      </span>
                    )}
                  </div>
                  <table className="ga-tv-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>PIC</th>
                        <th>Java Type</th>
                        <th>Copybook</th>
                        <th className="ga-tv-th-num">Level</th>
                        <th className="ga-tv-th-num">Uses</th>
                        <th>Used In Paragraphs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.slice(0, 50).map((r, i) => (
                        <tr key={i}>
                          <td className="ga-tv-td-name">{r.field}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: '11px' }}>{r.pic || '—'}</td>
                          <td style={{ fontSize: '11px' }}>{r.java_type || '—'}</td>
                          <td>{r.copybook || '—'}</td>
                          <td className="ga-tv-td-num">{r.level ?? '—'}</td>
                          <td className="ga-tv-td-num">
                            <span style={{ fontWeight: r.usage_count > 0 ? 600 : 400 }}>{r.usage_count}</span>
                          </td>
                          <td className="ga-tv-td-list" style={{ fontSize: '11px' }}>
                            {r.used_in_paragraphs?.length > 0
                              ? r.used_in_paragraphs.slice(0, 4).join(', ') +
                                (r.used_in_paragraphs.length > 4 ? ` +${r.used_in_paragraphs.length - 4} more` : '')
                              : <span className="ga-tv-muted">—</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filtered.length > 50 && (
                    <p style={{ color: 'var(--text-tertiary, #666)', fontSize: '11px', margin: '6px 0 0' }}>
                      Showing 50 of {filtered.length} fields{xrefFilter ? ' (filtered)' : ''} — use the filter to narrow results.
                    </p>
                  )}
                </>
              )
            })() : (
              <TechPending
                title="No field cross-reference data"
                hint="Run Code Analysis to populate the field cross-reference from graph data."
              />
            )}
          </div>
        )}

        {/* JCL / EXECUTION — SOURCE: asset graph JCL jobs + relationships + file_usage */}
        {tvTab === 'JCL / Execution' && (() => {
          // ── Derive all available JCL signals ─────────────────────────────────
          const execRelsAll = agRelationships.filter(r => r.type === 'EXECUTES')
          const ddRelsAll   = agRelationships.filter(r => r.type === 'DD_REFERENCES')

          // Normalised fileRows — file_usage values may be arrays or strings
          const safeFileRows = Object.entries(fileUsage).map(([name, progs]) => {
            const progList = Array.isArray(progs) ? progs : (progs ? [String(progs)] : [])
            return { name, usedBy: progList.length, programs: progList }
          }).sort((a, b) => b.usedBy - a.usedBy)

          const hasJclJobs  = agJclAssets.length > 0
          const hasExecRels = execRelsAll.length > 0
          const hasDdRels   = ddRelsAll.length > 0
          const hasDatasets = agDatasetAssets.length > 0
          const hasFileRows = safeFileRows.length > 0

          console.log('[JCL Tab] Render check:', {
            agJclAssets:  agJclAssets.length,
            execRels:     execRelsAll.length,
            ddRels:       ddRelsAll.length,
            agDatasets:   agDatasetAssets.length,
            safeFileRows: safeFileRows.length,
            fileUsageKeys: Object.keys(fileUsage).length,
            hasJclJobs, hasExecRels, hasDdRels, hasDatasets, hasFileRows,
            assetGraph: !!assetGraph,
            parseData:  !!parseData,
          })

          // ── Branch: full JCL job view (asset graph has JCL_JOB assets) ────────
          if (hasJclJobs) {
            return (
              <div className="ga-tv-section">
                <div className="ga-tv-section-hdr">
                  <HardDrive size={14} className="ga-tv-section-icon" />
                  <span className="ga-tv-section-title">JCL Jobs &amp; Execution</span>
                  <span className="ga-dash-source-tag">asset graph · JCL_JOB</span>
                </div>
                {/* Job summary */}
                <table className="ga-tv-table">
                  <thead>
                    <tr>
                      <th>JCL Job</th>
                      <th className="ga-tv-th-num">Steps</th>
                      <th className="ga-tv-th-num">DD Refs</th>
                      <th>Executes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agJclAssets.map(a => {
                      const eRels     = agRelationships.filter(r => r.from === a.id && r.type === 'EXECUTES')
                      const dRels     = agRelationships.filter(r => r.from === a.id && r.type === 'DD_REFERENCES')
                      const execNames = eRels.map(r => r.metadata?.program || r.to.split(':').pop())
                      const stepCount = a.metadata?.steps?.length || eRels.length
                      return (
                        <tr key={a.id}>
                          <td className="ga-tv-td-name">{a.name}</td>
                          <td className="ga-tv-td-num">{stepCount}</td>
                          <td className="ga-tv-td-num">{dRels.length}</td>
                          <td className="ga-tv-td-list">
                            {execNames.length > 0
                              ? execNames.slice(0, 4).join(', ') + (execNames.length > 4 ? ` +${execNames.length - 4} more` : '')
                              : <span className="ga-tv-muted">—</span>
                            }
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>

                {/* Step-level DSN detail */}
                {agJclAssets.some(a => (a.metadata?.steps || []).length > 0) && (
                  <>
                    <div className="ga-tv-section-subhdr" style={{ marginTop: '16px' }}>Step Detail</div>
                    {agJclAssets.map(job => {
                      const steps = job.metadata?.steps || []
                      if (steps.length === 0) return null
                      return (
                        <div key={job.id} className="ga-tv-jcl-job-block">
                          <div className="ga-tv-jcl-job-name">{job.name}</div>
                          <table className="ga-tv-table ga-tv-table--nested">
                            <thead>
                              <tr>
                                <th>Step</th><th>Program</th><th>DD Name</th><th>DSN</th>
                              </tr>
                            </thead>
                            <tbody>
                              {steps.map((step, si) => {
                                const dds = step.dds || []
                                if (dds.length === 0) {
                                  return (
                                    <tr key={`${si}-empty`}>
                                      <td className="ga-tv-td-name">{step.name || `STEP${si + 1}`}</td>
                                      <td className="ga-tv-td-tag">{step.exec_program || '—'}</td>
                                      <td colSpan={2} className="ga-tv-muted">no DD statements</td>
                                    </tr>
                                  )
                                }
                                return dds.map((dd, di) => (
                                  <tr key={`${si}-${di}`}>
                                    {di === 0 && (
                                      <>
                                        <td className="ga-tv-td-name" rowSpan={dds.length}>{step.name || `STEP${si + 1}`}</td>
                                        <td className="ga-tv-td-tag" rowSpan={dds.length}>{step.exec_program || '—'}</td>
                                      </>
                                    )}
                                    <td className="ga-tv-td-name">{dd.name || '—'}</td>
                                    <td className="ga-tv-td-path">
                                      {dd.dsn ? <span className="ga-tv-dsn">{dd.dsn}</span> : <span className="ga-tv-muted">—</span>}
                                    </td>
                                  </tr>
                                ))
                              })}
                            </tbody>
                          </table>
                        </div>
                      )
                    })}
                  </>
                )}

                {hasDatasets && (
                  <>
                    <div className="ga-tv-section-subhdr" style={{ marginTop: '16px' }}>Dataset References — {agDatasetAssets.length}</div>
                    <table className="ga-tv-table">
                      <thead>
                        <tr><th>Dataset</th><th>Type</th><th>Scope</th></tr>
                      </thead>
                      <tbody>
                        {agDatasetAssets.map(a => (
                          <tr key={a.id}>
                            <td className="ga-tv-td-name">{a.name}</td>
                            <td className="ga-tv-td-tag">
                              <span className={`ga-tv-type-tag ga-tv-type-tag--${(a.type || 'dataset').toLowerCase()}`}>{a.type}</span>
                            </td>
                            <td className="ga-tv-td-bool">
                              {a.metadata?.is_external
                                ? <span className="ga-tv-warn">external</span>
                                : <span className="ga-tv-muted">internal</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            )
          }

          // ── Branch: relationship-derived view (no JCL_JOB node, but edges exist) ─
          if (hasDdRels || hasExecRels) {
            // Group DD_REFERENCES by step_name
            const byStep = {}
            for (const r of ddRelsAll) {
              const step = r.metadata?.step_name || 'STEP'
              if (!byStep[step]) byStep[step] = { exec: null, dds: [] }
              const dsn  = r.metadata?.dsn || r.to.split(':').pop().replace(/_/g, '.')
              const dd   = r.metadata?.dd_name || '—'
              byStep[step].dds.push({ dd, dsn, to: r.to })
            }
            for (const r of execRelsAll) {
              const step = r.metadata?.step_name || 'STEP'
              if (!byStep[step]) byStep[step] = { exec: null, dds: [] }
              byStep[step].exec = r.metadata?.program || r.to.split(':').pop()
            }
            return (
              <div className="ga-tv-section">
                <div className="ga-tv-section-hdr">
                  <HardDrive size={14} className="ga-tv-section-icon" />
                  <span className="ga-tv-section-title">JCL Execution Relationships</span>
                  <span className="ga-dash-source-tag">asset graph · EXECUTES/DD_REFERENCES</span>
                </div>
                <table className="ga-tv-table">
                  <thead>
                    <tr><th>Step</th><th>Program</th><th>DD Name</th><th>DSN / Dataset</th></tr>
                  </thead>
                  <tbody>
                    {Object.entries(byStep).map(([step, info]) =>
                      info.dds.length > 0
                        ? info.dds.map((dd, di) => (
                          <tr key={`${step}-${di}`}>
                            {di === 0 && (
                              <>
                                <td className="ga-tv-td-name" rowSpan={info.dds.length}>{step}</td>
                                <td className="ga-tv-td-tag" rowSpan={info.dds.length}>{info.exec || '—'}</td>
                              </>
                            )}
                            <td className="ga-tv-td-name">{dd.dd}</td>
                            <td className="ga-tv-td-path"><span className="ga-tv-dsn">{dd.dsn}</span></td>
                          </tr>
                        ))
                        : [(
                          <tr key={step}>
                            <td className="ga-tv-td-name">{step}</td>
                            <td className="ga-tv-td-tag">{info.exec || '—'}</td>
                            <td colSpan={2} className="ga-tv-muted">no DD statements recorded</td>
                          </tr>
                        )]
                    )}
                  </tbody>
                </table>
              </div>
            )
          }

          // ── Branch: file_usage from comprehensive_parse_results ───────────────
          if (hasFileRows) {
            return (
              <div className="ga-tv-section">
                <div className="ga-tv-section-hdr">
                  <HardDrive size={14} className="ga-tv-section-icon" />
                  <span className="ga-tv-section-title">File Usage</span>
                  <span className="ga-dash-source-tag">cross_reference.file_usage</span>
                </div>
                <table className="ga-tv-table">
                  <thead>
                    <tr>
                      <th>File / Dataset</th>
                      <th className="ga-tv-th-num">Programs</th>
                      <th>Used By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {safeFileRows.map(row => (
                      <tr key={row.name}>
                        <td className="ga-tv-td-name">{row.name}</td>
                        <td className="ga-tv-td-num">{row.usedBy || <span className="ga-tv-muted">—</span>}</td>
                        <td className="ga-tv-td-list">
                          {row.programs.length > 0
                            ? row.programs.slice(0, 4).join(', ') +
                              (row.programs.length > 4 ? ` +${row.programs.length - 4} more` : '')
                            : <span className="ga-tv-muted">—</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }

          // ── Branch: datasets from asset graph (partial data) ──────────────────
          if (hasDatasets) {
            return (
              <div className="ga-tv-section">
                <div className="ga-tv-section-hdr">
                  <HardDrive size={14} className="ga-tv-section-icon" />
                  <span className="ga-tv-section-title">Dataset References</span>
                  <span className="ga-dash-source-tag">asset graph · DATASET</span>
                </div>
                <table className="ga-tv-table">
                  <thead>
                    <tr><th>Dataset</th><th>Type</th><th>Scope</th></tr>
                  </thead>
                  <tbody>
                    {agDatasetAssets.map(a => (
                      <tr key={a.id}>
                        <td className="ga-tv-td-name">{a.name}</td>
                        <td className="ga-tv-td-tag">
                          <span className={`ga-tv-type-tag ga-tv-type-tag--${(a.type || 'dataset').toLowerCase()}`}>{a.type}</span>
                        </td>
                        <td className="ga-tv-td-bool">
                          {a.metadata?.is_external
                            ? <span className="ga-tv-warn">external</span>
                            : <span className="ga-tv-muted">internal</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }

          // ── Branch: nothing — honest empty state ──────────────────────────────
          console.log('[JCL Tab] All sources empty — showing TechPending')
          return (
            <div className="ga-tv-section">
              <div className="ga-tv-section-hdr">
                <HardDrive size={14} className="ga-tv-section-icon" />
                <span className="ga-tv-section-title">JCL Jobs &amp; Execution</span>
                <span className="ga-dash-source-tag">asset graph</span>
              </div>
              <TechPending
                title="No JCL or file usage data found"
                hint="JCL execution data requires JCL source files to be included in the upload and analyzed. Re-run Code Analysis with JCL files present."
              />
            </div>
          )
        })()}

        {/* STATISTICS — SOURCE: summary.* */}
        {tvTab === 'Compiler' && (() => {
          if (!gnucobolEnrichment) {
            return (
              <div className="ga-tv-section" style={{ padding: '40px 20px', textAlign: 'center' }}>
                <AlertTriangle size={36} style={{ color: 'var(--text-tertiary, #666)', marginBottom: '12px' }} />
                <h3 style={{ margin: '0 0 8px', color: 'var(--text-secondary, #8b92a5)' }}>Compiler Analysis Not Available</h3>
                <p style={{ margin: 0, color: 'var(--text-tertiary, #666)', fontSize: '13px' }}>
                  Compiler analysis requires a COBOL compiler to be installed on the server.<br />
                  This step runs automatically during Code Analysis when a compiler is available.
                </p>
              </div>
            )
          }
          const gc = gnucobolEnrichment
          const compilerSubTabs = ['Compile Status', 'Diagnostics', 'Dead Code']
          return (
            <div className="ga-tv-section">
              <div className="ga-tv-section-hdr">
                <Code2 size={14} className="ga-tv-section-icon" />
                <span className="ga-tv-section-title">Compiler Analysis</span>
                <span className="ga-dash-source-tag">cobc {gc.cobc_version}</span>
              </div>

              {/* Summary stats */}
              <div className="ga-tv-stat-row">
                {[
                  { label: 'Files Analyzed',  value: gc.files_analyzed },
                  { label: 'Passed',          value: gc.files_succeeded },
                  { label: 'Failed',          value: gc.files_failed },
                  { label: 'Diagnostics',     value: gc.compilation_diagnostics?.length || 0 },
                  { label: 'Dead Code Items', value: gc.compiler_dead_code?.length || 0 },
                  { label: 'Data Moves',      value: gc.data_flow_moves?.length || 0 },
                ].map(s => (
                  <div key={s.label} className="ga-tv-stat">
                    <span className="ga-tv-stat-label">{s.label}</span>
                    <span className="ga-tv-stat-value">{s.value}</span>
                  </div>
                ))}
              </div>

              {/* Compiler sub-tab bar */}
              <div style={{ display: 'flex', gap: '2px', marginTop: '14px', borderBottom: '1px solid var(--border-primary, #333)', paddingBottom: '0' }}>
                {compilerSubTabs.map(st => (
                  <button
                    key={st}
                    onClick={() => setCompilerSubTab(st)}
                    style={{
                      padding: '6px 14px',
                      fontSize: '11px',
                      fontWeight: compilerSubTab === st ? 600 : 400,
                      color: compilerSubTab === st ? 'var(--text-primary, #e0e0e0)' : 'var(--text-tertiary, #666)',
                      background: compilerSubTab === st ? 'var(--bg-tertiary, #2a2a3a)' : 'transparent',
                      border: 'none',
                      borderBottom: compilerSubTab === st ? '2px solid var(--accent-primary, #4a90d9)' : '2px solid transparent',
                      borderRadius: '4px 4px 0 0',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >{st}</button>
                ))}
              </div>

              {/* ── Sub-tab: Compile Status ── */}
              {compilerSubTab === 'Compile Status' && (
                <div style={{ marginTop: '12px' }}>
                  {gc.file_results?.length > 0 ? (
                    <table className="ga-tv-table">
                      <thead><tr>
                        <th>File</th><th>Status</th><th>Errors</th><th>Warnings</th>
                        <th>Symbols</th><th>Data Moves</th><th>Calls</th>
                      </tr></thead>
                      <tbody>
                        {gc.file_results.map((f, i) => (
                          <tr key={i} style={f.status === 'fail' ? { background: 'rgba(255,80,80,0.08)' } : {}}>
                            <td className="ga-tv-td-name">{f.filename}</td>
                            <td>
                              <span style={{
                                fontSize: '10px', padding: '2px 6px', borderRadius: '3px', fontWeight: 600,
                                background: f.status === 'pass' ? 'rgba(34,197,94,0.15)' : f.status === 'warnings' ? 'rgba(250,204,21,0.15)' : 'rgba(239,68,68,0.15)',
                                color: f.status === 'pass' ? '#22c55e' : f.status === 'warnings' ? '#facc15' : '#ef4444',
                              }}>{f.status?.toUpperCase()}</span>
                            </td>
                            <td className="ga-tv-td-num">{f.error_count}</td>
                            <td className="ga-tv-td-num">{f.warning_count}</td>
                            <td className="ga-tv-td-num">{f.symbol_count || 0}</td>
                            <td className="ga-tv-td-num">{f.data_move_count || 0}</td>
                            <td className="ga-tv-td-num">{f.call_interface_count || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ color: 'var(--text-tertiary, #666)', fontSize: '12px', margin: '20px 0' }}>No file results available.</p>
                  )}

                  {/* Data Flow Moves — shown here alongside compile status */}
                  {gc.data_flow_moves?.length > 0 && (
                    <div style={{ marginTop: '20px' }}>
                      <h4 style={{ margin: '0 0 8px', fontSize: '13px', color: 'var(--text-secondary, #8b92a5)' }}>Data Flow (Field Movements)</h4>
                      <table className="ga-tv-table">
                        <thead><tr>
                          <th>Source Field</th><th style={{ width: '30px', textAlign: 'center' }}></th><th>Target Field</th><th>Program</th>
                        </tr></thead>
                        <tbody>
                          {gc.data_flow_moves.map((m, i) => (
                            <tr key={i}>
                              <td className="ga-tv-td-name">{m.source_cobol}</td>
                              <td style={{ textAlign: 'center', color: 'var(--text-tertiary, #666)' }}>&rarr;</td>
                              <td className="ga-tv-td-name">{m.target_cobol}</td>
                              <td>{m.program}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Call Interfaces — shown here alongside compile status */}
                  {gc.call_interfaces?.length > 0 && (
                    <div style={{ marginTop: '20px' }}>
                      <h4 style={{ margin: '0 0 8px', fontSize: '13px', color: 'var(--text-secondary, #8b92a5)' }}>Call Interfaces</h4>
                      <table className="ga-tv-table">
                        <thead><tr>
                          <th>Target Program</th><th>Parameters</th><th>Count</th><th>Caller</th>
                        </tr></thead>
                        <tbody>
                          {gc.call_interfaces.map((c, i) => (
                            <tr key={i}>
                              <td className="ga-tv-td-name">{c.target_program}</td>
                              <td style={{ fontSize: '11px' }}>{(c.param_cobol || []).join(', ')}</td>
                              <td className="ga-tv-td-num">{c.param_count}</td>
                              <td>{c.program}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ── Sub-tab: Diagnostics ── */}
              {compilerSubTab === 'Diagnostics' && (
                <div style={{ marginTop: '12px' }}>
                  {gc.compilation_diagnostics?.length > 0 ? (
                    <table className="ga-tv-table">
                      <thead><tr>
                        <th>Program</th><th>Line</th><th>Level</th><th>Message</th>
                      </tr></thead>
                      <tbody>
                        {gc.compilation_diagnostics.map((d, i) => (
                          <tr key={i} style={d.level === 'error' ? { background: 'rgba(255,80,80,0.08)' } : {}}>
                            <td className="ga-tv-td-name">{d.program || d.filename}</td>
                            <td className="ga-tv-td-num">{d.line}</td>
                            <td>
                              <span style={{
                                fontSize: '10px', padding: '2px 5px', borderRadius: '3px',
                                background: d.level === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(250,204,21,0.15)',
                                color: d.level === 'error' ? '#ef4444' : '#facc15',
                              }}>{d.level}</span>
                            </td>
                            <td style={{ fontSize: '12px' }}>{d.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ color: 'var(--text-tertiary, #666)', fontSize: '12px', margin: '20px 0' }}>No compilation diagnostics — all files compiled cleanly.</p>
                  )}
                </div>
              )}

              {/* ── Sub-tab: Dead Code ── */}
              {compilerSubTab === 'Dead Code' && (
                <div style={{ marginTop: '12px' }}>
                  {gc.compiler_dead_code?.length > 0 ? (
                    <table className="ga-tv-table">
                      <thead><tr>
                        <th>Identifier</th><th>Type</th><th>Section</th><th>Defined Line</th><th>Program</th>
                      </tr></thead>
                      <tbody>
                        {gc.compiler_dead_code.map((d, i) => (
                          <tr key={i}>
                            <td className="ga-tv-td-name">{d.identifier}</td>
                            <td>{d.is_paragraph ? 'Paragraph' : 'Data Field'}</td>
                            <td>{d.section}</td>
                            <td className="ga-tv-td-num">{d.definition_line}</td>
                            <td>{d.program}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ color: 'var(--text-tertiary, #666)', fontSize: '12px', margin: '20px 0' }}>No dead code detected by the compiler.</p>
                  )}
                </div>
              )}
            </div>
          )
        })()}

        {tvTab === 'Statistics' && (
          <div className="ga-tv-section">
            <div className="ga-tv-section-hdr">
              <BarChart2 size={14} className="ga-tv-section-icon" />
              <span className="ga-tv-section-title">Code Analysis Statistics</span>
              <span className="ga-dash-source-tag">comprehensive_parse_results</span>
            </div>

            {/* ── Parse statistics ────────────────────────────────────────────────
              Sources:
                program_count, copybook_count, total_files, total_data_items,
                copy_statements_* → comprehensive_parse_results.summary ✅
                source_lines  → tree-sitter runner (NOT in comprehensive_parse summary)
                                 shown as '—' when unavailable
                files_analyzed → use total_files when files_analyzed absent
                paragraphs     → computed from programs[].paragraphs when sum.paragraphs absent
            */}
            <div className="ga-tv-stat-row">
              {(() => {
                // Field-by-field honest derivation with source logging
                const filesAnalyzed = sum.files_analyzed || sum.total_files || 0
                const dataItems     = sum.total_data_items || sum.data_items || 0
                const paragraphs    = sum.paragraphs || derivedParagraphs || 0
                // source_lines is ONLY in the runner output_data, not in comprehensive_parse_results.
                // Show '—' if programs exist but source_lines is missing — do not show fake 0.
                const sourceLines   = sum.source_lines > 0
                  ? sum.source_lines.toLocaleString()
                  : (sum.program_count > 0 ? '—' : '0')

                console.log('[Statistics] Source mapping:', {
                  program_count:    sum.program_count,
                  copybook_count:   sum.copybook_count,
                  total_files:      sum.total_files,
                  files_analyzed_raw: sum.files_analyzed,
                  files_analyzed_used: filesAnalyzed,
                  source_lines_raw: sum.source_lines,
                  source_lines_display: sourceLines,
                  total_data_items: sum.total_data_items,
                  paragraphs_raw:   sum.paragraphs,
                  paragraphs_derived: derivedParagraphs,
                  paragraphs_used:  paragraphs,
                  copy_statements_total:    sum.copy_statements_total,
                  copy_statements_resolved: sum.copy_statements_resolved,
                  assetGraph_present: !!assetGraph,
                })

                const stats = [
                  { label: 'Programs',       value: sum.program_count  || 0 },
                  { label: 'Copybooks',      value: sum.copybook_count || 0 },
                  { label: 'Total Files',    value: sum.total_files    || 0 },
                  { label: 'Files Analyzed', value: filesAnalyzed },
                  { label: 'Source Lines',   value: sourceLines },
                  { label: 'Data Items',     value: dataItems.toLocaleString() },
                  { label: 'Paragraphs',     value: paragraphs.toLocaleString() },
                  ...(sum.java_lines > 0
                    ? [{ label: 'Java LOC (gen)', value: sum.java_lines.toLocaleString() }]
                    : []),
                ]

                return stats.map(s => (
                  <div key={s.label} className="ga-dash-stat">
                    <span className={`ga-dash-stat-value${s.value === '—' ? ' ga-stat-unavail' : ''}`}>
                      {s.value}
                    </span>
                    <span className="ga-dash-stat-label">{s.label}</span>
                  </div>
                ))
              })()}
            </div>

            {/* ── Asset graph summary (when available) ── */}
            {assetGraph && (() => {
              const agSum = assetGraph.summary || {}
              return (
                <div className="ga-tv-stats-section">
                  <div className="ga-tv-section-subhdr">Asset Graph Summary</div>
                  <div className="ga-tv-stat-row">
                    {[
                      { label: 'Total Assets',   value: agSum.total_assets        || 0 },
                      { label: 'Relationships',  value: agSum.total_relationships  || 0 },
                      { label: 'Issues',         value: agSum.total_issues         || 0 },
                      { label: 'DB2 Tables',     value: agSum.db2_table_count      || 0 },
                      { label: 'VSAM Datasets',  value: agSum.vsam_dataset_count   || 0 },
                      { label: 'JCL Jobs',       value: agSum.jcl_job_count        || 0 },
                    ].map(s => (
                      <div key={s.label} className="ga-dash-stat">
                        <span className="ga-dash-stat-value">{s.value}</span>
                        <span className="ga-dash-stat-label">{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}

            {totalCopy > 0 && (
              <div className="ga-tv-stats-section">
                <div className="ga-tv-section-subhdr">COPY Statement Resolution</div>
                <div className="ga-tv-resolve-bar">
                  <span className="ga-tv-resolve-label">
                    {resolvedCopy} resolved / {totalCopy} total
                    {unresolvedCopy > 0 && (
                      <span className="ga-tv-unresolved"> ({unresolvedCopy} unresolved)</span>
                    )}
                  </span>
                  <div className="ga-comp-bar-track ga-tv-resolve-track">
                    <div
                      className="ga-comp-bar-fill"
                      style={{
                        width: `${Math.round(resolvedCopy / totalCopy * 100)}%`,
                        backgroundColor: unresolvedCopy > 0 ? '#f59e0b' : '#22c55e',
                      }}
                    />
                  </div>
                  <span className="ga-tv-resolve-pct">
                    {Math.round(resolvedCopy / totalCopy * 100)}%
                  </span>
                </div>
              </div>
            )}

            {sum.zero_loss !== undefined && (
              <div className="ga-tv-stats-section">
                <div className="ga-tv-section-subhdr">Parse Quality</div>
                <div className={`ga-tv-quality-badge${sum.zero_loss ? ' ga-tv-quality-badge--ok' : ' ga-tv-quality-badge--warn'}`}>
                  {sum.zero_loss
                    ? '✓ Zero-loss line accounting verified'
                    : '⚠ Zero-loss accounting incomplete — some lines may be unaccounted'
                  }
                </div>
              </div>
            )}

            {/* ── Per-Program Intelligence Metrics table (Phase 2) ──────── */}
            {intelligenceMetrics?.programs && Object.keys(intelligenceMetrics.programs).length > 0 ? (
              <div className="ga-tv-stats-section ga-tv-intel-section">
                <div className="ga-tv-section-subhdr ga-tv-intel-subhdr">
                  <span>Per-Program Metrics</span>
                  <span className="ga-tv-intel-phase-tag">
                    {intelligenceMetrics.cc_available && intelligenceMetrics.lcom4_available
                      ? 'Phase 4 · LCOM4 + CC + Graph + Fan-Out + Instability'
                      : intelligenceMetrics.cc_available && intelligenceMetrics.project_summary?.graph_available
                      ? 'Phase 3 · CC + Graph + Fan-Out + Instability'
                      : intelligenceMetrics.cc_available
                      ? 'Phase 2 · CC + Fan-Out + Instability'
                      : 'Phase 1 · Fan-Out + Instability'}
                  </span>
                </div>
                <div className="ga-tv-intel-table-wrap">
                  <table className="ga-tv-intel-table">
                    <thead>
                      <tr>
                        <th className="ga-tv-intel-th ga-tv-intel-th--name"   title="Program stem name">Program</th>
                        <th className="ga-tv-intel-th ga-tv-intel-th--lines"  title="Total source lines in this program">Lines</th>
                        <th className="ga-tv-intel-th ga-tv-intel-th--cc"     title="Cyclomatic Complexity — number of independent paths (1 + decision points). Higher = harder to test and modernize.">CC</th>
                        <th className="ga-tv-intel-th ga-tv-intel-th--fanout" title="Fan-Out — programs called + copybooks used + files accessed. Higher = more outbound dependencies.">Fan-Out</th>
                        <th className="ga-tv-intel-th ga-tv-intel-th--fanin"  title="Fan-In — programs that call this one. Higher = more callers depend on it.">Fan-In</th>
                        <th className="ga-tv-intel-th ga-tv-intel-th--instab" title="Instability — Fan-Out / (Fan-In + Fan-Out). 0.0 = fully stable, 1.0 = fully unstable.">Instab.</th>
                        <th className="ga-tv-intel-th ga-tv-intel-th--risk"   title="Composite Risk Score (0–100). Relative to this project — the most complex program scores highest.">Risk</th>
                        {intelligenceMetrics.lcom4_available && (
                          <th className="ga-tv-intel-th ga-tv-intel-th--lcom4" title="LCOM4 — Lack of Cohesion in Methods (variant 4). Number of independent responsibility groups in this program's paragraph graph. LCOM4=1 is cohesive; LCOM4>1 indicates a split candidate for Define Services.">LCOM4</th>
                        )}
                        {intelligenceMetrics.project_summary?.graph_available && (<>
                          <th className="ga-tv-intel-th ga-tv-intel-th--graph" title="Betweenness Centrality — how often this program sits on the shortest call path between other programs. 0 = not a bridge, higher = structural bottleneck.">BC</th>
                          <th className="ga-tv-intel-th ga-tv-intel-th--graph" title="PageRank — relative inbound importance in the call graph. Higher = more programs (directly or transitively) call this one.">PR</th>
                        </>)}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(intelligenceMetrics.programs)
                        .sort((a, b) => b[1].risk_score - a[1].risk_score)
                        .map(([stem, m]) => (
                          <tr key={stem} className="ga-tv-intel-row">
                            <td className="ga-tv-intel-td ga-tv-intel-td--name">{stem}</td>
                            <td className="ga-tv-intel-td ga-tv-intel-td--num">
                              {m.line_count != null ? m.line_count.toLocaleString() : '—'}
                            </td>
                            <td className="ga-tv-intel-td ga-tv-intel-td--num">
                              {m.cyclomatic_complexity != null
                                ? <span className={`ga-tv-intel-cc${m.cyclomatic_complexity >= 20 ? ' ga-tv-intel-cc--high' : m.cyclomatic_complexity >= 10 ? ' ga-tv-intel-cc--med' : ''}`}>
                                    {m.cyclomatic_complexity}
                                  </span>
                                : <span className="ga-tv-intel-na">—</span>}
                            </td>
                            <td className="ga-tv-intel-td ga-tv-intel-td--num">{m.fan_out}</td>
                            <td className="ga-tv-intel-td ga-tv-intel-td--num">{m.fan_in}</td>
                            <td className="ga-tv-intel-td ga-tv-intel-td--num">{m.instability.toFixed(2)}</td>
                            <td className="ga-tv-intel-td ga-tv-intel-td--risk">
                              <div className="ga-tv-intel-risk-cell">
                                <span className={`ga-risk-badge ga-risk-badge--${m.risk_tier}`}>{m.risk_score}</span>
                                <div className="ga-risk-bar-track ga-tv-intel-bar-track">
                                  <div className={`ga-risk-bar-fill ga-risk-bar-fill--${m.risk_tier}`}
                                       style={{ width: `${m.risk_score}%` }} />
                                </div>
                              </div>
                            </td>
                            {intelligenceMetrics.lcom4_available && (
                              <td className="ga-tv-intel-td ga-tv-intel-td--num">
                                {m.lcom4 != null
                                  ? <span className={`ga-tv-intel-lcom4${m.lcom4 > 1 ? ' ga-tv-intel-lcom4--split' : ''}`}>
                                      {m.lcom4}
                                    </span>
                                  : <span className="ga-tv-intel-na">—</span>}
                              </td>
                            )}
                            {intelligenceMetrics.project_summary?.graph_available && (<>
                              <td className="ga-tv-intel-td ga-tv-intel-td--num">
                                {m.betweenness_centrality != null
                                  ? <span className={`ga-tv-intel-graph-val${m.betweenness_centrality > 0 ? ' ga-tv-intel-graph-val--active' : ''}`}>
                                      {m.betweenness_centrality.toFixed(4)}
                                    </span>
                                  : <span className="ga-tv-intel-na">—</span>}
                              </td>
                              <td className="ga-tv-intel-td ga-tv-intel-td--num">
                                {m.pagerank != null
                                  ? <span className="ga-tv-intel-graph-val">
                                      {m.pagerank.toFixed(4)}
                                    </span>
                                  : <span className="ga-tv-intel-na">—</span>}
                              </td>
                            </>)}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="ga-tv-stats-section">
                <div className="ga-tv-section-subhdr">Per-Program Metrics</div>
                <div className="ga-tv-intel-unavail">
                  <span className="ga-tv-intel-unavail-icon">📊</span>
                  <span>Intelligence metrics not available — re-run Code Analysis to generate Phase 2 data.</span>
                </div>
              </div>
            )}

            {/* ── Paragraph Complexity Heatmap (Tier 1) ── */}
            {complexityHeatmap?.records?.length > 0 && (
              <div className="ga-tv-stats-section">
                <div className="ga-tv-section-subhdr">
                  Paragraph Complexity Heatmap — top 25 of {complexityHeatmap.record_count}
                </div>
                <table className="ga-tv-table">
                  <thead>
                    <tr>
                      <th>Paragraph</th>
                      <th>Program</th>
                      <th className="ga-tv-th-num">Stmts</th>
                      <th className="ga-tv-th-num">Score</th>
                      <th>Complexity</th>
                      <th>Verb Types</th>
                    </tr>
                  </thead>
                  <tbody>
                    {complexityHeatmap.records.slice(0, 25).map((r, i) => {
                      const maxScore = complexityHeatmap.records[0]?.complexity_score || 1
                      const pct = Math.round((r.complexity_score || 0) / maxScore * 100)
                      const color = pct >= 80 ? '#ef4444' : pct >= 50 ? '#f59e0b' : '#22c55e'
                      const verbs = []
                      if (r.has_call) verbs.push('CALL')
                      if (r.has_read) verbs.push('READ')
                      if (r.has_write) verbs.push('WRITE')
                      if (r.has_compute) verbs.push('COMPUTE')
                      if (r.has_evaluate) verbs.push('EVALUATE')
                      if (r.has_search) verbs.push('SEARCH')
                      if (r.has_string) verbs.push('STRING')
                      return (
                        <tr key={i}>
                          <td className="ga-tv-td-name">{r.paragraph}</td>
                          <td>{r.program}</td>
                          <td className="ga-tv-td-num">{r.statement_count}</td>
                          <td className="ga-tv-td-num">
                            <span style={{ color, fontWeight: 600 }}>{r.complexity_score}</span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <div style={{ width: '80px', height: '6px', background: 'var(--bg-tertiary, #2a2a3a)', borderRadius: '3px' }}>
                                <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '3px' }} />
                              </div>
                              <span style={{ fontSize: '10px', color: 'var(--text-tertiary, #666)' }}>{pct}%</span>
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                              {verbs.map(v => (
                                <span key={v} style={{
                                  fontSize: '9px', padding: '1px 4px', borderRadius: '3px',
                                  background: 'var(--bg-tertiary, #2a2a3a)', color: 'var(--text-secondary, #8b92a5)',
                                }}>{v}</span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  )
}

// ── Inventory AI Action Modal ─────────────────────────────────────────────────

/**
 * AI Actions available per Inventory row.
 * All 5 actions route through AIResponseModal using real aiService calls.
 *
 * ✅ custom-prompt     → mode='custom'    POST /ai/chat  purpose='custom'
 * ✅ explain           → mode='explain'   POST /ai/chat  purpose='code_explain'   (auto)
 * ✅ business-use-case → mode='explain'   POST /ai/chat  purpose='custom'         (auto)
 * ✅ document          → mode='explain'   POST /ai/chat  purpose='custom'         (auto)
 * ✅ java-quick-view   → mode='generate'  POST /ai/chat  purpose='generate_java'  (auto)
 *
 * File content is fetched from /editor/cobol/file at modal open.
 * No fake placeholders. No "coming soon" text.
 */
// Per-row single-file actions (used in the row ⋯ dropdown)
const AI_ACTIONS = [
  { id: 'custom-prompt',     label: 'Custom Prompt',           icon: Code2     },
  { id: 'explain',           label: 'Explain This Code',       icon: BookOpen  },
  { id: 'business-use-case', label: 'Business Use Case',       icon: BarChart2 },
  { id: 'document',          label: 'Document',                icon: FileCode  },
  { id: 'java-quick-view',   label: 'Generate Java Quick View',icon: Sparkles  },
]

// Bulk-only actions — shown in the Bulk Actions banner above the grid.
//
// TWO distinct COBOL-only bulk actions are provided:
//   java-quick-view → Generate Java     : real Java code output via generate_java purpose
//   service-preview → Generate Service  : service-definition specification text (not Java code)
//
// These are separate concerns and must not be merged.
const BULK_AI_ACTIONS = [
  { id: 'custom-prompt',     label: 'Custom Prompt',              icon: Code2     },
  { id: 'explain',           label: 'Explain This Code',          icon: BookOpen  },
  { id: 'business-use-case', label: 'Business Use Case',          icon: BarChart2 },
  { id: 'document',          label: 'Document',                   icon: FileCode  },
  { id: 'java-quick-view',   label: 'Generate Java',              icon: Sparkles  },
  { id: 'service-preview',   label: 'Generate Service Definition',icon: Layers    },
]

const ACTION_TITLE = {
  'custom-prompt':     'Custom Prompt',
  'explain':           'Explain This Code',
  'business-use-case': 'Business Use Case',
  'document':          'Document',
  'java-quick-view':   'Generate Java — Quick View',
  'service-preview':   'Generate Service Definition',
}

const ACTION_MODE = {
  'custom-prompt':     'custom',
  'explain':           'explain',
  'business-use-case': 'explain',
  'document':          'explain',
  'java-quick-view':   'explain',  // structured preview text, not a code block
  // service-preview produces a structured service definition document (not Java code)
  'service-preview':   'explain',
}

/**
 * BulkInventoryAIModal
 *
 * Multi-file variant of InventoryAIModal.
 * Fetches content for all selected files in parallel, concatenates them with
 * clear file separators, then fires the same AI actions as the single-file path.
 *
 * Props:
 *   action           — one of the AI_ACTIONS ids
 *   files            — array of { name, path, group } file objects
 *   catalogIngestJobId — ingest job ID for file fetch
 *   project          — { name, account }
 *   onClose          — dismiss callback
 */
export function BulkInventoryAIModal({ action, files, catalogIngestJobId, project, onClose }) {
  const [fileContents,  setFileContents]  = useState(null)   // { filename: content }
  const [fetchLoading,  setFetchLoading]  = useState(false)
  const [aiLoading,     setAiLoading]     = useState(false)
  const [aiResponse,    setAiResponse]    = useState(null)
  const [aiError,       setAiError]       = useState(null)
  const [autoTriggered, setAutoTriggered] = useState(false)
  const [saving,        setSaving]        = useState(false)
  const [saveStatus,    setSaveStatus]    = useState(null)  // null | 'saved' | 'error'

  // Both java-quick-view and service-preview operate on COBOL programs only.
  // Other file types (copybooks, JCL, etc.) are noted as supporting context but
  // are not passed as primary source to the AI for either action.
  const cobolOnlyActions = action === 'java-quick-view' || action === 'service-preview'
  const activeFiles    = cobolOnlyActions ? files.filter(f => f.group === 'COBOL') : files
  const supportingFiles = cobolOnlyActions ? files.filter(f => f.group !== 'COBOL') : []

  const fileCountLabel = activeFiles.length === files.length
    ? `${files.length} file${files.length !== 1 ? 's' : ''}`
    : `${activeFiles.length} COBOL file${activeFiles.length !== 1 ? 's' : ''} (of ${files.length} selected)`

  // ── Fetch all file contents in parallel ─────────────────────────────────
  useEffect(() => {
    if (!catalogIngestJobId || activeFiles.length === 0) {
      setFileContents({})
      return
    }
    setFetchLoading(true)
    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = token ? { Authorization: `Bearer ${token}` } : {}

    console.log('[BulkAI] Fetching content for', activeFiles.length, 'files:',
      activeFiles.map(f => f.name))

    const promises = activeFiles.map(file => {
      const url = `${API_BASE}/editor/cobol/file?job_id=${encodeURIComponent(catalogIngestJobId)}&path=${encodeURIComponent(file.path)}`
      return fetch(url, { headers })
        .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
        .then(data => ({ name: file.name, content: data?.content || '' }))
        .catch(err => {
          console.warn('[BulkAI] Content fetch failed:', file.name, err.message)
          return { name: file.name, content: '' }
        })
    })

    Promise.all(promises).then(results => {
      const map = {}
      results.forEach(({ name, content }) => { map[name] = content })
      console.log('[BulkAI] Content ready for', Object.keys(map).length, 'files')
      setFileContents(map)
      setFetchLoading(false)
    })
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Build combined code block ────────────────────────────────────────────
  const buildCombinedCode = (contents) =>
    activeFiles
      .map(file => {
        const divider = '='.repeat(60)
        return `${divider}\n--- FILE: ${file.name} (${file.group}) ---\n${divider}\n${contents[file.name] || ''}`
      })
      .join('\n\n')

  // ── Auto-trigger AI once content loads (non-custom actions) ─────────────
  useEffect(() => {
    if (action === 'custom-prompt') return
    if (autoTriggered || aiLoading || aiResponse) return
    if (fetchLoading) return
    if (fileContents === null) return
    runAI()
    setAutoTriggered(true)
  }, [fileContents, fetchLoading, autoTriggered, aiLoading, aiResponse])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── AI call dispatcher ───────────────────────────────────────────────────
  const runAI = async (userPrompt) => {
    setAiLoading(true)
    setAiResponse(null)
    setAiError(null)

    const fileContentsMap  = fileContents || {}
    const combinedCode     = buildCombinedCode(fileContentsMap)
    const fileNames        = activeFiles.map(f => f.name).join(', ')
    const fileCount        = activeFiles.length
    const supportingNames  = supportingFiles.map(f => f.name).join(', ')

    // Verify all expected files are represented in the combined code block
    const includedFiles  = activeFiles.filter(f => fileContentsMap[f.name] !== undefined).map(f => f.name)
    const missingContent = activeFiles.filter(f => !fileContentsMap[f.name]).map(f => f.name)

    const ctx = {
      scout_account_id: project?.account,
      application_name: project?.name,
      bulk_file_count:  fileCount,
      bulk_file_names:  fileNames,
      bulk_action_mode: action === 'service-preview' ? 'service-preview' : 'multi-file',
    }

    // Stable IDs for API compatibility
    const bulkId   = `bulk_${fileCount}_files`
    const bulkName = fileCount === 1
      ? activeFiles[0]?.name
      : `${fileCount} files: ${activeFiles.slice(0, 2).map(f => f.name).join(', ')}${fileCount > 2 ? ` +${fileCount - 2} more` : ''}`

    console.log('[BulkAI] Running:', action,
      '\n  All selected files passed to AI:', includedFiles,
      '\n  Files included in combined code:', fileCount,
      '\n  Files with empty content (still included):', missingContent.length ? missingContent : 'none',
      '\n  Supporting (non-COBOL) files:', supportingFiles.map(f => f.name),
      '\n  Mode:', action === 'service-preview' ? 'service-preview' : 'multi-file')

    try {
      let result

      if (action === 'explain') {
        // ── Bulk explain: each file must be addressed individually.
        // explainCobolCode() has a hardcoded single-file prompt ("Explain what this code does.
        // Section: X") which causes the AI to focus on the first program it finds.
        // For bulk mode we use customPromptAI with an explicit per-file requirement instead.
        const prompt =
          `You are analyzing ${fileCount} COBOL file${fileCount !== 1 ? 's' : ''} as a group: ${fileNames}.\n\n` +
          `Provide a structured explanation for THIS ENTIRE SET. ` +
          `Do NOT focus only on the first file. Cover every file explicitly — one section per file.\n\n` +
          `For EACH of the ${fileCount} files, explain:\n` +
          `- Its purpose and main processing function\n` +
          `- Key PROCEDURE DIVISION paragraphs and what each does\n` +
          `- Data it reads, writes, or passes between programs\n` +
          `- External dependencies: CALL targets, COPY books, file I/O\n` +
          `- How it relates to the other files in this selection\n\n` +
          `After covering each file individually, write a brief summary of how ` +
          `the ${fileCount} files work together as a unit.`
        result = await customPromptAI(combinedCode, prompt, bulkId, bulkName, ctx)

      } else if (action === 'business-use-case') {
        const prompt =
          `You are analyzing ${fileCount} COBOL file${fileCount !== 1 ? 's' : ''}: ${fileNames}.\n\n` +
          `Describe the COLLECTIVE business use case for this entire set. ` +
          `Do NOT reduce this to a single file — address all ${fileCount} files.\n\n` +
          `Cover:\n` +
          `- The business process each file supports, with one section per file\n` +
          `- The combined business function this set delivers together\n` +
          `- Key stakeholders and user groups\n` +
          `- Primary inputs and outputs across the selected set\n` +
          `- Business value of modernising these ${fileCount} files to Java as a group`
        result = await customPromptAI(combinedCode, prompt, bulkId, bulkName, ctx)

      } else if (action === 'document') {
        const prompt =
          `Generate developer documentation for ALL ${fileCount} COBOL file${fileCount !== 1 ? 's' : ''}: ${fileNames}.\n\n` +
          `Produce one documentation section per file. ` +
          `Do NOT collapse or skip any file — all ${fileCount} must be documented.\n\n` +
          `For EACH file, document:\n` +
          `- Purpose and overall function\n` +
          `- PROCEDURE DIVISION: key paragraphs, their roles, and processing logic\n` +
          `- DATA DIVISION: WORKING-STORAGE variables, FD/SD entries, key data structures\n` +
          `- COPY book dependencies and what each brings in\n` +
          `- External CALL targets and their expected inputs/outputs\n` +
          `- Integration points with the other files in this selection\n\n` +
          `End with a cross-reference section mapping the relationships between all ${fileCount} files.`
        result = await customPromptAI(combinedCode, prompt, bulkId, bulkName, ctx)

      } else if (action === 'java-quick-view') {
        // ── Service shape preview — structured markdown, NOT a full Java conversion.
        // Uses purpose='custom' so the backend returns prose/markdown, not a code dump.
        console.log('[BulkAI] java-quick-view | mode: service-shape-preview | files:', fileCount)
        const supportingSection = supportingNames
          ? `\nThe selection also includes these supporting files for context: ${supportingNames}.`
          : ''
        const prompt =
          `You are a COBOL-to-Java modernization architect doing a quick service-shape preview.\n` +
          `The user has selected ${fileCount} COBOL program${fileCount !== 1 ? 's' : ''} as a candidate service scope: ${fileNames}.${supportingSection}\n\n` +
          `Do NOT produce full compiled Java. Do NOT write import blocks, file writers, full helper implementations, or annotation noise.\n` +
          `Return a STRUCTURED SERVICE PREVIEW using exactly these sections:\n\n` +
          `## Suggested Service Name\n` +
          `Propose a clear Java service name (e.g. "CustomerAccountService") reflecting the collective purpose of the selected files.\n\n` +
          `## Java Components / Classes\n` +
          `List the classes and interfaces this service would need. One entry per line. Include a layer hint in brackets (Controller, Service, Repository, Model, Util, etc.).\n\n` +
          `## Key Methods per Class\n` +
          `For each main class, list 2–5 method signatures: method name, parameters, and return type. No method bodies.\n\n` +
          `## Inputs / Outputs\n` +
          `Describe the primary inputs and outputs at the service boundary.\n\n` +
          `## Business Purpose\n` +
          `One paragraph: what business capability does this service represent?\n\n` +
          `## Validation and Processing Flow\n` +
          `Step-by-step: how data enters, is validated, processed through the selected files, and what is returned.\n\n` +
          `## Light Skeleton (optional)\n` +
          `If useful, show a brief interface or class outline (30 lines maximum total). Omit if it adds no value.`
        result = await customPromptAI(combinedCode, prompt, bulkId, bulkName, ctx)

      } else if (action === 'service-preview') {
        // ── Service Definition: produces specification TEXT — not Java code.
        // Uses customPromptAI (purpose='custom') with a structured architectural prompt.
        // This is intentionally separate from java-quick-view.
        const supportingSection = supportingNames
          ? `\nThe selection also includes these supporting files: ${supportingNames}.`
          : ''
        const prompt =
          `You are a COBOL-to-Java modernization architect. ` +
          `The user has selected ${fileCount} COBOL program${fileCount !== 1 ? 's' : ''} as a logical service scope ` +
          `for modernization: ${fileNames}.${supportingSection}\n\n` +
          `Generate a SERVICE DEFINITION DOCUMENT (not compilable code) using the following structure. ` +
          `Be specific about each selected file — do NOT collapse this into a single-file analysis. ` +
          `Do NOT say "I'll start with the first file" or ask "Would you like me to continue?"\n\n` +
          `## Service Name\n` +
          `Propose a clear Java service name (e.g. "CustomerAccountService") that reflects what these programs collectively do.\n\n` +
          `## Entry Point\n` +
          `Identify the primary COBOL program in this set that acts as the main entry point ` +
          `(e.g. the program called from JCL or the outermost CALL target). Explain why.\n\n` +
          `## Selected Files — Roles & Responsibilities\n` +
          `For each of the ${fileCount} selected files, describe its role and responsibility within this service scope. ` +
          `Be explicit — one entry per file.\n\n` +
          `## Proposed Java Classes / Components\n` +
          `List the Java classes and interfaces this service would need, grouped by layer ` +
          `(e.g. controller, service, repository, model, util).\n\n` +
          `## REST API Endpoints (if applicable)\n` +
          `Propose plausible REST endpoints that would expose this service's business logic. ` +
          `If REST is not appropriate, say so and explain why.\n\n` +
          `## Data Model / Entities\n` +
          `Identify key data structures from the WORKING-STORAGE sections and FD/SD entries. ` +
          `Propose Java entity or DTO names and their primary fields.\n\n` +
          `## High-Level Processing Flow\n` +
          `Describe the processing flow from entry point through the supporting programs — ` +
          `step by step, referencing the actual file names.\n\n` +
          `## Assumptions & Notes\n` +
          `List what you assumed, what would need verification (e.g. missing copybooks, external CALLs, ` +
          `VSAM file definitions), and any significant modernization risks for this selection.`

        result = await customPromptAI(combinedCode, prompt, bulkId, bulkName, ctx)

      } else if (action === 'custom-prompt' && userPrompt) {
        // Prepend a bulk scope header to the combined code so the AI always knows
        // it is working on the full selected set, not just the first file it reads.
        const bulkContextCode =
          `[BULK SELECTION — ${fileCount} file${fileCount !== 1 ? 's' : ''}: ${fileNames}]\n` +
          `The user's prompt below applies to ALL of the following files as a group.\n` +
          `Do not focus only on the first file — address the full selected set.\n\n` +
          combinedCode
        result = await customPromptAI(bulkContextCode, userPrompt, bulkId, bulkName, ctx)
      }

      console.log('[BulkAI] Complete:', action,
        '| output mode:', action === 'service-preview' ? 'service-definition-doc' : 'multi-file-analysis',
        '| log_id:', result?.log_id,
        '| files:', fileCount)
      setAiResponse(result)
    } catch (err) {
      console.error('[BulkAI] Failed:', action, err.message)
      setAiError(err.message || 'AI request failed')
    } finally {
      setAiLoading(false)
    }
  }

  const handleCustomPromptSubmit = (prompt) => runAI(prompt)

  const handleRegenerate = () => {
    if (action === 'custom-prompt') {
      setAiResponse(null)
    } else {
      runAI()
    }
  }

  // ── Save AI output to S3 project workspace ───────────────────────────────
  const handleSave = async (content) => {
    if (!content || !project?.account || !project?.name) return
    setSaving(true)
    setSaveStatus(null)
    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const user     = (() => { try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}') } catch { return {} } })()
    try {
      const resp = await fetch(`${API_BASE}/workspace/ai-outputs/save`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          account_id:       project.account,
          user_id:          user?.user_id || user?.id || '',
          application_name: project.name,
          action,
          source_filename:  `bulk_${activeFiles.length}_files`,
          content,
          log_id:           aiResponse?.log_id || '',
        }),
      })
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: 'Unknown error' }))
        throw new Error(err.detail || `HTTP ${resp.status}`)
      }
      const result = await resp.json()
      console.log('[BulkAI] Saved:', result.filename, '→', result.s3_uri)
      setSaveStatus('saved')
    } catch (err) {
      console.error('[BulkAI] Save failed:', err.message)
      setSaveStatus('error')
    } finally {
      setSaving(false)
    }
  }

  const modalResponse = aiError
    ? { explanation: `Error: ${aiError}`, response: `Error: ${aiError}` }
    : aiResponse

  const isLoading = aiLoading || fetchLoading

  return (
    <AIResponseModal
      isOpen={true}
      onClose={onClose}
      title={`${ACTION_TITLE[action] || 'AI Analysis'} — ${fileCountLabel}`}
      mode={ACTION_MODE[action] || 'explain'}
      selectedCode=""
      sectionName={`${activeFiles.length} files`}
      aiResponse={modalResponse}
      aiLoading={isLoading}
      onRegenerate={handleRegenerate}
      onAccept={null}
      onCustomPromptSubmit={handleCustomPromptSubmit}
      onSave={handleSave}
      saving={saving}
      saveStatus={saveStatus}
    />
  )
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * InventoryAIModal
 *
 * Container that:
 *   1. Fetches file content from /editor/cobol/file at mount
 *   2. Auto-triggers the AI call for non-custom actions once content is ready
 *   3. Delegates rendering to the existing AIResponseModal (reused as-is)
 *
 * Props:
 *   action           — one of the AI_ACTIONS ids
 *   file             — { name, path, group }
 *   catalogIngestJobId — ingest job ID for file fetch
 *   project          — { name, account } for AI tenant context
 *   onClose          — dismiss callback
 */
export function InventoryAIModal({ action, file, catalogIngestJobId, project, onClose }) {
  const [fileContent,    setFileContent]    = useState(null)
  const [contentLoading, setContentLoading] = useState(false)
  const [aiLoading,      setAiLoading]      = useState(false)
  const [aiResponse,     setAiResponse]     = useState(null)
  const [aiError,        setAiError]        = useState(null)
  const [autoTriggered,  setAutoTriggered]  = useState(false)
  const [saving,         setSaving]         = useState(false)
  const [saveStatus,     setSaveStatus]     = useState(null)  // null | 'saved' | 'error'

  // ── Fetch file content at mount ─────────────────────────────────────────
  useEffect(() => {
    const canFetch = catalogIngestJobId && file?.path &&
                     file.group !== 'VSAM' && file.group !== 'UNKNOWN'
    if (!canFetch) return

    setContentLoading(true)
    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = token ? { Authorization: `Bearer ${token}` } : {}
    const url      = `${API_BASE}/editor/cobol/file?job_id=${encodeURIComponent(catalogIngestJobId)}&path=${encodeURIComponent(file.path)}`

    console.log('[InventoryAI] Fetching file content for AI:', file.name)
    fetch(url, { headers })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => {
        // Endpoint returns { content: "..." } — extract the plain-text source
        const text = data?.content || ''
        console.log('[InventoryAI] File content loaded:', file.name, {
          type:        typeof text,
          extracted:   'data.content',
          length_chars: text.length,
          length_kb:   (text.length / 1024).toFixed(1),
        })
        setFileContent(text)
        setContentLoading(false)
      })
      .catch(err => {
        console.warn('[InventoryAI] Content fetch failed:', err.message, '— proceeding with empty code')
        setFileContent('')   // proceed with empty string; AI still runs
        setContentLoading(false)
      })
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-trigger AI for non-custom actions once content is ready ────────
  useEffect(() => {
    if (action === 'custom-prompt') return   // custom waits for user input
    if (autoTriggered || aiLoading || aiResponse) return
    if (contentLoading) return               // wait for fetch to finish
    if (fileContent === null) return         // not yet resolved

    runAI()
    setAutoTriggered(true)
  }, [fileContent, contentLoading, autoTriggered, aiLoading, aiResponse])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── AI call dispatcher ───────────────────────────────────────────────────
  const runAI = async (userPrompt) => {
    setAiLoading(true)
    setAiResponse(null)
    setAiError(null)

    const code = fileContent || ''
    const ctx  = {
      scout_account_id: project?.account,
      application_name: project?.name,
    }

    console.log('[InventoryAI] Running action:', action, 'for:', file?.name)
    try {
      let result
      if (action === 'explain') {
        result = await explainCobolCode(code, file.name, file.name)

      } else if (action === 'business-use-case') {
        const prompt =
          `Describe the business use case for this COBOL program "${file.name}": ` +
          `what business process does it support, who are the stakeholders, ` +
          `what are the key inputs and outputs, and what is the business value of modernising it to Java?`
        result = await customPromptAI(code, prompt, file.name, file.name, ctx)

      } else if (action === 'document') {
        const prompt =
          `Generate comprehensive developer documentation for "${file.name}": ` +
          `describe its purpose, key paragraphs and their roles, data structures (WORKING-STORAGE), ` +
          `copybook dependencies, external CALL targets, and integration points with other programs.`
        result = await customPromptAI(code, prompt, file.name, file.name, ctx)

      } else if (action === 'java-quick-view') {
        // ── Service shape preview for a single file — structured markdown, not a code dump.
        const prompt =
          `You are a COBOL-to-Java modernization architect doing a quick service-shape preview.\n` +
          `File: ${file.name}.\n\n` +
          `Do NOT produce full compiled Java. Do NOT write import blocks, file writers, full helper implementations, or annotation noise.\n` +
          `Return a STRUCTURED SERVICE PREVIEW using exactly these sections:\n\n` +
          `## Suggested Service Name\n` +
          `Propose a clear Java service name reflecting what this file does.\n\n` +
          `## Java Components / Classes\n` +
          `List the classes and interfaces this file would map to. One entry per line with a layer hint in brackets.\n\n` +
          `## Key Methods per Class\n` +
          `For each main class, list 2–5 method signatures: name, parameters, return type. No bodies.\n\n` +
          `## Inputs / Outputs\n` +
          `Describe the primary inputs and outputs at the service boundary.\n\n` +
          `## Business Purpose\n` +
          `One paragraph: what business capability does this file represent?\n\n` +
          `## Validation and Processing Flow\n` +
          `Step-by-step: how data enters, is validated, processed, and what is returned.\n\n` +
          `## Light Skeleton (optional)\n` +
          `If useful, show a brief interface or class outline (30 lines maximum). Omit if it adds no value.`
        result = await customPromptAI(code, prompt, file.name, file.name, ctx)

      } else if (action === 'custom-prompt' && userPrompt) {
        result = await customPromptAI(code, userPrompt, file.name, file.name, ctx)
      }

      console.log('[InventoryAI] Action complete:', action, '| log_id:', result?.log_id)
      setAiResponse(result)
    } catch (err) {
      console.error('[InventoryAI] AI call failed:', action, err.message)
      setAiError(err.message || 'AI request failed')
    } finally {
      setAiLoading(false)
    }
  }

  const handleCustomPromptSubmit = (prompt) => runAI(prompt)

  // ── Save AI output to S3 project workspace ───────────────────────────────
  const handleSave = async (content) => {
    if (!content || !project?.account || !project?.name) {
      console.warn('[InventoryAI] Cannot save — missing project context')
      return
    }
    setSaving(true)
    setSaveStatus(null)

    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const user     = (() => { try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}') } catch { return {} } })()

    try {
      const resp = await fetch(`${API_BASE}/workspace/ai-outputs/save`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          account_id:       project.account,
          user_id:          user?.user_id || user?.id || '',
          application_name: project.name,
          action,
          source_filename:  file?.name || 'unknown',
          content,
          log_id:           aiResponse?.log_id || '',
        }),
      })

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: 'Unknown error' }))
        throw new Error(err.detail || `HTTP ${resp.status}`)
      }

      const result = await resp.json()
      console.log('[InventoryAI] Saved AI output:', result.filename, '→', result.s3_uri)
      setSaveStatus('saved')
    } catch (err) {
      console.error('[InventoryAI] Save failed:', err.message)
      setSaveStatus('error')
    } finally {
      setSaving(false)
    }
  }

  const handleRegenerate = () => {
    if (action === 'custom-prompt') {
      setAiResponse(null)   // go back to the input textarea
    } else {
      runAI()
    }
  }

  // Build the response shape AIResponseModal expects
  // For errors: surface as explanation text so the modal shows something honest
  const modalResponse = aiError
    ? { explanation: `Error: ${aiError}`, response: `Error: ${aiError}` }
    : aiResponse

  return (
    <AIResponseModal
      isOpen={true}
      onClose={onClose}
      title={`${ACTION_TITLE[action] || 'AI Analysis'} — ${file?.name}`}
      mode={ACTION_MODE[action] || 'explain'}
      selectedCode={fileContent || ''}
      sectionName={file?.name}
      aiResponse={modalResponse}
      aiLoading={aiLoading || contentLoading}
      onRegenerate={handleRegenerate}
      onAccept={null}                      // preview-only from Inventory — no insert
      onCustomPromptSubmit={handleCustomPromptSubmit}
      onSave={handleSave}
      saving={saving}
      saveStatus={saveStatus}
    />
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function GuidedAnalyzeView({ project, ingestResult, onBack, onBackToProjects, onConfirm, onOpenSettings, onOpenHelp, onOpenReports, onGenerateDocs, onExtractBizLogic, onDecomposeCode, onGoToServiceGeneration, onGoToJavaPkg, onGoToRefineCode, onGoToRefactorCode, userProfile = {} }) {

  // ── Real-data state (ingest catalog) ──────────────────────────────────────
  const [realFiles,      setRealFiles]      = useState(null)
  const [realMetrics,    setRealMetrics]    = useState(null)
  const [catalogData,    setCatalogData]    = useState(null)  // raw classified_catalog
  const [loading,        setLoading]        = useState(true)
  const [fetchError,     setFetchError]     = useState(null)
  // catalogMissing = true when ingest job id absent OR catalog fetch failed.
  // Non-fatal when CA artifacts are present — only affects the Inventory tab.
  const [catalogMissing, setCatalogMissing] = useState(false)

  // ── Code analysis state (optional — from most recent workflow run) ─────────
  const [caJobId,    setCaJobId]    = useState(null)
  // ── Code Refactor state — queued in background during upload ────────────────
  // crStatus: null | 'queued' | 'running' | 'succeeded' | 'failed'
  const [crJobId,    setCrJobId]    = useState(null)
  const [crStatus,   setCrStatus]   = useState(null)
  // ── Data Analysis job — used by the Data View tab ────────────────────────────
  const [daJobId,    setDaJobId]    = useState(null)
  const [parseData,            setParseData]            = useState(null)   // comprehensive_parse_results
  const [assetGraph,           setAssetGraph]           = useState(null)   // guided_analyze_asset_graph
  const [intelligenceMetrics,  setIntelligenceMetrics]  = useState(null)   // intelligence_metrics.json (Phase 1)
  // Tier 1 enhancement analysis — graph-powered queries
  const [deadCode,             setDeadCode]             = useState(null)
  const [copybookImpact,       setCopybookImpact]       = useState(null)
  const [fieldXref,            setFieldXref]            = useState(null)
  const [complexityHeatmap,    setComplexityHeatmap]    = useState(null)
  const [gnucobolEnrichment,   setGnucobolEnrichment]   = useState(null)
  const [performChain,         setPerformChain]         = useState(null)
  const [caLoading,            setCaLoading]            = useState(true)   // true until we know if CA data exists
  // catalogRecovering: true while async CA-upstream ingest-link fetch is in flight
  // Lets Inventory show a "recovering..." state instead of the amber unavailable notice
  const [catalogRecovering, setCatalogRecovering] = useState(false)
  // catalogIngestJobId: the ingest job that backed the loaded catalog — used for file preview
  const [catalogIngestJobId, setCatalogIngestJobId] = useState(null)

  // ── File preview state ─────────────────────────────────────────────────────
  // filePreview: content for the quick-preview pane in the detail panel
  const [filePreview, setFilePreview] = useState(null)  // { loading, content, error }
  // fullModal: drives the full-file modal (opened by the More button)
  const [fullModal,   setFullModal]   = useState(false) // boolean open/closed

  // aiMenu: positioned dropdown shown when clicking ⋯ on a row — { file, top, left } or null
  const [aiMenu,  setAiMenu]  = useState(null)
  // aiModal: single-file AI action modal — { action, file } or null
  const [aiModal, setAiModal] = useState(null)
  // bulkAiModal: multi-file AI action modal — { action, files[] } or null
  const [bulkAiModal, setBulkAiModal] = useState(null)

  // caJavaFiles: Java file list resolved via bundle endpoint (same as workflow CodeEditorView)
  // Each entry: { name, path, job_id, stage, ... } from /files/{account}/{app}/bundle
  const [caJavaFiles,    setCaJavaFiles]    = useState([])
  // javaModal: null when closed; { loading, content, error, fileName, javaFile } when open
  const [javaModal,      setJavaModal]      = useState(null)

  // ── Navigation / view state ────────────────────────────────────────────────
  const [activeTab,      setActiveTab]      = useState('Inventory')
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false)
  const [selectedFile,   setSelectedFile]   = useState(null)
  const [activeScope,    setActiveScope]    = useState('all')
  const [activeView,     setActiveView]     = useState('List')
  const [riskHelpOpen,   setRiskHelpOpen]   = useState(false)          // Risk Analysis floating help window
  const [riskHelpPos,    setRiskHelpPos]    = useState({ top: 80, left: 40 }) // draggable position (top/left)
  const riskHelpBtnRef     = useRef(null)   // toggle button (for Escape close focus return)
  const riskHelpPopoverRef = useRef(null)   // the floating window
  const [searchText,     setSearchText]     = useState('')
  const [expandedGroups, setExpandedGroups] = useState(
    () => Object.fromEntries(GROUP_ORDER.map(g => [g, true]))
  )

  // ── Bulk mode state ────────────────────────────────────────────────────────
  const [bulkMode,       setBulkMode]       = useState(false)
  const [selectedRowIds, setSelectedRowIds] = useState(new Set())

  // ── Header user menu ───────────────────────────────────────────────────────
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const getShortName = () => {
    if (userProfile.firstName && userProfile.lastName) {
      return `${userProfile.firstName} ${userProfile.lastName.charAt(0)}.`
    }
    return userProfile.displayName || 'User'
  }

  // ── Risk help window: close on Escape (floating window — no outside-click close) ──
  useEffect(() => {
    if (!riskHelpOpen) return
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setRiskHelpOpen(false)
        riskHelpBtnRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [riskHelpOpen])

  // ── Effect 1: fetch classified_catalog from ingest job ────────────────────
  useEffect(() => {
    const ingestJobId = ingestResult?.jobs?.ingest_job_id

    console.log('[GuidedAnalyze] Loading catalog — ingest_job_id:', ingestJobId || '(none)')

    if (!ingestJobId) {
      // Non-fatal: catalog is unavailable, but CA artifacts (parseData/assetGraph) may still load.
      // Dashboard and Technical View work without catalog. Inventory shows a clean unavailable notice.
      console.warn('[GuidedAnalyze] No ingest_job_id — catalog unavailable, continuing with CA artifacts only.', {
        ingestResult,
        note: 'Dashboard + Technical View will use CA artifacts; Inventory will show catalog-unavailable notice',
      })
      setCatalogMissing(true)
      setLoading(false)
      // Default to Dashboard (which works from CA data) instead of Inventory (which needs catalog)
      setActiveTab('Dashboard')
      return
    }

    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = token ? { Authorization: `Bearer ${token}` } : {}

    fetch(`${API_BASE}/ingest/results/${ingestJobId}`, { headers })
      .then(res => {
        if (!res.ok) throw new Error(`API returned HTTP ${res.status}`)
        return res.json()
      })
      .then(data => {
        const catalog = data.classified_catalog

        if (!catalog) {
          throw new Error(
            `classified_catalog missing from ingest job ${ingestJobId}. ` +
            `The ingest worker may not have finished, or the catalog was not ` +
            `written to S3. Check API logs for [IngestResults] entries.`
          )
        }

        const typeKeys = Object.keys(catalog.classifications || {})
        console.log('[GuidedAnalyze] Catalog received:', {
          job_id: ingestJobId, types: typeKeys, summary: catalog.summary,
        })

        const files   = mapCatalogToFiles(catalog)
        const metrics = buildMetrics(files)

        const groupCounts = files.reduce((acc, f) => {
          acc[f.group] = (acc[f.group] || 0) + 1
          return acc
        }, {})
        console.log('[GuidedAnalyze] Mapped', files.length, 'files:', groupCounts)

        setCatalogData(catalog)
        setRealFiles(files)
        setRealMetrics(metrics)
        setSelectedFile(files[0] || null)
        setCatalogIngestJobId(ingestJobId)   // enables file preview
        setLoading(false)
      })
      .catch(err => {
        console.error('[GuidedAnalyze] Catalog fetch failed:', err.message, {
          ingest_job_id: ingestJobId,
          note: 'Non-fatal when CA artifacts are present — Inventory will show catalog-unavailable notice',
        })
        // catalogMissing flags the Inventory section; fetchError preserved for detail messaging.
        // Neither alone blocks the whole screen — see render logic below.
        setCatalogMissing(true)
        setFetchError(`Could not load analysis catalog: ${err.message}`)
        setLoading(false)
      })
  }, [ingestResult])

  // ── Effect 2: load comprehensive_parse_results for Dashboard / Technical View ─
  //
  // Two paths:
  //   FAST PATH  — ingestResult.jobs.code_analysis_job_id is set by GuidedUploadView
  //                after it auto-ran and polled code analysis to completion.
  //                Use that job ID directly — no workflow-runs roundtrip needed.
  //
  //   FALLBACK   — Project opened from history (no fresh upload). Query workflow
  //                runs to find the most recent pipeline-code-analysis job.
  //
  // If no code analysis exists either way, caLoading=false, parseData=null.
  // Dashboard and Technical View show honest "pending" states.
  useEffect(() => {
    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = token ? { Authorization: `Bearer ${token}` } : {}

    // Shared helper: fetch CA artifacts (parse results + asset graph) in parallel.
    // Also triggers a catalog-recovery side-chain when ingest_job_id is absent
    // from the session (project-click entry path) — queries the CA job's upstream
    // ingest link and loads classified_catalog so Inventory works normally.
    const fetchCaArtifacts = (caId) => {
      setCaJobId(caId)
      const parseUrl = `${API_BASE}/codeanalysis/${caId}/results/json/comprehensive_parse_results.json`
      const agUrl    = `${API_BASE}/codeanalysis/${caId}/results/json/guided_analyze_asset_graph.json`
      console.log('[Dashboard] Fetching CA artifacts from job:', caId)

      const fetchJson = (url) =>
        fetch(url, { headers }).then(r => r.ok ? r.json() : null).catch(() => null)

      // ── Catalog-recovery side-chain ─────────────────────────────────────────
      // Runs only when the UI was entered without an active ingest session
      // (project-click path, no ingestResult.jobs.ingest_job_id).
      // Queries GET /codeanalysis/{caId}/ingest-link → gets upstream ingest_job_id
      // → fetches /ingest/results/{id} → wires classified_catalog into Inventory.
      // Fires independently after CA artifacts; does NOT delay setCaLoading(false).
      const tryRecoverCatalogFromUpstream = async () => {
        console.log('[Inventory] No ingest session — trying CA upstream ingest link for job:', caId)
        setCatalogRecovering(true)  // Inventory shows "recovering..." instead of amber notice
        try {
          const linkRes = await fetch(`${API_BASE}/codeanalysis/${caId}/ingest-link`, { headers })
          if (!linkRes.ok) {
            console.log('[Inventory] ingest-link returned', linkRes.status, '— no upstream link available')
            setCatalogRecovering(false)
            return
          }
          const linkData = await linkRes.json()
          const linkedIngestId = linkData?.ingest_job_id
          if (!linkedIngestId) {
            console.log('[Inventory] CA job has no upstream ingest_job_id recorded — Inventory stays unavailable')
            setCatalogRecovering(false)
            return
          }

          console.log('[Inventory] ✅ Upstream ingest job found:', linkedIngestId, '— fetching catalog')

          const ingestRes = await fetch(`${API_BASE}/ingest/results/${linkedIngestId}`, { headers })
          if (!ingestRes.ok) {
            console.warn('[Inventory] ingest/results returned', ingestRes.status, 'for', linkedIngestId)
            setCatalogRecovering(false)
            return
          }
          const ingestData = await ingestRes.json()
          const catalog = ingestData?.classified_catalog
          if (!catalog) {
            console.warn('[Inventory] classified_catalog absent from ingest results — Inventory stays unavailable')
            setCatalogRecovering(false)
            return
          }

          const files   = mapCatalogToFiles(catalog)
          const metrics = buildMetrics(files)
          const groupCounts = files.reduce((acc, f) => {
            acc[f.group] = (acc[f.group] || 0) + 1; return acc
          }, {})

          console.log('[Inventory] ✅ Catalog recovered from CA upstream ingest link:', {
            ca_job_id:            caId,
            linked_ingest_job_id: linkedIngestId,
            files_loaded:         files.length,
            groups:               groupCounts,
            source:               'ca_upstream_ingest_link',
          })

          // Wire into Inventory state — React will re-render Inventory with real data
          setCatalogData(catalog)
          setRealFiles(files)
          setRealMetrics(metrics)
          setSelectedFile(files[0] || null)
          setCatalogMissing(false)          // clear the amber unavailable notice
          setCatalogRecovering(false)       // done recovering
          setCatalogIngestJobId(linkedIngestId)  // enables file preview
          setFetchError(null)               // clear any prior catalog error message
        } catch (err) {
          console.warn('[Inventory] Catalog recovery via CA upstream failed:', err.message)
          setCatalogRecovering(false)
          // catalogMissing stays true — amber notice remains honest
        }
      }

      const metricsUrl = `${API_BASE}/codeanalysis/${caId}/results/json/intelligence_metrics.json`

      return Promise.all([fetchJson(parseUrl), fetchJson(agUrl), fetchJson(metricsUrl)])
        .then(([parse, ag, metrics]) => {
          if (parse) {
            const pSum = parse.summary || {}
            console.log('[Dashboard] ✅ comprehensive_parse_results loaded:', {
              source:           parseUrl,
              program_count:    pSum.program_count,
              copybook_count:   pSum.copybook_count,
              total_files:      pSum.total_files,
              total_data_items: pSum.total_data_items,
              source_lines:     pSum.source_lines,   // NOTE: absent in comprehensive_parse summary
              paragraphs:       pSum.paragraphs,     // NOTE: absent in comprehensive_parse summary
              copy_total:       pSum.copy_statements_total,
              copy_resolved:    pSum.copy_statements_resolved,
              copybooks_xref:   Object.keys(parse.cross_reference?.copybook_usage || {}).length,
              call_graph_progs: Object.keys(parse.cross_reference?.call_graph     || {}).length,
            })
            setParseData(parse)
          } else {
            console.warn('[Dashboard] ⚠ comprehensive_parse_results failed to load from:', parseUrl)
          }
          if (ag) {
            console.log('[Dashboard] ✅ guided_analyze_asset_graph loaded:', {
              source:              agUrl,
              total_assets:        ag.summary?.total_assets,
              total_relationships: ag.summary?.total_relationships,
              total_issues:        ag.summary?.total_issues,
              asset_counts:        ag.summary?.asset_counts,
            })
            setAssetGraph(ag)
          } else {
            console.warn(
              '[Dashboard] ⚠ guided_analyze_asset_graph not available from:', agUrl,
              '— Issues and JCL/Execution tabs will show unavailable state'
            )
          }
          // Intelligence metrics — graceful fallback: old jobs won't have this file
          if (metrics?.programs) {
            const ps = metrics.project_summary || {}
            console.log('[Intelligence] ✅ intelligence_metrics loaded:', {
              phase:           metrics.phase,
              schema_version:  metrics.schema_version,
              program_count:   metrics.program_count,
              high_risk:       ps.high_risk_count,
              medium_risk:     ps.medium_risk_count,
              low_risk:        ps.low_risk_count,
              highest_risk:    ps.highest_risk_program,
              highest_score:   ps.highest_risk_score,
            })
            setIntelligenceMetrics(metrics)
          } else {
            console.log(
              '[Intelligence] intelligence_metrics.json not available (old run or pre-Phase 1) — ' +
              'By Risk view will show placeholder. Re-run Code Analysis to generate metrics.'
            )
          }
          console.log('[Dashboard] CA artifacts resolved:', {
            parseData:          !!parse,
            assetGraph:         !!ag,
            intelligenceMetrics: !!metrics,
            caJobId:    caId,
            tabs_enabled: {
              Dependencies:    !!parse,
              Copybooks:       !!parse,
              'Data Assets':   !!(parse || ag),
              'JCL/Execution': !!ag,
              Issues:          !!ag,
              Statistics:      !!parse,
            },
          })

          // CA artifacts done — unblock CA-driven tabs immediately
          setCaLoading(false)

          // Tier 1 enhancement analysis — graph queries served as JSON artifacts
          // Non-blocking: failures are swallowed so existing tabs still work
          const tier1Fetches = [
            fetchJson(`${API_BASE}/codeanalysis/${caId}/results/json/dead_code_analysis.json`),
            fetchJson(`${API_BASE}/codeanalysis/${caId}/results/json/copybook_change_impact.json`),
            fetchJson(`${API_BASE}/codeanalysis/${caId}/results/json/field_cross_reference.json`),
            fetchJson(`${API_BASE}/codeanalysis/${caId}/results/json/complexity_heatmap.json`),
            fetchJson(`${API_BASE}/codeanalysis/${caId}/results/json/gnucobol_enrichment.json`),
            fetchJson(`${API_BASE}/codeanalysis/${caId}/results/json/perform_chain.json`),
          ]
          Promise.all(tier1Fetches).then(([dc, ci, fx, ch, gc, pc]) => {
            if (dc) setDeadCode(dc)
            if (ci) setCopybookImpact(ci)
            if (fx) setFieldXref(fx)
            if (ch) setComplexityHeatmap(ch)
            if (gc && gc.ran) setGnucobolEnrichment(gc)
            if (pc && pc.record_count > 0) setPerformChain(pc)
            console.log('[Tier1] Enhancement analysis loaded:', {
              deadCode: dc?.record_count ?? 'n/a',
              copybookImpact: ci?.record_count ?? 'n/a',
              fieldXref: fx?.record_count ?? 'n/a',
              complexityHeatmap: ch?.record_count ?? 'n/a',
              gnucobol: gc?.ran ? `${gc.files_succeeded}/${gc.files_analyzed} files` : 'n/a',
              performChain: pc?.record_count ?? 'n/a',
            })
          }).catch(err => console.warn('[Tier1] Enhancement fetch failed:', err.message))

          // Java file discovery is deferred to the bundle effect below (fires when
          // both caJobId and catalogIngestJobId are set). No fetch here.

          // Catalog-recovery side-chain: runs only when no ingest session was active
          // (project-click entry path). Fires async, does NOT delay CA tab rendering.
          if (!ingestResult?.jobs?.ingest_job_id) {
            tryRecoverCatalogFromUpstream()
          } else {
            console.log('[Inventory] Ingest session present — catalog already loading from Effect 1, skipping CA upstream fallback')
          }
        })
        .catch(err => {
          console.warn('[Dashboard] CA artifacts fetch failed:', err.message)
          setCaLoading(false)
        })
    }

    // ── FAST PATH: guided upload just completed with auto-run CA ────────────
    const directCaJobId = ingestResult?.jobs?.code_analysis_job_id
    // Pick up CR job ID queued silently during upload (if present)
    const directCrJobId = ingestResult?.jobs?.code_refactor_job_id
    // Pick up DA job ID queued silently during upload (if present)
    const directDaJobId = ingestResult?.jobs?.data_analysis_job_id
    if (directCrJobId) {
      console.log('[Dashboard] Code Refactor job ID from guided upload:', directCrJobId)
      setCrJobId(directCrJobId)
    }
    if (directDaJobId) {
      console.log('[Dashboard] Data Analysis job ID from guided upload:', directDaJobId)
      setDaJobId(directDaJobId)
    }
    if (directCaJobId) {
      console.log('[Dashboard] Direct CA job ID from guided upload — skipping runs lookup:', directCaJobId)
      fetchCaArtifacts(directCaJobId)
      return
    }

    // ── FALLBACK: look up most recent CA run for this project ───────────────
    if (!project?.account || !project?.name) {
      console.log('[Dashboard] No project context or direct CA job — skipping code analysis lookup')
      setCaLoading(false)
      return
    }

    const runsUrl = `${API_BASE}/workflows/runs?account_id=${encodeURIComponent(project.account)}&app_name=${encodeURIComponent(project.name)}&limit=10`
    console.log('[Dashboard] Fallback: looking for code analysis job via runs:', runsUrl)

    fetch(runsUrl, { headers })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        const runs = data?.workflow_runs || []
        console.log('[Dashboard] Workflow runs found:', runs.length)

        for (const run of runs) {
          const caId = run.job_ids?.['pipeline-code-analysis']
          if (caId) {
            console.log('[Dashboard] CA job found in run history:', caId, 'run:', run.workflow_run_id)
            return fetchCaArtifacts(caId)
          }
        }

        // ── SECOND FALLBACK: query Jobs table directly by app name ────────────
        // Handles projects that were analyzed via Guided Analyze before
        // WorkflowRun tracking existed, so no WorkflowRun record links the job.
        console.log('[Dashboard] No WorkflowRun found — trying direct CA job lookup for:', project.name)
        const latestJobUrl = `${API_BASE}/codeanalysis/latest-job?account_id=${encodeURIComponent(project.account)}&app_name=${encodeURIComponent(project.name)}`
        fetch(latestJobUrl, { headers })
          .then(res => res.ok ? res.json() : null)
          .then(data => {
            if (data?.job_id) {
              console.log('[Dashboard] ✅ Found CA job via direct lookup:', data.job_id, 'type:', data.job_type)
              fetchCaArtifacts(data.job_id)
            } else {
              console.log('[Dashboard] No CA job found via direct lookup — showing Pending state')
              setCaLoading(false)
            }
          })
          .catch(err => {
            console.warn('[Dashboard] Direct CA job lookup failed:', err.message)
            setCaLoading(false)
          })
      })
      .catch(err => {
        console.warn('[Dashboard] Error in runs fallback lookup:', err.message)
        setCaLoading(false)
      })
  }, [project, ingestResult])

  // ── Effect: discover DA job for the Data View tab ────────────────────────────
  // Fast path: already set from ingestResult (guided upload path).
  // Slow path: look up via /jobs/by-app for projects opened via project-click.
  useEffect(() => {
    if (daJobId) return  // already known (fast path)
    if (!project?.account || !project?.name) return
    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = token ? { Authorization: `Bearer ${token}` } : {}
    const daLookupUrl = `${API_BASE}/dataanalysis/latest-job?account_id=${encodeURIComponent(project.account)}&app_name=${encodeURIComponent(project.name)}`
    fetch(daLookupUrl, { headers })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.job_id) {
          console.log('[GuidedAnalyze] DA job found via lookup:', data.job_id)
          setDaJobId(data.job_id)
        } else {
          console.log('[GuidedAnalyze] No SUCCEEDED DA job found for app:', project.name)
        }
      })
      .catch(err => console.warn('[GuidedAnalyze] DA lookup failed (non-fatal):', err.message))
  }, [project, daJobId])

  // ── Mode summary log: emitted once both catalog + CA effects have settled ─────
  // Logs the final rendering mode so we can grep [GuidedAnalyze] Mode:
  useEffect(() => {
    if (loading || caLoading) return  // wait for both to finish
    const catalogLoaded  = !catalogMissing && !!catalogData
    const caLoaded       = !!(parseData || assetGraph)
    const mode           = catalogLoaded && caLoaded ? 'FULL'
                         : caLoaded                  ? 'PARTIAL (CA only — Inventory unavailable)'
                         : catalogLoaded             ? 'PARTIAL (Catalog only — Dashboard/Technical pending)'
                         :                            'EMPTY (nothing loaded)'
    const tabAvailability = {
      Dashboard:       caLoaded || 'pending (no CA data)',
      Inventory:       catalogLoaded || 'unavailable (no catalog)',
      'Technical View': caLoaded || 'pending (no CA data)',
    }
    console.log('[GuidedAnalyze] Mode:', mode, {
      ingest_job_id:    ingestResult?.jobs?.ingest_job_id || '(none)',
      ca_job_id:        caJobId || '(none)',
      catalog_loaded:   catalogLoaded,
      parse_loaded:     !!parseData,
      asset_graph_loaded: !!assetGraph,
      catalog_missing:  catalogMissing,
      fetch_error:      fetchError || null,
      tab_availability: tabAvailability,
    })
  }, [loading, caLoading])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Code Refactor status polling ─────────────────────────────────────────
  // Fires when crJobId is set (queued during upload). Polls every 5s until
  // succeeded or failed. Controls whether "Download Refactored Java" is shown.
  useEffect(() => {
    if (!crJobId || crStatus === 'succeeded' || crStatus === 'failed') return
    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = token ? { Authorization: `Bearer ${token}` } : {}

    const poll = async () => {
      try {
        const r = await fetch(`${API_BASE}/jobs/${crJobId}`, { headers })
        if (!r.ok) return
        const job = await r.json()
        const st  = (job.status || '').toLowerCase()
        if (st === 'succeeded' || st === 'completed') {
          setCrStatus('succeeded')
          console.log('[GuidedAnalyze] Code Refactor complete — Download Refactored Java available')
        } else if (st === 'failed' || st === 'canceled' || st === 'cancelled') {
          setCrStatus('failed')
          console.warn('[GuidedAnalyze] Code Refactor job failed:', crJobId)
        } else {
          setCrStatus(st)
        }
      } catch (_) {}
    }

    poll() // immediate first check
    const timer = setInterval(poll, 5000)
    return () => clearInterval(timer)
  }, [crJobId, crStatus])

  // ── Java file discovery via /editor/bundle ────────────────────────────────
  // Fires when caJobId (Code Analysis job) and catalogIngestJobId are both known.
  // GET /editor/bundle?ingest_job_id=...&code_analysis_job_id=...
  // API-side _resolve_java_files() extracts the Java list from generated_java.zip
  // (or generated/ directory for worker-mode CA jobs).
  useEffect(() => {
    if (!caJobId || !catalogIngestJobId || !project?.account || !project?.name) {
      console.log('[JavaFiles] bundle fetch skipped — missing prereqs',
        { caJobId, catalogIngestJobId, account: project?.account, app: project?.name })
      return
    }
    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = token ? { Authorization: `Bearer ${token}` } : {}
    const url      = `${API_BASE}/editor/bundle` +
                     `?ingest_job_id=${encodeURIComponent(catalogIngestJobId)}` +
                     `&code_analysis_job_id=${encodeURIComponent(caJobId)}`
    console.log('[JavaFiles] fetching bundle for CA java files →', url)
    fetch(url, { headers })
      .then(r => {
        console.log('[JavaFiles] bundle response HTTP', r.status, r.ok ? 'OK' : 'FAIL')
        return r.ok ? r.json() : null
      })
      .then(bundle => {
        const files = bundle?.java?.files || []
        setCaJavaFiles(files)
        console.log('[JavaFiles] resolved', files.length, 'java files:',
          files.map(f => f.name))
      })
      .catch(err => {
        console.warn('[JavaFiles] bundle fetch error:', err.message)
      })
  }, [caJobId, catalogIngestJobId])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── File preview: fetch real content when a row is selected ───────────────
  // Uses GET /editor/cobol/file?job_id={catalogIngestJobId}&path={file.path}
  // This endpoint resolves any file from the ingest archive by its catalog path,
  // with a filename-fallback search — so COBOL, copybooks, JCL, SQL all work.
  // VSAM / binary types get an honest "not previewable" message instead.
  useEffect(() => {
    setFilePreview(null)   // clear previous on every selection change
    setFullModal(false)    // close modal when switching files

    if (!selectedFile) return

    // Binary / data assets — no source text to preview
    if (selectedFile.group === 'VSAM' || selectedFile.group === 'UNKNOWN') {
      setFilePreview({ loading: false, content: null,
        error: 'Binary or data file — source text preview not available.' })
      console.log('[Preview] Skipped (non-text group):', selectedFile.group, selectedFile.name)
      return
    }

    if (!catalogIngestJobId) {
      setFilePreview({ loading: false, content: null,
        error: 'Preview requires file catalog context — not available in this view.' })
      console.log('[Preview] No catalogIngestJobId — cannot fetch preview for:', selectedFile.name)
      return
    }

    setFilePreview({ loading: true, content: null, error: null })

    const API_BASE = window.__MODERNIZEIT_API_URL__ || getAPIBaseUrl()
    // Guard: localStorage can hold the string "null" if JSON.stringify(null) was stored.
    // A real JWT is always > 100 characters. Treat anything shorter as no token.
    const rawToken = localStorage.getItem('modernizeit_token') || ''
    const token    = rawToken.length > 100 ? rawToken : ''
    const headers  = token ? { Authorization: `Bearer ${token}` } : {}
    const url      = `${API_BASE}/editor/cobol/file?job_id=${encodeURIComponent(catalogIngestJobId)}&path=${encodeURIComponent(selectedFile.path)}`

    if (!token) {
      console.warn('[Preview] No valid auth token in localStorage — preview requires login', {
        raw_len: rawToken.length,
        file: selectedFile.name,
      })
      setFilePreview({ loading: false, content: null,
        error: 'Session expired — please log out and back in to view file previews.' })
      return
    }

    console.log('[Preview] Fetching file content:', {
      file:   selectedFile.name,
      group:  selectedFile.group,
      path:   selectedFile.path,
      job_id: catalogIngestJobId,
    })

    fetch(url, { headers })
      .then(r => {
        if (!r.ok) {
          const status = r.status
          return r.json().catch(() => null).then(body => {
            const detail = body?.detail || r.statusText || 'Unknown error'
            throw Object.assign(new Error(`HTTP ${status}`), { status, detail })
          })
        }
        return r.json()
      })
      .then(data => {
        const content = data.content || ''
        console.log('[Preview] ✅ Content loaded:', {
          file:  selectedFile.name,
          lines: content.split('\n').length,
          bytes: content.length,
        })
        setFilePreview({ loading: false, content, error: null })
      })
      .catch(err => {
        const status = err.status
        const detail = err.detail
        console.warn('[Preview] ⚠ Content unavailable:', { file: selectedFile.name, status, detail, error: err.message })
        let msg = 'File content not available for preview.'
        if (status === 401) msg = 'Session expired — please log out and back in to reload preview.'
        else if (status === 404) msg = `File not found on server: ${selectedFile.name}`
        else if (status === 422) msg = 'Preview not available — invalid catalog reference.'
        else if (status === 409) msg = 'Ingest job is not yet complete — preview unavailable.'
        else if (status === 500) msg = `Server error reading file: ${detail || 'check API logs'}`
        setFilePreview({ loading: false, content: null, error: msg })
      })
  }, [selectedFile?.id, catalogIngestJobId])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── ESC key closes modals + AI menu ──────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setFullModal(false)
        setAiMenu(null)
        setAiModal(null)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // ── Derived inventory data ─────────────────────────────────────────────────
  const files   = realFiles   || []
  const metrics = realMetrics || []

  const filteredFiles = files.filter(f => {
    const matchScope =
      activeScope === 'all'          ? true :
      activeScope === 'vsam'         ? f.group === 'VSAM' :
                                       f.status === activeScope
    const matchSearch = searchText.trim()
      ? f.name.toLowerCase().includes(searchText.toLowerCase()) ||
        f.path.toLowerCase().includes(searchText.toLowerCase())
      : true
    return matchScope && matchSearch
  })

  const grouped = GROUP_ORDER.reduce((acc, g) => {
    const rows = filteredFiles.filter(f => f.group === g)
    if (rows.length) acc[g] = rows
    return acc
  }, {})

  const toggleGroup = (g) =>
    setExpandedGroups(prev => ({ ...prev, [g]: !prev[g] }))

  // ── Line count lookup from parse results ──────────────────────────────────
  // parseData.programs is keyed by filename WITH extension (e.g. "IFPR321.CBL").
  // parseData.copybooks is keyed the same way (e.g. "CUSTREC.cpy").
  // Inventory catalog files are identified by name with extension ("IFPR321.cbl").
  //
  // FIX (2026-03-31): previous build stripped only the path separator but kept the
  // extension, so the map stored "IFPR321.CBL" while getLineCount looked up "IFPR321".
  // Both sides now strip the extension before uppercasing → consistent stem keys.
  const lineCountByName = (() => {
    const map = {}
    let programsWithCount = 0

    // Programs
    for (const [key, prog] of Object.entries(parseData?.programs || {})) {
      if (prog.line_count) {
        programsWithCount++
        // key = "IFPR321.CBL" or "path/to/IFPR321.CBL" → stem = "IFPR321"
        const stem = key.split('/').pop().replace(/\.[^.]+$/, '').toUpperCase()
        map[stem] = prog.line_count
      }
    }

    // Copybooks — also carry line_count; don't overwrite a program entry if stems collide
    for (const [key, cb] of Object.entries(parseData?.copybooks || {})) {
      if (cb.line_count) {
        const stem = key.split('/').pop().replace(/\.[^.]+$/, '').toUpperCase()
        if (!(stem in map)) map[stem] = cb.line_count
      }
    }

    if (parseData?.programs) {
      console.log('[Inventory] lineCountByName built:', {
        programs_with_line_count: programsWithCount,
        total_lookup_entries:     Object.keys(map).length,
        match_key:                'filename stem, extension stripped, uppercased',
        sample_keys:              Object.keys(map).slice(0, 8),
      })
    }

    return map
  })()

  // Helper: resolve line count for a catalog file (strips extension, uppercases stem)
  const getLineCount = (fileName) => {
    const stem = (fileName || '').replace(/\.[^.]+$/, '').toUpperCase()
    return lineCountByName[stem] ?? null
  }

  // Complexity from CA per-program data — COBOL only, derived from avg lines per paragraph
  const complexityByName = (() => {
    if (!parseData?.programs) return {}
    const map = {}
    for (const [key, prog] of Object.entries(parseData.programs)) {
      const stem      = key.split('/').pop().replace(/\.[^.]+$/, '').toUpperCase()
      const lines     = prog.line_count || 0
      const paraCount = (prog.paragraphs || []).length
      if (lines === 0) continue
      const avg = paraCount > 0 ? lines / paraCount : lines
      map[stem] = avg > 60 ? 'High' : avg > 20 ? 'Medium' : 'Low'
    }
    return map
  })()

  const getComplexity = (fileName, group) => {
    if (group !== 'COBOL') return null
    const stem = (fileName || '').replace(/\.[^.]+$/, '').toUpperCase()
    return complexityByName[stem] ?? null
  }

  // ── Phase 1 intelligence metrics lookup ────────────────────────────────────
  // Returns the per-program metrics object from intelligence_metrics.json,
  // or null if metrics are unavailable (old job) or program not found.
  // Caller is responsible for null-guarding before rendering.
  const getRiskMetrics = (fileName) => {
    if (!intelligenceMetrics?.programs) return null
    const stem = (fileName || '').replace(/\.[^.]+$/, '').toUpperCase()
    return intelligenceMetrics.programs[stem] || null
  }

  // ── Line count match audit (runs only when both catalog files and parse data are ready) ──
  if (parseData?.programs && files.length > 0) {
    const cobolFiles   = files.filter(f => f.group === 'COBOL')
    const matched      = cobolFiles.filter(f => getLineCount(f.name) !== null)
    const unmatched    = cobolFiles.filter(f => getLineCount(f.name) === null)
    console.log('[Inventory] Line Count match audit:', {
      cobol_rows:     cobolFiles.length,
      matched:        matched.length,
      unmatched:      unmatched.length,
      unmatched_names: unmatched.map(f => f.name),
    })
  }

  // ── Bulk mode computed values ──────────────────────────────────────────────
  const allVisibleIds       = filteredFiles.map(f => f.id)
  const allVisibleSelected  = allVisibleIds.length > 0 &&
                              allVisibleIds.every(id => selectedRowIds.has(id))
  const someVisibleSelected = !allVisibleSelected &&
                              allVisibleIds.some(id => selectedRowIds.has(id))
  const selectedCount       = selectedRowIds.size

  const toggleBulkMode  = () => { setBulkMode(prev => !prev); setSelectedRowIds(new Set()) }

  const handleRowClick  = (file) => {
    if (bulkMode) {
      setSelectedRowIds(prev => {
        const next = new Set(prev)
        if (next.has(file.id)) next.delete(file.id)
        else next.add(file.id)
        return next
      })
    } else {
      setSelectedFile(file)
    }
  }

  const handleSelectAll = (checked) =>
    setSelectedRowIds(checked ? new Set(allVisibleIds) : new Set())

  // ── Per-row AI Actions ────────────────────────────────────────────────────
  // All 5 actions route through AIResponseModal using real aiService calls.
  const handleAiMenuOpen = (e, file) => {
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    console.log('[AIActions] Menu open for:', file.name, '| group:', file.group)
    setAiMenu({ file, top: rect.bottom + 4, left: Math.max(8, rect.right - 220) })
  }

  // View Java: uses /editor/java/file (same endpoint as workflow CodeEditorView readJavaFile)
  const handleViewJava = async (javaFile) => {
    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = token ? { Authorization: `Bearer ${token}` } : {}
    setJavaModal({ loading: true, content: null, error: null, fileName: javaFile.name, javaFile })
    try {
      const url = `${API_BASE}/editor/java/file?job_id=${encodeURIComponent(javaFile.job_id)}&path=${encodeURIComponent(javaFile.path)}`
      const res  = await fetch(url, { headers })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      console.log('[JavaView] Loaded Java source:', javaFile.name, `(${(data.content?.length / 1024).toFixed(1)} KB) path:`, javaFile.path)
      setJavaModal({ loading: false, content: data.content, error: null, fileName: javaFile.name, javaFile })
    } catch (err) {
      console.warn('[JavaView] Failed to load Java source:', err.message)
      setJavaModal({ loading: false, content: null, error: err.message, fileName: null, javaFile })
    }
  }

  // Download Java: same /editor/java/file endpoint, triggers blob download
  const handleDownloadJava = async (javaFile) => {
    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = token ? { Authorization: `Bearer ${token}` } : {}
    try {
      const url  = `${API_BASE}/editor/java/file?job_id=${encodeURIComponent(javaFile.job_id)}&path=${encodeURIComponent(javaFile.path)}`
      const res  = await fetch(url, { headers })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const blob = new Blob([data.content], { type: 'text/plain' })
      const href = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = href
      a.download = javaFile.name
      a.click()
      URL.revokeObjectURL(href)
      console.log('[JavaView] Download triggered:', javaFile.name)
    } catch (err) {
      console.warn('[JavaView] Download failed:', err.message)
    }
  }

  // Download Refactored Java: fetches transformed/{fileName} from the CR job
  const handleDownloadRefactoredJava = async (fileName) => {
    const API_BASE = getAPIBaseUrl()
    const token    = localStorage.getItem('modernizeit_token') || ''
    const headers  = token ? { Authorization: `Bearer ${token}` } : {}
    try {
      const url  = `${API_BASE}/editor/java/file?job_id=${encodeURIComponent(crJobId)}&path=transformed/${encodeURIComponent(fileName)}`
      const res  = await fetch(url, { headers })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const blob = new Blob([data.content], { type: 'text/plain' })
      const href = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = href
      a.download = fileName
      a.click()
      URL.revokeObjectURL(href)
      console.log('[JavaView] Refactored Java download triggered:', fileName)
    } catch (err) {
      console.warn('[JavaView] Refactored Java download failed:', err.message)
    }
  }

  const handleAiAction = (actionId) => {
    console.log('[AIActions] Action:', actionId, 'for:', aiMenu?.file?.name)
    setAiModal({ action: actionId, file: aiMenu.file })
    setAiMenu(null)
  }

  // ── Detail panel values ────────────────────────────────────────────────────
  const detail     = selectedFile
  const detailMeta = DETAIL_FOR[selectedFile?.group] || DETAIL_FOR.default

  const projectName = project?.name || 'Project'
  const colCount    = bulkMode ? 7 : 6

  // ── Analyze-page assistant context ─────────────────────────────────────────
  // Maps the active tab to a distinct pageType so PAGE_SUGGESTIONS / PAGE_LABELS
  // update automatically, and the assistant clears its message history on tab switch.
  const analyzePageType =
    activeTab === 'Dashboard'  ? 'analyze_dashboard'  :
    activeTab === 'Inventory'  ? 'analyze_inventory'  :
                                  'analyze_technical'

  const analyzePageContext = {
    tab:           activeTab,
    project_name:  project?.name  || '',
    account_id:    project?.account || '',
    ...(selectedFile ? {
      selected_file:       selectedFile.name,
      selected_file_group: selectedFile.group,
    } : {}),
    ...(realMetrics ? {
      total_files:    realMetrics.totalFiles,
      total_lines:    realMetrics.totalLines,
      cobol_programs: realMetrics.cobolPrograms,
    } : {}),
  }

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="ga-view">

      {/* ── Header — same shell as Transformation Runs (RunsView) ──────────── */}
      <div className="runs-header-bar">

        {/* Left: back + breadcrumb */}
        <div className="runs-header-left">
          <button className="runs-back-btn" onClick={onBack}>
            <ArrowLeft size={15} />
            <span>Projects</span>
          </button>
          <div className="runs-breadcrumb">
            <span className="runs-bc-project">{projectName}</span>
            <ChevronRight size={14} className="runs-bc-sep" />
            <span className="runs-bc-page">Analyze</span>
          </div>
        </div>

        {/* Center: mode-switcher nav (same buttons as RunsView, Analyze active) */}
        <div className="runs-header-center">
          <div className="mode-switcher">
            <button className="mode-btn" onClick={onBackToProjects || onBack} title="Projects">
              <Home size={16} />
              <span>Home</span>
            </button>
            <button className="mode-btn active" title="Analyze — review assets, dependencies and issues">
              <BarChart2 size={16} />
              <span>Analyze</span>
            </button>
            {/* Workflow and MCP hidden from nav — accessible via Journey dropdown
            <button
              className="mode-btn"
              onClick={() => {
                console.log('[Header] Workflow clicked — confirming to pipeline')
                onConfirm()
              }}
              title="Workflow — go to transformation pipeline"
            >
              <Workflow size={16} />
              <span>Workflow</span>
            </button>
            <button className="mode-btn" disabled title="MCP — coming soon">
              <Plug size={16} />
              <span>MCP</span>
            </button>
            */}
            <button
              className="mode-btn"
              onClick={() => {
                console.log('[Header] Reports clicked — opening in new tab', { account: project?.account, app: project?.name })
                onOpenReports?.()
              }}
              title="Reports (opens in new tab)"
            >
              <FileBarChart size={16} />
              <span>Reports</span>
            </button>

            {/* ── More menu — anchored dropdown to the right of Reports ── */}
            <div className="more-menu-container">
              <button
                className={`mode-btn more-trigger ${showMoreMenu ? 'active' : ''}`}
                onClick={() => setShowMoreMenu(v => !v)}
                disabled={false}
                title="HITL Journey"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                </svg>
                <ChevronDown size={12} className={`more-chevron ${showMoreMenu ? 'open' : ''}`} />
              </button>

              {showMoreMenu && (
                <>
                  <div className="more-backdrop" onClick={() => setShowMoreMenu(false)} />
                  <div className="more-dropdown">
                    <div className="dropdown-section-label">MODERNIZATION FLOW</div>
                    <button className="dropdown-item active-item" onClick={() => setShowMoreMenu(false)}>
                      <span>Analyze</span>
                    </button>
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGenerateDocs?.(caJobId) }}>
                      <span>Generate Documentation</span>
                    </button>
                    {/* EBL disabled — graph + AIL replaces it (see docs/20260721_define_services_architecture.md)
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onExtractBizLogic?.(caJobId) }}>
                      <span>Extract Business Logic</span>
                    </button>
                    */}
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onDecomposeCode?.(caJobId) }}>
                      <span>Define Services</span>
                    </button>
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToServiceGeneration?.() }}>
                      <span>Build Services</span>
                    </button>
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToRefactorCode?.() }}>
                      <span>Refactor Code</span>
                    </button>
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToRefineCode?.() }}>
                      <span>Refine Code</span>
                    </button>
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToJavaPkg?.([]) }}>
                      <span>Build / Package</span>
                    </button>
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenReports?.() }}>
                      <span>Reports</span>
                    </button>
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenSettings?.() }}>
                      <span>Settings</span>
                    </button>
                  </div>
                </>
              )}
            </div>

          </div>
        </div>

        {/* Right: settings, help, user menu */}
        <div className="runs-header-right">
          <button className="icon-btn" onClick={onOpenSettings} title="Settings">
            <Settings size={18} />
          </button>
          <button className="icon-btn" onClick={onOpenHelp} title="Help">
            <HelpCircle size={18} />
          </button>
          <div className="user-menu-container">
            <button className="user-btn" onClick={() => setShowUserMenu(!showUserMenu)}>
              <div className="user-avatar"><User size={16} /></div>
              <span>{getShortName()}</span>
              <ChevronDown size={14} />
            </button>
            {showUserMenu && (
              <div className="user-dropdown">
                <div className="dropdown-header">
                  <span className="user-name">{userProfile.displayName || 'User'}</span>
                  <span className="user-email">{userProfile.email || ''}</span>
                </div>
                <div className="dropdown-divider" />
                <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onOpenSettings?.() }}>
                  <Settings size={16} />
                  <span>Settings</span>
                </button>
                <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onBack() }}>
                  <Home size={16} />
                  <span>Back to Home</span>
                </button>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ── Workspace ─────────────────────────────────────────────────────── */}
      <div className="ga-workspace">

        {loading && (
          <div className="ga-load-state">
            <Loader2 size={28} className="ga-load-spinner" />
            <p className="ga-load-msg">Loading analysis catalog…</p>
          </div>
        )}

        {/* ── Fatal error: only when BOTH catalog AND CA artifacts are unavailable ── */}
        {/* Catalog-only failures are non-fatal when CA data (parseData/assetGraph)    */}
        {/* loaded successfully. caLoading=true means CA is still in flight — wait.   */}
        {!loading && fetchError && !caLoading && !parseData && !assetGraph && (
          <div className="ga-error-state">
            <AlertTriangle size={28} />
            <p className="ga-error-title">Could not load analysis results</p>
            <p className="ga-error-detail">{fetchError}</p>
            <p className="ga-error-hint">
              Check the API logs for more detail. The ingest job may still be running,
              or the catalog may not have been written to S3.
            </p>
          </div>
        )}

        {/* ── Main content: render when catalog loaded OR CA artifacts are present ── */}
        {!loading && !(fetchError && !caLoading && !parseData && !assetGraph) && (
          <>
            {/* ── Left/center: always shown ──────────────────────────────── */}
            <div className={`ga-main${(activeTab === 'Dashboard' || activeTab === 'Technical View' || activeTab === 'Data View') ? ' ga-main--full' : ''}`}>

              {/* Page header */}
              <div className="ga-page-header">
                <div className="ga-page-title-row">
                  <h1 className="ga-page-title">Analyze</h1>
                </div>
                <p className="ga-page-subtitle">
                  {catalogMissing
                    ? <>Review Code Analysis results for <strong>{projectName}</strong>. File catalog unavailable — Dashboard and Technical View use Code Analysis data.</>
                    : <>Review the <strong>{files.length}</strong> assets extracted from{' '}
                        <strong>{projectName}</strong>.
                        Confirm when ready to start the full transformation pipeline.</>
                  }
                </p>
              </div>

              {/* Nav tabs */}
              <div className="ga-nav-tabs">
                {NAV_TABS.map(tab => (
                  <button
                    key={tab}
                    className={`ga-nav-tab${activeTab === tab ? ' active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* ── Dashboard tab ──────────────────────────────────────── */}
              {activeTab === 'Dashboard' && (
                <DashboardContent
                  catalogData={catalogData}
                  files={files}
                  parseData={parseData}
                  assetGraph={assetGraph}
                  caLoading={caLoading}
                  caJobId={caJobId}
                  projectName={projectName}
                  intelligenceMetrics={intelligenceMetrics}
                  deadCode={deadCode}
                />
              )}

              {/* ── Inventory tab ──────────────────────────────────────── */}
              {activeTab === 'Inventory' && (
                <>
                  {/* Catalog-recovering notice — shown while async CA-upstream catalog fetch is in flight */}
                  {catalogMissing && catalogRecovering && (
                    <div className="ga-catalog-notice ga-catalog-notice--recovering">
                      <Loader2 size={14} className="ga-load-spinner" />
                      <span>
                        <strong>Loading file catalog</strong> — resolving from Code Analysis upstream…
                      </span>
                    </div>
                  )}

                  {/* Catalog-unavailable notice — shown when ingest job id was missing or fetch failed
                      AND recovery has finished (or never started). Non-fatal: Dashboard/Technical View
                      still work from CA artifacts.                                                     */}
                  {catalogMissing && !catalogRecovering && (
                    <div className="ga-catalog-notice">
                      <AlertCircle size={15} />
                      <span>
                        <strong>Inventory unavailable</strong> — no ingest job linked to this project.
                        The file catalog requires an upload ingest job.
                        Dashboard and Technical View are still available from Code Analysis data.
                        {fetchError && <span className="ga-catalog-notice-detail"> ({fetchError})</span>}
                      </span>
                    </div>
                  )}

                  {/* Metrics strip */}
                  <div className="ga-metrics-strip">
                    {metrics.map(m => (
                      <div key={m.label} className={`ga-metric-card${m.accent ? ` ga-metric-${m.accent}` : ''}`}>
                        <span className="ga-metric-value">{m.value}</span>
                        <span className="ga-metric-label">{m.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Toolbar */}
                  <div className="ga-toolbar">
                    <div className="ga-search">
                      <Search size={14} className="ga-search-icon" />
                      <input
                        className="ga-search-input"
                        placeholder="Search assets..."
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                      />
                    </div>
                    <button className="ga-tool-btn">
                      <Filter size={14} />Filter
                    </button>
                    <button
                      className={`ga-tool-btn${bulkMode ? ' ga-tool-btn--active' : ''}`}
                      onClick={toggleBulkMode}
                    >
                      <List size={14} />
                      {bulkMode ? 'Bulk Mode On' : 'Bulk Actions'}
                    </button>
                    <div className="ga-view-selector">
                      {VIEW_OPTIONS.map(v => (
                        <button
                          key={v}
                          className={`ga-view-opt${activeView === v ? ' active' : ''}`}
                          onClick={() => setActiveView(v)}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Scope chips */}
                  <div className="ga-scope-chips">
                    {SCOPE_CHIPS.map(chip => (
                      <button
                        key={chip.id}
                        className={`ga-scope-chip${activeScope === chip.id ? ' active' : ''}`}
                        onClick={() => setActiveScope(chip.id)}
                      >
                        {chip.label}
                      </button>
                    ))}
                    <span className="ga-scope-count">{filteredFiles.length} assets</span>
                  </div>

                  {/* Bulk banner */}
                  {bulkMode && (
                    <div className="ga-bulk-banner">
                      <span className="ga-bulk-banner-count">
                        {selectedCount > 0
                          ? `${selectedCount} of ${filteredFiles.length} selected`
                          : `${filteredFiles.length} rows — select to begin`}
                      </span>
                      <div className="ga-bulk-banner-sep" />

                      {/* AI Actions label */}
                      <span className="ga-bulk-banner-label">
                        <Sparkles size={11} />
                        AI Actions:
                      </span>

                      <div className="ga-bulk-banner-actions">
                        {BULK_AI_ACTIONS
                          .filter(item => {
                            // java-quick-view and service-preview both require at least one
                            // selected COBOL file — they don't apply to JCL/copybook-only selections
                            if (item.id !== 'java-quick-view' && item.id !== 'service-preview') return true
                            return filteredFiles.some(
                              f => selectedRowIds.has(f.id) && f.group === 'COBOL'
                            )
                          })
                          .map(item => {
                            const Icon = item.icon
                            const disabled = selectedCount === 0
                            const selectedFiles = filteredFiles.filter(f => selectedRowIds.has(f.id))
                            return (
                              <button
                                key={item.id}
                                className={`ga-bulk-action-btn${disabled ? '' : ' ga-bulk-action-btn--active'}`}
                                disabled={disabled}
                                title={
                                  disabled
                                    ? 'Select files first'
                                    : `${item.label} — ${selectedCount} file${selectedCount !== 1 ? 's' : ''}`
                                }
                                onClick={() => {
                                  const bulkOutputMode =
                                    item.id === 'java-quick-view'  ? 'java-code-output'  :
                                    item.id === 'service-preview'   ? 'service-definition' :
                                                                      'multi-file-text'
                                  console.log('[BulkAI] Opened:', item.id,
                                    '| files:', selectedFiles.map(f => f.name),
                                    '| count:', selectedFiles.length,
                                    '| output mode:', bulkOutputMode)
                                  setBulkAiModal({ action: item.id, files: selectedFiles })
                                }}
                              >
                                <Icon size={11} />
                                {item.label}
                              </button>
                            )
                          })}
                      </div>

                      <button className="ga-bulk-exit-btn" onClick={toggleBulkMode}>
                        <X size={13} />Exit Bulk Mode
                      </button>
                    </div>
                  )}

                  {/* ── By Risk view — Phase 1 intelligence metrics ─────── */}
                  {activeView === 'By Risk' && (
                    <div className="ga-risk-view">
                      {!intelligenceMetrics ? (
                        <div className="ga-risk-no-metrics">
                          <span className="ga-risk-no-metrics-icon">📊</span>
                          <p className="ga-risk-no-metrics-title">Intelligence metrics not available</p>
                          <p className="ga-risk-no-metrics-sub">
                            This run was completed before Phase 1 intelligence metrics were added.
                            Re-run Code Analysis to generate Fan-Out, Fan-In, Instability, and Risk Scores.
                          </p>
                        </div>
                      ) : (() => {
                        // Build ranked list: COBOL programs only, sorted by risk_score desc
                        const rankedPrograms = files
                          .filter(f => f.group === 'COBOL')
                          .map(f => {
                            const m = getRiskMetrics(f.name)
                            return { file: f, metrics: m }
                          })
                          .filter(({ metrics }) => metrics !== null)
                          .sort((a, b) => b.metrics.risk_score - a.metrics.risk_score)

                        const unscored = files.filter(f =>
                          f.group === 'COBOL' && getRiskMetrics(f.name) === null
                        )

                        return (
                          <>
                            <div className="ga-risk-summary-strip">
                              {(() => {
                                const ps = intelligenceMetrics.project_summary || {}
                                return (
                                  <>
                                    <span className="ga-risk-sum-chip ga-risk-sum-chip--high">
                                      {ps.high_risk_count || 0} High
                                    </span>
                                    <span className="ga-risk-sum-chip ga-risk-sum-chip--medium">
                                      {ps.medium_risk_count || 0} Medium
                                    </span>
                                    <span className="ga-risk-sum-chip ga-risk-sum-chip--low">
                                      {ps.low_risk_count || 0} Low
                                    </span>
                                    <span className="ga-risk-sum-note">
                                      Phase 1 score: Fan-Out (60%) + Instability (40%)
                                    </span>
                                  </>
                                )
                              })()}
                            </div>

                            <table className="ga-table ga-risk-table">
                              <thead>
                                <tr>
                                  <th className="ga-th ga-th-name">Program</th>
                                  <th className="ga-th ga-th-risk-score">Risk Score</th>
                                  <th className="ga-th ga-th-fanout">Fan-Out</th>
                                  <th className="ga-th ga-th-fanin">Fan-In</th>
                                  <th className="ga-th ga-th-instab">Instability</th>
                                  <th className="ga-th ga-th-signal">Primary Signal</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rankedPrograms.map(({ file, metrics }) => (
                                  <tr
                                    key={file.id}
                                    className={[
                                      'ga-file-row',
                                      selectedFile?.id === file.id ? 'selected' : '',
                                    ].filter(Boolean).join(' ')}
                                    onClick={() => setSelectedFile(file)}
                                  >
                                    <td className="ga-td ga-td-name">
                                      <span className="ga-file-name">{file.name}</span>
                                    </td>
                                    <td className="ga-td ga-td-risk-score">
                                      <div className="ga-risk-score-cell">
                                        <span className={`ga-risk-badge ga-risk-badge--${metrics.risk_tier}`}>
                                          {metrics.risk_score}
                                        </span>
                                        <div className="ga-risk-bar-track">
                                          <div
                                            className={`ga-risk-bar-fill ga-risk-bar-fill--${metrics.risk_tier}`}
                                            style={{ width: `${metrics.risk_score}%` }}
                                          />
                                        </div>
                                      </div>
                                    </td>
                                    <td className="ga-td ga-td-fanout">
                                      <span className="ga-metric-val">{metrics.fan_out}</span>
                                    </td>
                                    <td className="ga-td ga-td-fanin">
                                      <span className="ga-metric-val">{metrics.fan_in}</span>
                                    </td>
                                    <td className="ga-td ga-td-instab">
                                      <span className="ga-metric-val">{metrics.instability.toFixed(2)}</span>
                                    </td>
                                    <td className="ga-td ga-td-signal">
                                      <span className="ga-signal-text">{metrics.primary_signal}</span>
                                    </td>
                                  </tr>
                                ))}
                                {unscored.length > 0 && (
                                  <tr className="ga-risk-unscored-row">
                                    <td colSpan={6} className="ga-risk-unscored-note">
                                      {unscored.length} COBOL program{unscored.length !== 1 ? 's' : ''} not matched to metrics
                                      ({unscored.map(f => f.name).join(', ')})
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </>
                        )
                      })()}
                    </div>
                  )}

                  {/* Inventory table */}
                  {activeView !== 'By Risk' && (
                  <div className="ga-table-container">
                    <table className={`ga-table${bulkMode ? ' ga-table--bulk' : ''}`}>
                      <thead>
                        <tr>
                          {bulkMode && (
                            <th className="ga-th ga-th-check">
                              <input
                                type="checkbox"
                                className="ga-bulk-cb"
                                checked={allVisibleSelected}
                                ref={el => { if (el) el.indeterminate = someVisibleSelected }}
                                onChange={e => handleSelectAll(e.target.checked)}
                              />
                            </th>
                          )}
                          <th className="ga-th ga-th-name">Name</th>
                          <th className="ga-th ga-th-path">Path</th>
                          <th className="ga-th ga-th-loc">Line Count</th>
                          {parseData && <th className="ga-th ga-th-cx">Complexity</th>}
                          <th className="ga-th ga-th-status">Status</th>
                          <th className="ga-th ga-th-actions">AI Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(grouped).map(([group, groupFiles]) => (
                          <Fragment key={`group-${group}`}>
                            <tr
                              className="ga-group-row"
                              onClick={() => toggleGroup(group)}
                            >
                              <td colSpan={colCount} className="ga-group-cell">
                                <div className="ga-group-label">
                                  {expandedGroups[group]
                                    ? <ChevronDown size={14} />
                                    : <ChevronRight size={14} />}
                                  <span className="ga-group-type" style={{ color: GROUP_COLORS[group] || '#888' }}>
                                    {group}
                                  </span>
                                  <span className="ga-group-count">{groupFiles.length}</span>
                                </div>
                              </td>
                            </tr>
                            {expandedGroups[group] && groupFiles.map(file => {
                              const sm           = STATUS_META[file.status] || { label: file.status, css: '' }
                              const isDetailSel  = !bulkMode && selectedFile?.id === file.id
                              const isBulkChecked = bulkMode && selectedRowIds.has(file.id)
                              return (
                                <tr
                                  key={file.id}
                                  className={[
                                    'ga-file-row',
                                    isDetailSel   ? 'selected'     : '',
                                    isBulkChecked ? 'bulk-checked' : '',
                                    bulkMode      ? 'bulk-mode'    : '',
                                  ].filter(Boolean).join(' ')}
                                  onClick={() => handleRowClick(file)}
                                >
                                  {bulkMode && (
                                    <td className="ga-td ga-td-check">
                                      <input
                                        type="checkbox"
                                        className="ga-bulk-cb"
                                        checked={isBulkChecked}
                                        onChange={() => handleRowClick(file)}
                                        onClick={e => e.stopPropagation()}
                                      />
                                    </td>
                                  )}
                                  <td className="ga-td ga-td-name">
                                    <span className="ga-file-name">{file.name}</span>
                                  </td>
                                  <td className="ga-td ga-td-path">
                                    <span className="ga-file-path">{file.path}</span>
                                  </td>
                                  <td className="ga-td ga-td-loc">
                                    {(() => { const lc = getLineCount(file.name); return lc !== null ? lc.toLocaleString() : '—' })()}
                                  </td>
                                  {parseData && (
                                    <td className="ga-td ga-td-cx">
                                      {(() => {
                                        const cx = getComplexity(file.name, file.group)
                                        return cx
                                          ? <span className={`ga-cx-badge ga-cx-${cx.toLowerCase()}`}>{cx}</span>
                                          : <span className="ga-cx-na">—</span>
                                      })()}
                                    </td>
                                  )}
                                  <td className="ga-td ga-td-status">
                                    <span className={`ga-status-badge ${sm.css}`}>{sm.label}</span>
                                  </td>
                                  <td className="ga-td ga-td-actions">
                                    {/* AI Actions menu — all text file types */}
                                    {!bulkMode && file.group !== 'VSAM' && file.group !== 'UNKNOWN' && (
                                      <button
                                        className="ga-row-action"
                                        onClick={e => handleAiMenuOpen(e, file)}
                                        title="AI Actions"
                                      >
                                        <MoreHorizontal size={14} />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </Fragment>
                        ))}
                        {Object.keys(grouped).length === 0 && (
                          <tr>
                            <td colSpan={colCount} className="ga-empty-row">
                              {files.length === 0 ? 'No assets found in catalog.' : 'No assets match the current filter.'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  )}
                </>
              )}

              {/* ── Technical View tab ─────────────────────────────────── */}
              {activeTab === 'Technical View' && (
                <TechnicalViewContent
                  parseData={parseData}
                  assetGraph={assetGraph}
                  caLoading={caLoading}
                  catalogData={catalogData}
                  intelligenceMetrics={intelligenceMetrics}
                  copybookImpact={copybookImpact}
                  fieldXref={fieldXref}
                  complexityHeatmap={complexityHeatmap}
                  gnucobolEnrichment={gnucobolEnrichment}
                  performChain={performChain}
                />
              )}

              {/* ── Data View tab ───────────────────────────────────────── */}
              {activeTab === 'Data View' && (
                <div className="ga-data-view-panel">
                  <DataView
                    embedded
                    onBack={() => {}}
                    analysisContext={{
                      jobs: {
                        dataanalysis_job_id: daJobId,
                        codeanalysis_job_id: caJobId,
                      },
                      baseName: null,
                      mainProgram: null,
                    }}
                  />
                </div>
              )}

            </div>

            {/* ── Right Detail Panel — Inventory tab only ────────────────── */}
            {activeTab === 'Inventory' && (
              <div className={`ga-detail-panel${rightPanelCollapsed ? ' ga-detail-panel--collapsed' : ''}`}>

                {/* Collapse/expand toggle — always visible in the panel */}
                <button
                  className="ga-detail-toggle"
                  onClick={() => setRightPanelCollapsed(c => !c)}
                  title={rightPanelCollapsed ? 'Expand Details Panel' : 'Collapse Details Panel'}
                >
                  {rightPanelCollapsed ? <PanelRightOpen size={16} /> : <PanelRightClose size={16} />}
                </button>

                {!rightPanelCollapsed && (bulkMode ? (

                  <>
                  {selectedCount === 0 ? (
                    <>
                      <div className="ga-detail-header">
                        <div className="ga-bulk-panel-title">Bulk Selection</div>
                      </div>
                      <div className="ga-bulk-panel-empty">
                        <div className="ga-bulk-panel-empty-icon"><List size={30} /></div>
                        <p className="ga-bulk-panel-empty-title">No items selected</p>
                        <p className="ga-bulk-panel-empty-hint">
                          Check rows in the table to build your selection.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="ga-detail-header">
                        <div className="ga-bulk-panel-title">
                          {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
                        </div>
                        <span className="ga-detail-group-tag"
                          style={{ backgroundColor: 'rgba(167,139,250,0.15)', color: '#a78bfa' }}>
                          Bulk
                        </span>
                      </div>
                      <div className="ga-bulk-panel-list">
                        {files.filter(f => selectedRowIds.has(f.id)).map(f => (
                          <div key={f.id} className="ga-bulk-panel-item">
                            <span className="ga-bulk-panel-item-name">{f.name}</span>
                            <span className="ga-cx-na">—</span>
                          </div>
                        ))}
                      </div>
                      <div className="ga-bulk-panel-note">
                        <AlertCircle size={13} />
                        <span>Use the toolbar above the table to act on your selection.</span>
                      </div>
                    </>
                  )}
                  </>

                ) : (

                  detail && (
                    <>
                    <div className="ga-detail-header">
                      <div className="ga-detail-file-name">{detail.name}</div>
                      <span className="ga-detail-group-tag"
                        style={{
                          backgroundColor: `${GROUP_COLORS[detail.group]}20`,
                          color: GROUP_COLORS[detail.group],
                        }}>
                        {detail.group}
                      </span>
                    </div>
                    <div className="ga-detail-meta">
                      <div className="ga-detail-row">
                        <span className="ga-detail-lbl">Type</span>
                        <span className="ga-detail-val">{detailMeta.type}</span>
                      </div>
                      <div className="ga-detail-row">
                        <span className="ga-detail-lbl">Path</span>
                        <span className="ga-detail-val ga-detail-path">{detail.path}</span>
                      </div>
                      <div className="ga-detail-row">
                        <span className="ga-detail-lbl">Line Count</span>
                        <span className="ga-detail-val">
                          {(() => {
                            const lc = detail?.name ? getLineCount(detail.name) : null
                            return lc !== null
                              ? lc.toLocaleString()
                              : <span className="ga-detail-muted">—</span>
                          })()}
                        </span>
                      </div>
                      {(() => {
                        const cx = getComplexity(detail.name, detail.group)
                        if (cx) return (
                          <div className="ga-detail-row">
                            <span className="ga-detail-lbl">Complexity</span>
                            <span className="ga-detail-val">
                              <span className={`ga-cx-badge ga-cx-${cx.toLowerCase()}`}>{cx}</span>
                            </span>
                          </div>
                        )
                        if (detail.group === 'COBOL' && !parseData) return (
                          <div className="ga-detail-row">
                            <span className="ga-detail-lbl">Complexity</span>
                            <span className="ga-detail-val ga-detail-muted">
                              — <span className="ga-detail-unavail">(available after Code Analysis)</span>
                            </span>
                          </div>
                        )
                        return null
                      })()}
                      <div className="ga-detail-row">
                        <span className="ga-detail-lbl">Status</span>
                        <span className={`ga-status-badge ${(STATUS_META[detail.status] || {}).css}`}>
                          {(STATUS_META[detail.status] || { label: detail.status }).label}
                        </span>
                      </div>
                    </div>

                    {/* ── Phase 1 Intelligence Metrics ─────────────────────── */}
                    {detail.group === 'COBOL' && (() => {
                      const rm = getRiskMetrics(detail.name)
                      if (!rm) {
                        return intelligenceMetrics ? null : (
                          <div className="ga-detail-section ga-risk-unavail-section">
                            <div className="ga-detail-row">
                              <span className="ga-detail-lbl">Risk Score</span>
                              <span className="ga-detail-val ga-detail-muted">
                                — <span className="ga-detail-unavail">(re-run Code Analysis to generate)</span>
                              </span>
                            </div>
                          </div>
                        )
                      }
                      return (
                        <div className="ga-detail-section ga-risk-metrics-panel">
                          <div className="ga-risk-metrics-header">
                            <div className="ga-risk-metrics-title-row">
                              <span className="ga-detail-section-title ga-risk-metrics-title">Risk Analysis</span>
                              <button
                                ref={riskHelpBtnRef}
                                className="ga-risk-help-btn"
                                onClick={() => {
                                  if (!riskHelpOpen) {
                                    // Open centered in the content area (avoids left nav + right panel)
                                    // 300px = popover width; 0.30 anchors it ~30% from left
                                    setRiskHelpPos({
                                      top:  110,
                                      left: Math.max(60, Math.round(window.innerWidth * 0.30)),
                                    })
                                  }
                                  setRiskHelpOpen(o => !o)
                                }}
                                title="What do these mean?"
                                aria-expanded={riskHelpOpen}
                              >
                                <HelpCircle size={12} />
                                <span className="ga-risk-help-label">What do these mean?</span>
                              </button>
                            </div>
                            <span className={`ga-risk-badge ga-risk-badge--${rm.risk_tier}`}>
                              {rm.risk_score} / 100
                            </span>
                          </div>

                          {/* ── Draggable floating help window (position:fixed, not in layout flow) ── */}
                          {riskHelpOpen && (
                            <div
                              ref={riskHelpPopoverRef}
                              className="ga-risk-help-popover"
                              style={{ top: riskHelpPos.top, left: riskHelpPos.left }}
                            >
                              {/* Drag handle — mousedown here starts the drag */}
                              <div
                                className="ga-risk-help-popover-header"
                                onMouseDown={(e) => {
                                  if (e.button !== 0) return
                                  e.preventDefault()
                                  const startX    = e.clientX
                                  const startY    = e.clientY
                                  const startLeft = riskHelpPos.left
                                  const startTop  = riskHelpPos.top
                                  const onMove = (e) => setRiskHelpPos({
                                    left: startLeft + (e.clientX - startX),
                                    top:  startTop  + (e.clientY - startY),
                                  })
                                  const onUp = () => {
                                    document.removeEventListener('mousemove', onMove)
                                    document.removeEventListener('mouseup',   onUp)
                                  }
                                  document.addEventListener('mousemove', onMove)
                                  document.addEventListener('mouseup',   onUp)
                                }}
                              >
                                <span className="ga-risk-help-popover-title">Understanding these metrics</span>
                                <button
                                  className="ga-risk-help-close"
                                  onClick={() => setRiskHelpOpen(false)}
                                  title="Close"
                                >
                                  <X size={12} />
                                </button>
                              </div>

                              <div className="ga-risk-help-terms">
                                <div className="ga-risk-help-term">
                                  <span className="ga-risk-help-term-name">Risk Score</span>
                                  <span className="ga-risk-help-term-def">
                                    This program's relative modernization difficulty in this project.
                                    0 = simplest, 100 = most complex. Scores are relative — the hardest
                                    program in your project scores highest.
                                  </span>
                                </div>
                                <div className="ga-risk-help-term">
                                  <span className="ga-risk-help-term-name">Fan-Out</span>
                                  <span className="ga-risk-help-term-def">
                                    How many other assets this program depends on — programs it calls,
                                    copybooks it includes, and files it accesses. High fan-out means
                                    more things can go wrong during modernization.
                                  </span>
                                </div>
                                <div className="ga-risk-help-term">
                                  <span className="ga-risk-help-term-name">Fan-In</span>
                                  <span className="ga-risk-help-term-def">
                                    How many other programs depend on this one. A high fan-in means
                                    changing this program carefully — other programs rely on it
                                    working correctly.
                                  </span>
                                </div>
                                <div className="ga-risk-help-term">
                                  <span className="ga-risk-help-term-name">Instability</span>
                                  <span className="ga-risk-help-term-def">
                                    How much this program depends on others versus being depended on.
                                    0.0 = stable (others rely on it, it relies on little).
                                    1.0 = unstable (it depends on many, nothing depends on it).
                                  </span>
                                </div>
                                <div className="ga-risk-help-term">
                                  <span className="ga-risk-help-term-name">Highly coupled and unstable</span>
                                  <span className="ga-risk-help-term-def">
                                    This program touches many other assets while nothing depends on it —
                                    often an orchestrator or entry point. It is more likely to cause
                                    downstream trouble if its transformation is incomplete.
                                  </span>
                                </div>
                                <div className="ga-risk-help-term">
                                  <span className="ga-risk-help-term-name">LCOM4</span>
                                  <span className="ga-risk-help-term-def">
                                    Lack of Cohesion in Methods — counts independent responsibility
                                    groups within this program's paragraphs. LCOM4 = 1 means the
                                    program is cohesive (one purpose). LCOM4 &gt; 1 means paragraphs
                                    form isolated clusters that don't share data — a strong signal that
                                    this program should be split before Define Services.
                                  </span>
                                </div>
                                <div className="ga-risk-help-term">
                                  <span className="ga-risk-help-term-name">Split candidate</span>
                                  <span className="ga-risk-help-term-def">
                                    When LCOM4 &gt; 1, the program has multiple independent paragraph
                                    groups. These groups can often be split into separate microservices
                                    during Define Services, reducing the scope of each transformation.
                                  </span>
                                </div>
                              </div>

                              <details className="ga-risk-help-formula">
                                <summary className="ga-risk-help-formula-toggle">
                                  How is the score calculated?
                                </summary>
                                <p className="ga-risk-help-formula-text">
                                  Phase 2 score = Fan-Out (50%) + Instability (30%) + Cyclomatic
                                  Complexity (20%), each normalized to the highest value in this project.
                                  Falls back to Phase 1 (Fan-Out 60% + Instability 40%) if CC is not
                                  available. Phase 3 adds Betweenness Centrality (BC) and PageRank (PR);
                                  Phase 4 adds LCOM4 — all three are supplementary signals shown
                                  alongside the score but do not change the 0–100 calculation.
                                </p>
                              </details>
                            </div>
                          )}

                          <div className="ga-risk-metrics-rows">
                            <div className="ga-risk-metric-row">
                              <span className="ga-risk-metric-lbl">Fan-Out</span>
                              <span className="ga-risk-metric-val">{rm.fan_out}</span>
                              <span className="ga-risk-metric-sub">
                                {(rm.fan_out_breakdown?.calls_out ?? 0)} call{(rm.fan_out_breakdown?.calls_out ?? 0) !== 1 ? 's' : ''} ·{' '}
                                {(rm.fan_out_breakdown?.copybooks_used ?? 0)} copybook{(rm.fan_out_breakdown?.copybooks_used ?? 0) !== 1 ? 's' : ''} ·{' '}
                                {(rm.fan_out_breakdown?.files_accessed ?? 0)} file{(rm.fan_out_breakdown?.files_accessed ?? 0) !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <div className="ga-risk-metric-row">
                              <span className="ga-risk-metric-lbl">Fan-In</span>
                              <span className="ga-risk-metric-val">{rm.fan_in}</span>
                              <span className="ga-risk-metric-sub">
                                {rm.fan_in === 0 ? 'entry point' : `called by ${rm.fan_in} program${rm.fan_in !== 1 ? 's' : ''}`}
                              </span>
                            </div>
                            <div className="ga-risk-metric-row ga-risk-metric-row--instab">
                              <span className="ga-risk-metric-lbl">Instability</span>
                              <span className="ga-risk-metric-val">{rm.instability.toFixed(2)}</span>
                              <div className="ga-risk-instab-bar-track">
                                <div
                                  className="ga-risk-instab-bar-fill"
                                  style={{ width: `${Math.round(rm.instability * 100)}%` }}
                                />
                              </div>
                            </div>
                            {rm.cyclomatic_complexity != null && (
                              <div className="ga-risk-metric-row">
                                <span className="ga-risk-metric-lbl">CC</span>
                                <span className={`ga-risk-metric-val ga-risk-cc-val${rm.cyclomatic_complexity >= 20 ? ' ga-risk-cc-val--high' : rm.cyclomatic_complexity >= 10 ? ' ga-risk-cc-val--med' : ''}`}>
                                  {rm.cyclomatic_complexity}
                                </span>
                                <span className="ga-risk-metric-sub">
                                  {rm.cyclomatic_complexity >= 20 ? 'complex' : rm.cyclomatic_complexity >= 10 ? 'moderate' : 'simple'} · {rm.decision_points} decision{rm.decision_points !== 1 ? 's' : ''}
                                </span>
                              </div>
                            )}
                            {rm.lcom4 != null && (
                              <div className="ga-risk-metric-row">
                                <span className="ga-risk-metric-lbl">LCOM4</span>
                                <span className={`ga-risk-metric-val ga-risk-lcom4-val${rm.lcom4 > 1 ? ' ga-risk-lcom4-val--split' : ''}`}>
                                  {rm.lcom4}
                                </span>
                                <span className="ga-risk-metric-sub">
                                  {rm.lcom4_paragraphs != null
                                    ? `${rm.lcom4_linked ?? '?'}/${rm.lcom4_paragraphs} paras linked`
                                    : 'cohesion components'}
                                </span>
                              </div>
                            )}
                            {rm.split_signal && (
                              <div className="ga-risk-lcom4-signal">
                                {rm.split_signal}
                              </div>
                            )}
                            {rm.betweenness_centrality != null && (
                              <div className="ga-risk-metric-row">
                                <span className="ga-risk-metric-lbl">BC</span>
                                <span className={`ga-risk-metric-val ga-risk-graph-val${rm.betweenness_centrality > 0 ? ' ga-risk-graph-val--active' : ''}`}>
                                  {rm.betweenness_centrality.toFixed(4)}
                                </span>
                                <span className="ga-risk-metric-sub">
                                  betweenness centrality · {rm.betweenness_centrality === 0 ? 'not a bridge' : 'structural bridge'}
                                </span>
                              </div>
                            )}
                            {rm.pagerank != null && (
                              <div className="ga-risk-metric-row">
                                <span className="ga-risk-metric-lbl">PR</span>
                                <span className="ga-risk-metric-val ga-risk-graph-val">
                                  {rm.pagerank.toFixed(4)}
                                </span>
                                <span className="ga-risk-metric-sub">
                                  pagerank · inbound call importance
                                </span>
                              </div>
                            )}
                          </div>
                          <p className="ga-risk-primary-signal">{rm.primary_signal}</p>
                        </div>
                      )
                    })()}

                    <div className="ga-detail-section">
                      <div className="ga-detail-section-title">
                        <AlertCircle size={14} />Recommendations
                      </div>
                      <ul className="ga-recs">
                        {detailMeta.recommendations.map((r, i) => (
                          <li key={i} className="ga-rec-item">{r}</li>
                        ))}
                      </ul>
                    </div>
                    {detail.status === 'needs-review' && (
                      <div className="ga-detail-alert">
                        <AlertTriangle size={14} />
                        <span>This file requires manual review before analysis can proceed.</span>
                      </div>
                    )}
                    {detail.status === 'missing-refs' && (
                      <div className="ga-detail-alert ga-detail-alert-red">
                        <AlertTriangle size={14} />
                        <span>Missing copybook or file reference detected.</span>
                      </div>
                    )}

                    {/* ── File Preview pane ─────────────────────────────── */}
                    <div className="ga-detail-section ga-preview-section">
                      <div className="ga-detail-section-title">
                        <FileCode size={14} />
                        <span>Preview</span>
                        {filePreview?.content && (
                          <button
                            className="ga-preview-more-btn"
                            onClick={() => {
                              console.log('[Preview] More → opening full modal for:', detail.name)
                              setFullModal(true)
                            }}
                          >
                            More →
                          </button>
                        )}
                      </div>

                      {/* Loading */}
                      {filePreview?.loading && (
                        <div className="ga-preview-status">
                          <Loader2 size={12} className="ga-load-spinner" />
                          <span>Loading…</span>
                        </div>
                      )}

                      {/* Unavailable / error */}
                      {!filePreview?.loading && filePreview?.error && (
                        <div className="ga-preview-status ga-preview-unavail">
                          {filePreview.error}
                        </div>
                      )}

                      {/* No file selected or no preview loaded yet */}
                      {!filePreview && (
                        <div className="ga-preview-status ga-preview-unavail">
                          Select a file to preview its contents.
                        </div>
                      )}

                      {/* Content snippet — first 50 lines */}
                      {!filePreview?.loading && filePreview?.content && (
                        <pre className="ga-preview-code">
                          {filePreview.content.split('\n').slice(0, 50).join('\n')}
                        </pre>
                      )}
                    </div>

                    {/* ── Project-level Java ZIP — always visible when CA has run ── */}
                    {caJobId && (
                      <div className="ga-detail-section ga-project-actions-section">
                        <div className="ga-detail-section-title">
                          <FileArchive size={14} />
                          <span>All Generated Java</span>
                        </div>
                        <a
                          href={`${getAPIBaseUrl()}/codeanalysis/${caJobId}/results/archive/generated_java.zip`}
                          download="generated_java.zip"
                          className="ga-project-zip-btn"
                          title="Download all Java files produced by Code Analysis as a single ZIP"
                        >
                          <Download size={13} />
                          Download All Generated Java (ZIP)
                        </a>
                        <p className="ga-project-zip-hint">All files from this analysis run</p>
                      </div>
                    )}

                    {/* ── Generated Java section — only when bundle resolved a real artifact ── */}
                    {(() => {
                      const isCobol  = detail.group === 'COBOL'
                      const stem     = detail.name.replace(/\.[^.]+$/, '').toLowerCase()
                      const javaFile = isCobol && caJavaFiles.length > 0
                        ? caJavaFiles.find(f => f.name.replace(/\.java$/i, '').toLowerCase() === stem)
                        : null
                      console.log('[JavaSection] file=', detail.name,
                        '| stem=', stem,
                        '| isCobol=', isCobol,
                        '| caJavaFiles=', caJavaFiles.length,
                        '| match=', javaFile ? javaFile.name : 'none',
                        '| willRender=', !!javaFile)
                      if (!javaFile) return null
                      return (
                        <div className="ga-detail-section ga-java-section">
                          <div className="ga-detail-section-title">
                            <Code2 size={14} />
                            <span>Generated Java</span>
                          </div>
                          {(() => {
                            const progStem = detail.name.replace(/\.[^.]+$/, '').toUpperCase()
                            const progKey  = Object.keys(parseData?.programs || {}).find(k =>
                              k.split('/').pop().replace(/\.[^.]+$/, '').toUpperCase() === progStem
                            )
                            const prog     = progKey ? parseData.programs[progKey] : null
                            const paras    = prog ? (prog.paragraphs || []).length : 0
                            const calls    = prog ? (prog.calls      || []).length : 0
                            const parts    = []
                            if (paras > 0) parts.push(`${paras} paragraph${paras !== 1 ? 's' : ''}`)
                            if (calls > 0) parts.push(`${calls} external call${calls !== 1 ? 's' : ''}`)
                            return (
                              <p className="ga-java-avail-msg">
                                {parts.length > 0
                                  ? `${parts.join(' · ')} — mapped to Java`
                                  : 'Procedural Java file generated from this source.'
                                }
                              </p>
                            )
                          })()}
                          <div className="ga-java-actions">
                            <button className="ga-java-btn" onClick={() => handleViewJava(javaFile)}>
                              View Java
                            </button>
                            <button
                              className="ga-java-btn"
                              onClick={() => handleDownloadJava(javaFile)}
                              title={`Download the Java file generated from ${detail.name} only`}
                            >
                              Download This Java File
                            </button>
                            {crStatus === 'succeeded' && crJobId && (
                              <button
                                className="ga-java-btn ga-java-btn-refactor"
                                onClick={() => handleDownloadRefactoredJava(javaFile.name)}
                                title={`Download the Code Refactor version of ${detail.name}`}
                              >
                                Download Refactored Java
                              </button>
                            )}
                            {crJobId && crStatus !== 'succeeded' && crStatus !== 'failed' && (
                              <span className="ga-java-cr-pending">Refactoring…</span>
                            )}
                          </div>
                        </div>
                      )
                    })()}

                  </>
                  )
                ))}

              </div>
            )}

          </>
        )}
      </div>

      {/* ── AI Actions dropdown menu ────────────────────────────────────────── */}
      {aiMenu && (
        <div
          className="ga-ai-menu-backdrop"
          onClick={() => setAiMenu(null)}
        >
          <div
            className="ga-ai-menu"
            style={{ top: aiMenu.top, left: aiMenu.left }}
            onClick={e => e.stopPropagation()}
          >
            <div className="ga-ai-menu-header">
              <Sparkles size={11} className="ga-ai-menu-sparkle" />
              <span className="ga-ai-menu-file">{aiMenu.file.name}</span>
            </div>
            {AI_ACTIONS
              .filter(item =>
                // Generate Java Quick View only makes sense for COBOL programs
                item.id !== 'java-quick-view' || aiMenu.file.group === 'COBOL'
              )
              .map(item => {
                const Icon = item.icon
                return (
                  <button
                    key={item.id}
                    className="ga-ai-menu-item"
                    onClick={() => handleAiAction(item.id)}
                  >
                    <Icon size={13} />
                    <span>{item.label}</span>
                  </button>
                )
              })}
          </div>
        </div>
      )}

      {/* ── Inventory AI Action modal (single file) ──────────────────────────── */}
      {aiModal && (
        <InventoryAIModal
          action={aiModal.action}
          file={aiModal.file}
          catalogIngestJobId={catalogIngestJobId}
          project={project}
          onClose={() => setAiModal(null)}
        />
      )}

      {/* ── Bulk AI Action modal (multi-file) ────────────────────────────────── */}
      {bulkAiModal && (
        <BulkInventoryAIModal
          action={bulkAiModal.action}
          files={bulkAiModal.files}
          catalogIngestJobId={catalogIngestJobId}
          project={project}
          onClose={() => setBulkAiModal(null)}
        />
      )}

      {/* ── Page-aware Workspace Assistant ──────────────────────────────────── */}
      <AIAssistant
        account={project?.account}
        application={project?.name}
        pageType={analyzePageType}
        pageContext={analyzePageContext}
      />

      {/* ── Full-file modal ─────────────────────────────────────────────────── */}
      {fullModal && selectedFile && filePreview?.content && (
        <div
          className="ga-file-modal-overlay"
          onClick={() => setFullModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label={`Full source: ${selectedFile.name}`}
        >
          <div className="ga-file-modal" onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="ga-file-modal-header">
              <div className="ga-file-modal-title-row">
                <FileCode size={15} className="ga-file-modal-icon" />
                <span className="ga-file-modal-name">{selectedFile.name}</span>
                <span
                  className="ga-detail-group-tag"
                  style={{
                    backgroundColor: `${GROUP_COLORS[selectedFile.group] || '#888'}20`,
                    color: GROUP_COLORS[selectedFile.group] || '#888',
                  }}
                >
                  {selectedFile.group}
                </span>
              </div>
              <div className="ga-file-modal-path">{selectedFile.path}</div>
              <button
                className="ga-file-modal-close"
                onClick={() => setFullModal(false)}
                title="Close (Esc)"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal body — full file content, scrollable */}
            <div className="ga-file-modal-body-wrap">
              <pre className="ga-file-modal-body">
                {filePreview.content}
              </pre>
            </div>

            {/* Modal footer */}
            <div className="ga-file-modal-footer">
              <span className="ga-file-modal-meta">
                {filePreview.content.split('\n').length.toLocaleString()} lines
                {' · '}
                {(filePreview.content.length / 1024).toFixed(1)} KB
              </span>
              <div className="ga-file-modal-footer-actions">
                <button
                  className="ga-file-modal-export-btn"
                  title={`Save As ${selectedFile.name}`}
                  onClick={() => {
                    const blob = new Blob([filePreview.content], { type: 'text/plain;charset=utf-8' })
                    const url  = URL.createObjectURL(blob)
                    const a    = document.createElement('a')
                    a.href     = url
                    a.download = selectedFile.name   // preserves original filename + extension
                    document.body.appendChild(a)
                    a.click()
                    document.body.removeChild(a)
                    URL.revokeObjectURL(url)
                    console.log('[Preview] Export:', selectedFile.name, `(${(filePreview.content.length / 1024).toFixed(1)} KB)`)
                  }}
                >
                  <Download size={13} />Export
                </button>
                <button className="ga-file-modal-close-btn" onClick={() => setFullModal(false)}>
                  <X size={13} />Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Generated Java viewer modal ─────────────────────────────────────── */}
      {javaModal && (
        <div
          className="ga-file-modal-overlay"
          onClick={() => setJavaModal(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Generated Java source"
        >
          <div className="ga-file-modal" onClick={e => e.stopPropagation()}>
            <div className="ga-file-modal-header">
              <div className="ga-file-modal-title-row">
                <Code2 size={15} className="ga-file-modal-icon" />
                <span className="ga-file-modal-name">{javaModal.fileName || 'Generated Java'}</span>
                <span className="ga-detail-group-tag" style={{ backgroundColor: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>
                  Java
                </span>
              </div>
              <button
                className="ga-file-modal-close"
                onClick={() => setJavaModal(null)}
                title="Close (Esc)"
              >
                <X size={16} />
              </button>
            </div>
            <div className="ga-file-modal-body-wrap">
              {javaModal.loading && (
                <div style={{ padding: '24px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Loader2 size={16} className="ga-load-spinner" />
                  Loading Java source…
                </div>
              )}
              {javaModal.error && (
                <div style={{ padding: '24px', color: '#f87171' }}>
                  Failed to load: {javaModal.error}
                </div>
              )}
              {javaModal.content && (
                <pre className="ga-file-modal-body">{javaModal.content}</pre>
              )}
            </div>
            {javaModal.content && (
              <div className="ga-file-modal-footer">
                <span className="ga-file-modal-meta">
                  {javaModal.content.split('\n').length.toLocaleString()} lines
                  {' · '}
                  {(javaModal.content.length / 1024).toFixed(1)} KB
                </span>
                <div className="ga-file-modal-footer-actions">
                  <button
                    className="ga-file-modal-export-btn"
                    onClick={() => javaModal.javaFile && handleDownloadJava(javaModal.javaFile)}
                  >
                    <Download size={13} />Download
                  </button>
                  <button className="ga-file-modal-close-btn" onClick={() => setJavaModal(null)}>
                    <X size={13} />Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  )
}

export default GuidedAnalyzeView
